/**
 * The one place the Waslo AI section talks to the outside world.
 *
 * It calls the authoring BFF at `/authoring/api/v1/accounts/:accountId`, which is
 * same-origin with the dashboard (Traefik routes `PathPrefix(/authoring)` to
 * `authoring-web` on the same host), so the Chatwoot session cookie rides along and
 * there is no login step and no token to hold. THE PLAN, §1.
 *
 * THE ACCOUNT SEGMENT IS NOT OPTIONAL. Every route the BFF mounts is scoped to
 * `/accounts/:accountId` (`services/authoring-web/src/routes/api/{config,credits}.ts`),
 * and `requireChatwootAdmin` is mounted on that same prefix — a request without it is
 * not merely unauthorised, it matches no route at all and falls through to the JSON
 * catch-all 404. So `accountId` is a required argument here, never a default.
 *
 * Deliberately `fetch` and not Chatwoot's axios instance: that instance carries a
 * `/api/v1` base URL, Chatwoot's own auth headers and Chatwoot's global error
 * interceptors, none of which apply to this BFF. A thin fetch keeps the boundary
 * explicit and keeps the contract testable without stubbing axios.
 *
 * WHAT THIS MODULE REFUSES TO DO: guess. A response whose shape is not the contract
 * throws `malformed`. It used to coerce a missing field to `0` / `false`, which turned
 * a wrong URL into "0 credits left" and "Off" — a confident lie shown to an owner whose
 * balance was fine and whose bot was running. A shape mismatch is a bug in one of two
 * repos and must read like one. `specs/bff-contract.spec.js` pins both halves.
 */

import { PRICED_REASONS } from '../helpers/ai-price.js';
// P-7.1: "Your customers write in". The only three the BFF sends.
import { CUSTOMER_LANGUAGES } from '../helpers/missing-language.js';
import { SPEAKS_AS } from '../helpers/arabic-voice.js';
// P-5.1 to P-5.9: the status codes, one list per wire field.
import {
  ACCOUNT_STATES,
  INGEST_NOTE_CODES,
  INGEST_REASON_CODES,
  NEEDS_YOU_CODES,
  NEEDS_YOU_FIXES,
  NEEDS_YOU_SUBJECTS,
  STATUS_FIXES,
  STATUS_STATES,
  TENANT_MODES,
} from '../helpers/status.js';
// P-1.8: the why-line's codes and fixes, and the closeness words.
import { CLOSENESS, WHY_CODES, WHY_FIXES } from '../helpers/why.js';
// Field report G2.1 / G2.2: the choices the two new settings may hold, and their defaults.
import {
  FIRST_AFTER_MINUTES,
  FOLLOW_UP_WORDING,
  NUDGE_COUNTS,
  SECOND_AFTER_MINUTES,
  defaultFollowUps,
  defaultSendingHours,
} from '../helpers/sending-hours.js';

export const WASLO_API_BASE = '/authoring/api/v1';

export const WASLO_ERROR_CODES = {
  NETWORK: 'network',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  NOT_PROVISIONED: 'not_provisioned',
  /**
   * A DIFFERENT ABSENCE FROM `NOT_PROVISIONED`, and the distinction is the whole reason
   * this code exists. `not_provisioned` means the account has no assistant at all;
   * `outbound_not_provisioned` means it has one and no WhatsApp number is wired for
   * SENDING from it. The second is the normal condition of a workspace on its first day
   * and is not a failure of any kind.
   *
   * It answers 403. Until the BFF named it, it arrived as a bare `forbidden` and three
   * screens inferred it from the fact that every outbound route happens to be admin-only
   * — true today, not a contract, and three separate inferences is how one workspace gets
   * told three stories about one missing number.
   */
  OUTBOUND_NOT_PROVISIONED: 'outbound_not_provisioned',
  /**
   * P-5.5: sending broadcasts was never set up on this workspace. 409, and by status alone a
   * plain conflict, which is how the Broadcasts page came to say "We couldn't load this"
   * about a state no retry can change. It is a STATE, with its own screen and one button.
   */
  BROADCAST_NOT_PROVISIONED: 'broadcast_not_provisioned',

  /**
   * THE FIVE WAYS SPENDING STOPS A REPLY, and they are five different facts about the
   * account — not five flavours of one. They all answer 402 and they all used to reach
   * the screen as a bare `server`, which is why every one of them was shown as "You have
   * run out of AI credits": the only sentence any surface had. An owner stopped by her
   * own DAILY limit was told to buy credits she already had, for a stop that would have
   * lifted by itself at midnight; an owner who had merely used up the budget for the Test
   * it page was told her customers were affected, which they were not.
   *
   * Only `OUT_OF_CREDITS` is about the balance. `TEST_BUDGET_USED` touches no customer at
   * all. `SPEND_LIMIT_DAY` and `SPEND_LIMIT_MONTH` are this account's own ceilings and we
   * raise them on request. `SPEND_LIMIT_PLATFORM` is ours and nothing the owner does can
   * change it.
   *
   * NO AMOUNT OF MONEY IS EVER CARRIED WITH THESE. The reason a limit was hit is the
   * owner's business; what it cost us is not, and the sentences on the screens are
   * written accordingly.
   */
  TEST_BUDGET_USED: 'test_budget_used',
  SPEND_LIMIT_DAY: 'spend_limit_day',
  SPEND_LIMIT_MONTH: 'spend_limit_month',
  SPEND_LIMIT_PLATFORM: 'spend_limit_platform',
  OUT_OF_CREDITS: 'out_of_credits',

  /**
   * P-5.12: an AI action the owner started needs her to confirm its price first. Status 409;
   * the server's quote is on the error's `quote` (`{ credits, balance, balanceAfter, what }`).
   * `useAiConfirm` answers it with the dialog; nothing else should ever see it.
   */
  CONFIRM_REQUIRED: 'confirm_required',

  /**
   * P-1.7: the account owner already has the most suggestions that may wait. 409; the error's
   * `count` says how many, for the sentence.
   */
  SUGGESTIONS_FULL: 'suggestions_full',

  /**
   * P-4.2: a task sends this place, so it cannot be removed. 409; the error's `tasks` names the
   * tasks, so the place panel can say which to change first.
   */
  PLACE_IN_USE: 'place_in_use',

  SERVER: 'server',
  MALFORMED: 'malformed',
};

export class WasloApiError extends Error {
  constructor(
    message,
    {
      status = 0,
      code = WASLO_ERROR_CODES.SERVER,
      fields = null,
      problems = null,
      quote = null,
      count = null,
      tasks = null,
    } = {}
  ) {
    super(message);
    this.name = 'WasloApiError';
    this.status = status;
    this.code = code;
    /**
     * `[{ stepId, problem|kind, detail|message }]` from a refused task definition, and null
     * otherwise — the ONE validation shape in this section that is not keyed by a request
     * body field, because it is keyed by a step id instead.
     *
     * It is carried raw. `helpers/task-flow-draft.js#normaliseProblems` is the single place
     * that reads either spelling the contract prints (see its comment); doing it here as
     * well would be a second reader for the same disagreement.
     */
    this.problems = problems;
    /**
     * `{ <requestBodyKey>: <one sentence> }` when the BFF answered `validation_failed`,
     * and null otherwise. The contract makes that an invariant in BOTH directions —
     * `validation_failed` ALWAYS carries fields and no other code ever does
     * (`http-errors.ts`) — so a form can pin each message under the control that caused it
     * without parsing prose, and can treat a null here as "there is nothing to pin, show
     * the sentence at form level".
     */
    this.fields = fields;
    /**
     * The price quote of a 409 `confirm_required` (P-5.12), in credits, and null otherwise.
     */
    this.quote = quote;
    /** How many suggestions are waiting, on a `suggestions_full` (P-1.7), and null otherwise. */
    this.count = count;
    /** The titles of the tasks that send a place, on a `place_in_use` (P-4.2), and null otherwise. */
    this.tasks = tasks;
  }
}

const malformed = detail =>
  new WasloApiError(`The server sent a response we could not read: ${detail}`, {
    status: 0,
    code: WASLO_ERROR_CODES.MALFORMED,
  });

/**
 * The BFF names its own failures (`http-errors.ts`), and its code is more precise than
 * the status: "this workspace has no assistant yet" is a 404 with
 * `code: "not_provisioned"`, indistinguishable from a plain 404 by status alone. Read
 * what it said; fall back to the status only when it said nothing.
 */
const CODE_BY_API_CODE = {
  unauthenticated: WASLO_ERROR_CODES.UNAUTHORIZED,
  forbidden: WASLO_ERROR_CODES.FORBIDDEN,
  not_provisioned: WASLO_ERROR_CODES.NOT_PROVISIONED,
  // A 403, and indistinguishable from a genuine permission refusal by status alone —
  // which is exactly why the BFF names it (`routes/api/outbound.ts`).
  outbound_not_provisioned: WASLO_ERROR_CODES.OUTBOUND_NOT_PROVISIONED,
  // P-5.5. A 409, like `confirm_required`, and just as meaningless by status alone.
  broadcast_not_provisioned: WASLO_ERROR_CODES.BROADCAST_NOT_PROVISIONED,
  not_found: WASLO_ERROR_CODES.NOT_FOUND,
  // The 402s. All five share a status, so the status can tell them apart from nothing —
  // drop any one of these rows and that refusal goes back to reading as a server error.
  test_budget_used: WASLO_ERROR_CODES.TEST_BUDGET_USED,
  spend_limit_day: WASLO_ERROR_CODES.SPEND_LIMIT_DAY,
  spend_limit_month: WASLO_ERROR_CODES.SPEND_LIMIT_MONTH,
  spend_limit_platform: WASLO_ERROR_CODES.SPEND_LIMIT_PLATFORM,
  out_of_credits: WASLO_ERROR_CODES.OUT_OF_CREDITS,
  // P-5.12. A 409, and a plain conflict by status alone.
  confirm_required: WASLO_ERROR_CODES.CONFIRM_REQUIRED,
  // P-1.7. A 409, and a plain conflict by status alone.
  suggestions_full: WASLO_ERROR_CODES.SUGGESTIONS_FULL,
  // P-4.2. A 409, and a plain conflict by status alone.
  place_in_use: WASLO_ERROR_CODES.PLACE_IN_USE,
};

/**
 * THE SAME FIVE REFUSALS, ARRIVING THE WAY THE BFF REALLY SENDS THEM — and without this,
 * the whole feature ships green and dead.
 *
 * `CODE_BY_API_CODE` reads `error.code`, which is what `http-errors.ts` fills in. But a
 * refusal that reaches the BFF from the sidecar goes through `upstreamFailure`, and
 * `classify(402)` has exactly one name for a 402: `out_of_credits`. The balance is the
 * only one of the five it knows how to say. The sidecar's real verdict rides alongside at
 * the TOP LEVEL, as a `code`/`reason` pair, so a day limit arrives as:
 *
 *   { error: { code: "out_of_credits", message: "You have run out of AI credits…" },
 *     code: "spend_limit", reason: "tenant_day_cap" }
 *
 * `out_of_credits` is a name this file maps, so "read `error.code`, fall back to the pair"
 * never reaches the pair — all five resolve to the balance and the owner is sent to buy
 * credits she already has. A safety net that cannot fire is worse than none, because it
 * reads like cover. `out_of_credits` is therefore the BLANKET answer that a recognised
 * pair REFINES (see the throw below); the four owner-facing names are more specific than
 * any pair and win outright.
 *
 * This is a translation of someone else's internal vocabulary and it is meant to die: the
 * day the BFF sets `error.code` to the owner-facing code, `specific` is one of the four
 * and the whole branch goes inert without being touched.
 */
const CODE_BY_SPEND_REASON = {
  playground_day_cap: WASLO_ERROR_CODES.TEST_BUDGET_USED,
  tenant_day_cap: WASLO_ERROR_CODES.SPEND_LIMIT_DAY,
  tenant_month_cap: WASLO_ERROR_CODES.SPEND_LIMIT_MONTH,
  platform_month_cap: WASLO_ERROR_CODES.SPEND_LIMIT_PLATFORM,
};

/**
 * @param {unknown} payload The whole error body, not its `error` member.
 * @returns {string|undefined} One of the five, or nothing — never a guess.
 */
const refusalFromTopLevel = payload => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return undefined;
  }
  const { code, reason } = payload;
  if (code === 'no_credits') return WASLO_ERROR_CODES.OUT_OF_CREDITS;
  if (code !== 'spend_limit' || typeof reason !== 'string') return undefined;
  const mapped = CODE_BY_SPEND_REASON[reason];
  // `typeof`, not truthiness: a `reason` of `constructor` or `toString` indexes straight
  // into Object.prototype and would otherwise hand a FUNCTION to the error's `code`.
  return typeof mapped === 'string' ? mapped : undefined;
};

const codeForStatus = status => {
  if (status === 401) return WASLO_ERROR_CODES.UNAUTHORIZED;
  if (status === 403) return WASLO_ERROR_CODES.FORBIDDEN;
  if (status === 404) return WASLO_ERROR_CODES.NOT_FOUND;
  return WASLO_ERROR_CODES.SERVER;
};

const parseJson = async response => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new WasloApiError('The server sent a response we could not read.', {
      status: response.status,
      code: WASLO_ERROR_CODES.MALFORMED,
    });
  }
};

/** The header every paid AI call carries: the price the owner confirmed, in credits. */
export const CONFIRM_CREDITS_HEADER = 'X-Waslo-Confirm-Credits';

/**
 * The option a paid call takes to send the confirmed price (P-5.12). Anything that is not a
 * whole number of credits sends nothing, and the server answers with its quote instead.
 *
 * @param {number|null|undefined} credits
 * @returns {{ confirmCredits?: number }}
 */
export const withConfirm = credits =>
  Number.isSafeInteger(credits) && credits >= 0
    ? { confirmCredits: credits }
    : {};

/**
 * "Sending broadcasts is not set up here" (P-5.5). The named code, and a bare 409 from a BFF
 * one version behind, which only ever sent that 409 for this reason on the broadcast reads.
 */
export const isBroadcastNotSetUp = error =>
  error?.code === WASLO_ERROR_CODES.BROADCAST_NOT_PROVISIONED ||
  error?.status === 409;

/**
 * The quote beside a 409 `confirm_required`, or null when any field is not what the contract
 * prints. Credits only.
 */
const readQuote = payload => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const { credits, balance, balanceAfter, what } = payload;
  if (!Number.isSafeInteger(credits) || credits < 0) return null;
  if (typeof balance !== 'number' || typeof balanceAfter !== 'number') {
    return null;
  }
  if (typeof what !== 'string') return null;
  return { credits, balance, balanceAfter, what };
};

/**
 * The URL every request in this section is built from — one function, so the account
 * segment cannot be forgotten on one call site and present on the next.
 *
 * @param {number|string} accountId Chatwoot account id, from the SPA route.
 * @param {string} path Path under the account scope, e.g. `/config`.
 */
export const wasloUrl = (accountId, path) => {
  const id = typeof accountId === 'number' ? String(accountId) : accountId;
  if (typeof id !== 'string' || id === '') {
    throw malformed('this page does not know which workspace it is in');
  }
  return `${WASLO_API_BASE}/accounts/${encodeURIComponent(id)}${path}`;
};

/**
 * P-1.7: the member route group, which staff can reach and which answers exactly three things
 * (the provisioning answer, the chat panel, a suggestion). Same account rule as `wasloUrl`.
 */
export const wasloMemberUrl = (accountId, path) => {
  const id = typeof accountId === 'number' ? String(accountId) : accountId;
  if (typeof id !== 'string' || id === '') {
    throw malformed('this page does not know which workspace it is in');
  }
  return `${WASLO_API_BASE}/member/accounts/${encodeURIComponent(id)}${path}`;
};

/**
 * @param {number|string} accountId
 * @param {string} path Path under the account scope, e.g. `/config`.
 * @param {{ method?: string, body?: unknown, signal?: AbortSignal,
 *           confirmCredits?: number, scope?: 'account'|'member' }} [options]
 *   `confirmCredits` comes only from `withConfirm` (P-5.12); `scope: 'member'` only for the
 *   three member routes (P-1.7).
 * @returns {Promise<unknown>} The parsed JSON body (`null` for an empty 204).
 */
export const wasloRequest = async (accountId, path, options = {}) => {
  const {
    method = 'GET',
    body,
    signal,
    confirmCredits,
    scope = 'account',
  } = options;
  // `scope: 'member'` only for the three member routes (P-1.7).
  const url =
    scope === 'member'
      ? wasloMemberUrl(accountId, path)
      : wasloUrl(accountId, path);

  // A PDF upload is the one write in this section that is not JSON: base64 in a JSON body
  // would inflate the file by a third and hide its real size from the BFF's size guard.
  // The browser must set `Content-Type` itself here — a hand-written `multipart/form-data`
  // has no boundary parameter, and the server then parses zero parts out of a body that
  // arrived intact. Same cookie, same `Origin`/`Sec-Fetch-Site` CSRF guard as every other
  // write; only the encoding differs.
  const isUpload = typeof FormData !== 'undefined' && body instanceof FormData;

  let response;
  try {
    response = await fetch(url, {
      method,
      signal,
      // Same-origin, so the dashboard session cookie is sent and the BFF's CSRF origin
      // check sees the origin it was pinned to. The browser adds `Origin` and
      // `Sec-Fetch-Site` on the write calls by itself, which is exactly what
      // `requireSameOrigin` reads — nothing to set here, and nothing page script could
      // forge if it tried.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body === undefined || isUpload
          ? {}
          : { 'Content-Type': 'application/json' }),
        // P-5.12: the price the owner confirmed. Only `withConfirm` sets it.
        ...(confirmCredits === undefined
          ? {}
          : { [CONFIRM_CREDITS_HEADER]: String(confirmCredits) }),
      },
      ...(body === undefined
        ? {}
        : { body: isUpload ? body : JSON.stringify(body) }),
    });
  } catch (error) {
    // An aborted request is a caller decision, not a failure — let it through
    // untouched so `useWasloApi` can ignore it.
    if (error?.name === 'AbortError') throw error;
    throw new WasloApiError('We could not reach the server.', {
      status: 0,
      code: WASLO_ERROR_CODES.NETWORK,
    });
  }

  if (!response.ok) {
    const payload = await parseJson(response).catch(() => null);
    // `{ "error": { "code", "message" } }` — the single shape every non-2xx under
    // `/authoring/api/` answers with (`http-errors.ts`).
    const detail =
      payload && typeof payload === 'object' && payload.error
        ? payload.error
        : null;
    const apiCode = typeof detail?.code === 'string' ? detail.code : '';
    const message =
      (typeof detail?.message === 'string' && detail.message) ||
      // A STRING `error` is the SIDECAR's own refusal shape rather than the BFF's. Nothing
      // in the BFF answers that way — every route wraps it in `{ code, message }` — but the
      // sidecar's authoring API is the documented fallback when Chatwoot is down, and one
      // line here is cheaper than "Request failed with status 400." over a real reason.
      (typeof detail === 'string' && detail) ||
      `Request failed with status ${response.status}.`;
    /**
     * `problems` from a refused task definition — BESIDE the envelope, not inside it:
     *
     *   { error: { code: "invalid_request", message: "…" },
     *     problems: [ { stepId, problem, detail? } ] }
     *
     * Read from the top level, which is where it is, and from inside `error` as one line of
     * insurance. Nothing else in this file needs a branch for it: the code resolves to
     * `server` by status and the editor decides what a 400 with problems means, because
     * `problems` is the only validation shape here keyed by a STEP rather than by a field.
     */
    const rawProblems =
      [payload?.problems, detail?.problems].find(Array.isArray) ?? null;
    // Only from `validation_failed`, and only when it really is a flat object of strings:
    // a form that pins `[object Object]` under a box is worse than one that shows the
    // sentence above the buttons.
    const rawFields = apiCode === 'validation_failed' ? detail?.fields : null;
    const fields =
      rawFields &&
      typeof rawFields === 'object' &&
      !Array.isArray(rawFields) &&
      Object.values(rawFields).every(value => typeof value === 'string')
        ? rawFields
        : null;
    // `typeof`, not truthiness: `CODE_BY_API_CODE.constructor` is a FUNCTION, and an
    // `error.code` of `constructor` would otherwise put it on `WasloApiError.code`, where
    // every `code === …` comparison in the section silently answers false.
    const named = CODE_BY_API_CODE[apiCode];
    const specific = typeof named === 'string' ? named : undefined;
    // The pair is a 402 instrument. A 500 that happens to mention spending is a server
    // failure, not a refusal, and must not be read as one.
    const fromPair =
      response.status === 402 ? refusalFromTopLevel(payload) : undefined;
    throw new WasloApiError(message, {
      status: response.status,
      // `out_of_credits` is the BLANKET 402 answer, not a specific one: `classify(402)`
      // returns it for all five refusals, so taking it at face value resolves a day limit
      // to "you have run out of credits" — the sentence this whole change exists to kill.
      // A RECOGNISED pair refines it; anything else it says stands, and the four
      // owner-facing names stay more specific than any pair. Identical when the pair
      // really does say `no_credits`, and inert the day the BFF names the code itself.
      code:
        (specific === undefined || specific === WASLO_ERROR_CODES.OUT_OF_CREDITS
          ? (fromPair ?? specific)
          : specific) ?? codeForStatus(response.status),
      fields,
      problems: rawProblems,
      quote: apiCode === 'confirm_required' ? readQuote(payload) : null,
      count:
        apiCode === 'suggestions_full' && Number.isSafeInteger(payload?.count)
          ? payload.count
          : null,
      tasks:
        apiCode === 'place_in_use' &&
        Array.isArray(payload?.tasks) &&
        payload.tasks.every(title => typeof title === 'string')
          ? payload.tasks
          : null,
    });
  }

  return parseJson(response);
};

// ── reading the contract, strictly ──────────────────────────────────────────────────
// Every reader below throws rather than substitutes. A default here is indistinguishable
// on screen from a real zero.

const envelope = (payload, key) => {
  const value =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload[key]
      : undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw malformed(`expected a \`${key}\` object`);
  }
  return value;
};

const bool = (object, key, where) => {
  if (typeof object[key] !== 'boolean') throw malformed(`${where}.${key}`);
  return object[key];
};

/**
 * A flag that a BFF older than this reader does not send yet. ABSENT is `false`; present
 * is read as strictly as `bool`, so a wrong type still throws. The default is safe here
 * for one reason only: every flag read this way marks an EXCEPTION ("this reply was held
 * back") whose absence renders as the ordinary case, which is exactly what an older BFF
 * — one that never held anything back — would have meant. Never use this for a field
 * whose `false` is a claim the UI acts on.
 */
const optionalBool = (object, key, where) =>
  object[key] === undefined ? false : bool(object, key, where);

const number = (object, key, where) => {
  if (typeof object[key] !== 'number' || !Number.isFinite(object[key])) {
    throw malformed(`${where}.${key}`);
  }
  return object[key];
};

/**
 * A COUNT a BFF older than this reader does not send yet. ABSENT is `0`; present is read
 * as strictly as `number`, so a string or a NaN still throws. `optionalBool`'s rule
 * applies here unchanged and is the only thing that makes the default safe: use this
 * ONLY where the bucket being counted could not have existed on the older server, so
 * "it did not send the field" and "the field is zero" are the same claim. A counter that
 * the old server tallied under a DIFFERENT name must never be read this way — that
 * default is not an absence, it is a wrong number with a confident zero on it.
 */
const optionalNumber = (object, key, where) =>
  object[key] === undefined ? 0 : number(object, key, where);

const string = (object, key, where) => {
  if (typeof object[key] !== 'string') throw malformed(`${where}.${key}`);
  return object[key];
};

const nullableString = (object, key, where) => {
  const value = object[key];
  if (value === null) return null;
  if (typeof value !== 'string') throw malformed(`${where}.${key}`);
  return value;
};

/**
 * A NULLABLE LINE a BFF older than this reader does not send yet. ABSENT reads as `null`,
 * present is read as strictly as `nullableString`, so a number still throws.
 *
 * `optionalBool`'s rule applies unchanged and is the only thing that makes the default
 * safe: use this ONLY where "it did not send the field" and "the field is null" are the
 * same claim to every reader downstream. It is not a licence to soften a key whose
 * absence would be a real deploy-order bug — those stay strict and must read like one.
 */
const optionalNullableString = (object, key, where) =>
  object[key] === undefined ? null : nullableString(object, key, where);

const nullableNumber = (object, key, where) => {
  const value = object[key];
  if (value === null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw malformed(`${where}.${key}`);
  }
  return value;
};

/**
 * A NULLABLE NUMBER a BFF older than this reader does not send yet (P-5.12's `charged`).
 * ABSENT reads as `null`, which every screen treats as "not known": no receipt line, never a
 * made-up zero. Present, it is read as strictly as `nullableNumber`.
 */
const optionalNullableNumber = (object, key, where) =>
  object[key] === undefined ? null : nullableNumber(object, key, where);

const numberArray = (object, key, where) => {
  const value = object[key];
  if (!Array.isArray(value) || value.some(entry => typeof entry !== 'number')) {
    throw malformed(`${where}.${key}`);
  }
  return value;
};

const stringArray = (object, key, where) => {
  const value = object[key];
  if (!Array.isArray(value) || value.some(entry => typeof entry !== 'string')) {
    throw malformed(`${where}.${key}`);
  }
  return value;
};

/**
 * `null` and `[]` are DIFFERENT ANSWERS here, which is why this is not `stringArray` with
 * a fallback: on the outbound limits, `null` means "no restriction of this kind" and `[]`
 * means "an empty restriction", i.e. nothing may be sent at all.
 */
const nullableStringArray = (object, key, where) =>
  object[key] === null ? null : stringArray(object, key, where);

/**
 * `{ <key>: [...] }`. Separate from `envelope` on purpose: `envelope` rejects an array,
 * because a top-level array where an object belongs is the exact shape a wrong route
 * returns, and the list endpoints wrap their arrays for the same reason the object ones
 * do — an envelope can gain a sibling field without becoming a different type.
 */
const listEnvelope = (payload, key) => {
  const value =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload[key]
      : undefined;
  if (!Array.isArray(value)) throw malformed(`expected a \`${key}\` array`);
  return value;
};

/** An object where an object is required. Guards every `x[key]` read below. */
const record = (value, where) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw malformed(`expected \`${where}\` to be an object`);
  }
  return value;
};

const nested = (object, key, where) => record(object[key], `${where}.${key}`);

const oneOf = (object, key, allowed, where) => {
  if (!allowed.includes(object[key])) throw malformed(`${where}.${key}`);
  return object[key];
};

/** P-3.4: what a connection does. The only two the BFF sends. */
const CONNECTION_PURPOSES = ['lookup', 'change'];

const readStringList = (object, key, where) =>
  listEnvelope(object, key).map((item, index) => {
    if (typeof item !== 'string') throw malformed(`${where}.${key}[${index}]`);
    return item;
  });

const CREDIT_LEVELS = ['ok', 'low', 'critical'];

const creditLevelOf = (object, where) => {
  const value = object.level;
  if (!CREDIT_LEVELS.includes(value)) throw malformed(`${where}.level`);
  return value;
};

/** P-6.10's two choices; the BFF's `config-bag.ts` `ANSWER_WHEN`. */
export const ANSWER_WHEN = ['always', 'closed_only'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * `config.businessHours` (P-6.10): per day a list of `[start, end]` or `null` (closed), or
 * `null` for "no hours set". Absent from an older BFF, which reads as `null`.
 */
const readBusinessHours = (raw, where) => {
  if (raw === undefined || raw === null) return null;
  const week = record(raw, where);
  const out = {};
  Object.keys(week).forEach(day => {
    if (!DAY_KEYS.includes(day)) throw malformed(`${where}.${day}`);
    const ranges = week[day];
    if (ranges === null) {
      out[day] = null;
      return;
    }
    if (!Array.isArray(ranges)) throw malformed(`${where}.${day}`);
    out[day] = ranges.map((range, index) => {
      if (
        !Array.isArray(range) ||
        range.length !== 2 ||
        range.some(v => typeof v !== 'string')
      ) {
        throw malformed(`${where}.${day}[${index}]`);
      }
      return [range[0], range[1]];
    });
  });
  return out;
};

/** A season's or a pause's dates and name (G2.1). */
const readDated = (raw, where) => {
  const item = record(raw, where);
  return {
    name: string(item, 'name', where),
    from: string(item, 'from', where),
    to: string(item, 'to', where),
  };
};

/**
 * `config.sendingHours` (G2.1). Absent from an older BFF, which reads as the sidecar's default;
 * present, every field is read as strictly as the rest of this envelope.
 */
const readSendingHours = (raw, where) => {
  if (raw === undefined) return defaultSendingHours();
  const value = record(raw, where);
  const gap = nested(value, 'prayerGap', where);
  const list = key => {
    if (!Array.isArray(value[key])) throw malformed(`${where}.${key}`);
    return value[key];
  };
  return {
    week: readBusinessHours(value.week, `${where}.week`),
    prayerGap: {
      on: bool(gap, 'on', `${where}.prayerGap`),
      from: string(gap, 'from', `${where}.prayerGap`),
      to: string(gap, 'to', `${where}.prayerGap`),
    },
    seasons: list('seasons').map((entry, index) => {
      const at = `${where}.seasons[${index}]`;
      const week = readBusinessHours(record(entry, at).week, `${at}.week`);
      if (week === null) throw malformed(`${at}.week`);
      return { ...readDated(entry, at), week };
    }),
    pauses: list('pauses').map((entry, index) =>
      readDated(entry, `${where}.pauses[${index}]`)
    ),
    broadcasts: bool(value, 'broadcasts', where),
  };
};

/** `config.followUps` (G2.2). Absent from an older BFF, which reads as off. */
const readFollowUps = (raw, where) => {
  if (raw === undefined) return defaultFollowUps();
  const value = record(raw, where);
  return {
    on: bool(value, 'on', where),
    nudges: oneOf(value, 'nudges', NUDGE_COUNTS, where),
    firstAfterMinutes: oneOf(
      value,
      'firstAfterMinutes',
      FIRST_AFTER_MINUTES,
      where
    ),
    secondAfterMinutes: oneOf(
      value,
      'secondAfterMinutes',
      SECOND_AFTER_MINUTES,
      where
    ),
    wording: oneOf(value, 'wording', FOLLOW_UP_WORDING, where),
    textEn: nullableString(value, 'textEn', where),
    textAr: nullableString(value, 'textAr', where),
  };
};

/** `{ config: TenantConfigDTO }` — `routes/api/config.ts`, GET and PATCH alike. */
const HANDOVER_TO = ['anyone', 'person', 'team'];
const NOTE_LANGS = ['en', 'ar'];

/** `config.handover` (P-1.3): absent → undefined, null → null, else read strictly. */
const readHandoverSetting = config => {
  if (config.handover === undefined) return undefined;
  if (config.handover === null) return null;
  const where = 'config.handover';
  const handover = record(config.handover, where);
  return {
    to: oneOf(handover, 'to', HANDOVER_TO, where),
    personId: number(handover, 'personId', where),
    teamId: nullableNumber(handover, 'teamId', where),
    noteLang: oneOf(handover, 'noteLang', NOTE_LANGS, where),
    // Task review item 11. Absent from an older BFF, which had no labels: none.
    labels:
      handover.labels === undefined
        ? []
        : stringArray(handover, 'labels', where),
  };
};

const STEP_TARGETS = ['person', 'team'];

/**
 * Task review item 11: where a handed-over turn's chat went (`reports.ts` `HandedToDTO`), or
 * `null`. Absent (an older BFF, or a turn that recorded no routing) is `null`: no line.
 */
const readHandedTo = (turn, where) => {
  if (turn.handedTo === undefined || turn.handedTo === null) return null;
  const at = `${where}.handedTo`;
  const handedTo = record(turn.handedTo, at);
  return {
    person: nullableString(handedTo, 'person', at),
    team: nullableString(handedTo, 'team', at),
    byTaskStep: bool(handedTo, 'byTaskStep', at),
    stepTargetGone:
      handedTo.stepTargetGone === null
        ? null
        : oneOf(handedTo, 'stepTargetGone', STEP_TARGETS, at),
  };
};

/**
 * P-1.8's why-line, or `null`. TOLERANT BY DESIGN: a code this build does not know is a newer
 * BFF, and the honest rendering is no sentence, not a wrong one or a thrown panel. The params
 * are the owner's own words (a task title, a saved question) and only strings survive.
 */
const readWhy = (raw, where) => {
  if (raw === undefined || raw === null) return null;
  const why = record(raw, where);
  if (!WHY_CODES.includes(why.code)) return null;
  const params = {};
  const rawParams =
    why.params && typeof why.params === 'object' && !Array.isArray(why.params)
      ? why.params
      : {};
  Object.entries(rawParams).forEach(([key, value]) => {
    if (typeof value === 'string') params[key] = value;
  });
  const target =
    why.target && typeof why.target === 'object' && !Array.isArray(why.target)
      ? why.target
      : {};
  return {
    code: why.code,
    params,
    fix: WHY_FIXES.includes(why.fix) ? why.fix : null,
    target: {
      taskId: Number.isSafeInteger(target.taskId) ? target.taskId : null,
      toolId: Number.isSafeInteger(target.toolId) ? target.toolId : null,
    },
  };
};

const readConfig = payload => {
  const config = envelope(payload, 'config');
  return {
    systemPrompt: string(config, 'systemPrompt', 'config'),
    temperature: number(config, 'temperature', 'config'),
    handoffMessage: nullableString(config, 'handoffMessage', 'config'),
    // The line a customer who writes in Arabic hears first. Read TOLERANTLY — the one key
    // in this envelope that is: fork `main` rebuilds Chatwoot on dev and prod at once, so
    // reading it strictly would pin this merge to the BFF being live on both. An absent
    // key means the same thing to this page as the `null` the BFF sends when no Arabic
    // line is set — an empty box — so nothing downstream can tell the two apart.
    handoffMessageAr: optionalNullableString(
      config,
      'handoffMessageAr',
      'config'
    ),
    aiEnabled: bool(config, 'aiEnabled', 'config'),
    personaIsDefault: bool(config, 'personaIsDefault', 'config'),
    onboarded: bool(config, 'onboarded', 'config'),
    // P-7.1. Read TOLERANTLY, `handoffMessageAr`'s rule: an older BFF does not send it, and
    // an account that never chose is Both, so absent and `both` mean the same thing here.
    // Present, it must be one of the three.
    customerLanguages:
      config.customerLanguages === undefined
        ? 'both'
        : oneOf(config, 'customerLanguages', CUSTOMER_LANGUAGES, 'config'),
    // P-10.6: the owner's line for a handover while closed, and whether hours are set at
    // all. Tolerant for the same reason as `handoffMessageAr`: an older BFF sends neither,
    // which reads as "not set" and "no hours", so the boxes simply do not show.
    handoverClosedEn: optionalNullableString(
      config,
      'handoverClosedEn',
      'config'
    ),
    handoverClosedAr: optionalNullableString(
      config,
      'handoverClosedAr',
      'config'
    ),
    hoursSet: optionalBool(config, 'hoursSet', 'config'),
    // P-1.3 "Hand chats to". THREE-VALUED on purpose: `undefined` from a BFF older than the
    // key (Overview then cannot tell, and asks nothing), `null` when nobody is chosen.
    handover: readHandoverSetting(config),
    // P-7.2 "How it speaks Arabic". Tolerant, `handoffMessageAr`'s rule: an older BFF sends
    // neither, which means the default (match the customer, not chosen). Present, each must
    // be what the BFF promises.
    arabicFormal: optionalBool(config, 'arabicFormal', 'config'),
    speaksAs:
      config.speaksAs === undefined || config.speaksAs === null
        ? null
        : oneOf(config, 'speaksAs', SPEAKS_AS, 'config'),
    // P-6.10 (ease C3). Tolerant, `handoffMessageAr`'s rule: an older BFF sends none of them,
    // which reads as Kuwait, no hours, "All the time" and no labels: what that BFF meant.
    timezone:
      config.timezone === undefined
        ? 'Asia/Kuwait'
        : string(config, 'timezone', 'config'),
    businessHours: readBusinessHours(
      config.businessHours,
      'config.businessHours'
    ),
    answerWhen:
      config.answerWhen === undefined
        ? 'always'
        : oneOf(config, 'answerWhen', ANSWER_WHEN, 'config'),
    skipLabels:
      config.skipLabels === undefined
        ? []
        : stringArray(config, 'skipLabels', 'config'),
    // F3.8 "Show that it is typing". Tolerant, `handoffMessageAr`'s rule: an older BFF sends
    // nothing, and the sidecar's default is on, so absent reads as on. Present, it is a boolean.
    showTyping:
      config.showTyping === undefined
        ? true
        : bool(config, 'showTyping', 'config'),
    // G2.1 / G2.2. Tolerant, `showTyping`'s rule: an older BFF sends neither, and the sidecar's
    // defaults (the opening hours with the Friday prayer pause; follow-ups off) are what it meant.
    sendingHours: readSendingHours(config.sendingHours, 'config.sendingHours'),
    followUps: readFollowUps(config.followUps, 'config.followUps'),
  };
};

/**
 * `prices` on the credits read (P-5.12): the five weights in whole credits, or null from a
 * BFF that does not send them yet. A wrong type throws, like every reader here.
 */
const readPrices = credits => {
  if (credits.prices === undefined || credits.prices === null) return null;
  const prices = record(credits.prices, 'credits.prices');
  return Object.fromEntries(
    PRICED_REASONS.map(reason => {
      const value = number(prices, reason, 'credits.prices');
      if (!Number.isSafeInteger(value) || value < 0) {
        throw malformed(`credits.prices.${reason}`);
      }
      return [reason, value];
    })
  );
};

/**
 * P-5.3's pace: used today, the 7-day daily average, days left. ABSENT or null from a BFF one
 * version behind, and the card then draws no runway lines. Present, it is read strictly: a pace
 * the client invented would be a number an owner acts on. `daysLeft: null` is "no pace yet".
 */
const readRunway = credits => {
  if (credits.runway === undefined || credits.runway === null) return null;
  const runway = record(credits.runway, 'credits.runway');
  return {
    usedToday: number(runway, 'usedToday', 'credits.runway'),
    dailyAverage7d: number(runway, 'dailyAverage7d', 'credits.runway'),
    daysLeft: nullableNumber(runway, 'daysLeft', 'credits.runway'),
  };
};

/** `{ credits: CreditsSummaryDTO }` — `routes/api/credits.ts`. */
const readCredits = payload => {
  const credits = envelope(payload, 'credits');
  return {
    balance: number(credits, 'balance', 'credits'),
    internal: bool(credits, 'internal', 'credits'),
    nextExpiry: nullableString(credits, 'nextExpiry', 'credits'),
    thresholds: numberArray(credits, 'thresholds', 'credits'),
    // `level` and `lowBalance` are computed once, on the BFF: by days left when there is a
    // pace (P-5.3), else from the sidecar's own ladder. This client never re-derives them.
    level: creditLevelOf(credits, 'credits'),
    lowBalance: bool(credits, 'lowBalance', 'credits'),
    prices: readPrices(credits),
    runway: readRunway(credits),
    // `optionalBool`'s rule holds: an older BFF never knew we were told, and a live button
    // that mails us twice is the harmless reading of its silence.
    alreadyTold: optionalBool(credits, 'alreadyTold', 'credits'),
  };
};

/**
 * What a reply can be stopped BY, other than the balance. `null` is the healthy answer
 * and is a value here, not an absence — an unknown reason throws rather than rendering a
 * stop with no sentence under it.
 */
const STOPPED_REASONS = [null, 'day_limit', 'month_limit', 'platform_limit'];

const readLimitBucket = (body, key) => {
  const bucket = nested(body, key, 'limits');
  return {
    // 0–100, a WHOLE number. It is the only measure of a limit that reaches an owner:
    // what a limit is worth in money is our business and never hers, so nothing on this
    // path carries an amount and no screen can print one it was not given.
    usedPercent: number(bucket, 'usedPercent', `limits.${key}`),
    reached: bool(bucket, 'reached', `limits.${key}`),
  };
};

/**
 * `{ month, day, test, stopped }` — `routes/api/limits.ts`.
 *
 * THE ONE UNWRAPPED BODY in this file, and deliberately pinned as such: the four keys sit
 * at the top level rather than under an envelope. Read exactly as strictly as the
 * enveloped ones — a percentage this client invented would be a number an owner acts on,
 * and "18% used" for an account that is actually at its ceiling is the same class of
 * confident lie as a fabricated zero balance.
 */
const readLimits = payload => {
  const body = record(payload, 'limits');
  return {
    month: readLimitBucket(body, 'month'),
    day: readLimitBucket(body, 'day'),
    test: readLimitBucket(body, 'test'),
    stopped: oneOf(body, 'stopped', STOPPED_REASONS, 'limits'),
  };
};

/**
 * One unanswered question. P-6.6's `id` (its group, for "Fix it") and `ways` are absent from an
 * older BFF: no group, asked one way.
 */
const readUnanswered = (raw, where) => {
  const item = record(raw, where);
  return {
    query: string(item, 'query', where),
    count: number(item, 'count', where),
    id: optionalNullableNumber(item, 'id', where),
    ways: item.ways === undefined ? 1 : number(item, 'ways', where),
  };
};

/**
 * `{ activity: ActivityDTO }` — `routes/api/reports.ts`.
 *
 * `lastReplyAt: null` is a real answer ("nothing in the last seven days"), which is why
 * it is the ONLY nullable field here: the BFF fails the whole read rather than shipping
 * an empty `unanswered` for a fetch that did not happen, because an empty list renders
 * as "nothing needs your attention".
 */
const readActivityDay = (day, where) => ({
  day: string(day, 'day', where),
  conversations: number(day, 'conversations', where),
  handoffConversations: number(day, 'handoffConversations', where),
});

const readActivity = payload => {
  const activity = envelope(payload, 'activity');
  const window = nested(activity, 'window', 'activity');
  return {
    today: readActivityDay(
      nested(activity, 'today', 'activity'),
      'activity.today'
    ),
    window: {
      days: number(window, 'days', 'activity.window'),
      conversations: number(window, 'conversations', 'activity.window'),
      handoffConversations: number(
        window,
        'handoffConversations',
        'activity.window'
      ),
    },
    lastReplyAt: nullableString(activity, 'lastReplyAt', 'activity'),
    unanswered: listEnvelope(activity, 'unanswered').map((raw, index) =>
      readUnanswered(raw, `activity.unanswered[${index}]`)
    ),
  };
};

const JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed'];
const JOB_PHASES = [null, 'crawling', 'generating'];

/**
 * P-5.6: a reason CODE the fork says in her words, or `null`. An old BFF sends no key (null);
 * a code this build has no words for is `unknown`, which has words. Never a throw: a row that
 * cannot say why is still a row that must be shown.
 */
const codeOrUnknown = (object, key, allowed) => {
  const value = object[key];
  if (value === undefined || value === null) return null;
  return allowed.includes(value) ? value : 'unknown';
};

/** `job.receipt` (P-6.7), or `null`. */
const readReceipt = (raw, where) => {
  if (raw === undefined || raw === null) return null;
  const receipt = record(raw, where);
  return {
    pagesRead: number(receipt, 'pagesRead', where),
    pagesUnreadable: numberArray(receipt, 'pagesUnreadable', where),
    linesUnreadable: stringArray(receipt, 'linesUnreadable', where),
    answersLearned: number(receipt, 'answersLearned', where),
    linesKept: number(receipt, 'linesKept', where),
  };
};

/** `{ jobs: JobSummaryDTO[] }` — `routes/api/jobs.ts`. */
const readJobs = payload =>
  listEnvelope(payload, 'jobs').map((raw, index) => {
    const where = `jobs[${index}]`;
    const job = record(raw, where);
    return {
      id: number(job, 'id', where),
      title: string(job, 'title', where),
      // An unknown status is not a job we can describe — a row that says nothing about
      // what to do with it is worse than a loud failure.
      status: oneOf(job, 'status', JOB_STATUSES, where),
      sourceType: string(job, 'sourceType', where),
      // P-5.6: always null from a BFF that sends `reason`. Read, never shown.
      error: optionalNullableString(job, 'error', where),
      documentId:
        job.documentId === null ? null : number(job, 'documentId', where),
      phase: oneOf(job, 'phase', JOB_PHASES, where),
      pagesCrawled: number(job, 'pagesCrawled', where),
      faqsGenerated: number(job, 'faqsGenerated', where),
      warning: optionalNullableString(job, 'warning', where),
      charged: optionalNullableNumber(job, 'charged', where),
      reason: codeOrUnknown(job, 'reason', INGEST_REASON_CODES),
      note: codeOrUnknown(job, 'note', INGEST_NOTE_CODES),
      // P-6.7: a photo or scanned source's receipt; absent from an older BFF (no receipt).
      receipt: readReceipt(job.receipt, `${where}.receipt`),
      createdAt: string(job, 'createdAt', where),
      updatedAt: string(job, 'updatedAt', where),
    };
  });

/**
 * One channel (P-5.5): Chatwoot's own inbox, joined by the BFF with the assistant's state on
 * it. `channelType`, `state` and `fix` are ABSENT from a BFF one version behind and read as
 * `null` (the row then falls back to on and off). Present, `state` is held to the list: a
 * state with no words must fail loudly, never paint as "Answering".
 */
const readChannel = (inbox, where) => ({
  id: number(inbox, 'id', where),
  // `''` is real: a bot-backed channel her stale session could not name.
  name: string(inbox, 'name', where),
  phoneNumber: nullableString(inbox, 'phoneNumber', where),
  // `connected` is the BFF's boolean, not a `!== null` this client re-derives from
  // an id it should never have been shown.
  connected: bool(inbox, 'connected', where),
  channelType: optionalNullableString(inbox, 'channelType', where),
  state:
    inbox.state === undefined || inbox.state === null
      ? null
      : oneOf(inbox, 'state', STATUS_STATES, where),
  fix:
    inbox.fix === undefined || inbox.fix === null
      ? null
      : oneOf(inbox, 'fix', STATUS_FIXES, where),
});

/** `{ inboxes: InboxSummaryDTO[] }` — `routes/api/inboxes.ts`. */
const readInboxes = payload =>
  listEnvelope(payload, 'inboxes').map((raw, index) => {
    const where = `inboxes[${index}]`;
    return readChannel(record(raw, where), where);
  });

/** One Needs-you row (P-5.9), or `null` for a code this build has no words for. */
const readNeedsYou = (raw, where) => {
  const row = record(raw, where);
  if (!NEEDS_YOU_CODES.includes(row.code)) return null;
  const subject = nested(row, 'subject', where);
  return {
    code: row.code,
    subject: {
      kind: oneOf(subject, 'kind', NEEDS_YOU_SUBJECTS, `${where}.subject`),
      id: number(subject, 'id', `${where}.subject`),
      title: string(subject, 'title', `${where}.subject`),
    },
    fix: oneOf(row, 'fix', NEEDS_YOU_FIXES, where),
    reason: codeOrUnknown(row, 'reason', INGEST_REASON_CODES),
    documentId: nullableNumber(row, 'documentId', where),
  };
};

/**
 * `{ status: StatusDTO }` — `routes/api/status.ts`, the one answer every screen says (P-5.1).
 *
 * STRICT, and that is the feature: an account state or a fix this build cannot name is a
 * status it cannot say, and the screen then says "We couldn't check your assistant", never a
 * guessed "answering". The one tolerance is a Needs-you row with a newer code, which costs
 * that row and not the headline.
 */
const readStatus = payload => {
  const status = envelope(payload, 'status');
  const account = nested(status, 'account', 'status');
  const readiness = nested(status, 'readiness', 'status');
  return {
    account: {
      state: oneOf(account, 'state', ACCOUNT_STATES, 'status.account'),
      fix: oneOf(account, 'fix', STATUS_FIXES, 'status.account'),
      resumesAt: nullableString(account, 'resumesAt', 'status.account'),
      aiEnabled: bool(account, 'aiEnabled', 'status.account'),
      mode: oneOf(account, 'mode', TENANT_MODES, 'status.account'),
    },
    channels: listEnvelope(status, 'channels').map((raw, index) => {
      const where = `status.channels[${index}]`;
      return readChannel(record(raw, where), where);
    }),
    readiness: {
      described: bool(readiness, 'described', 'status.readiness'),
      knows: bool(readiness, 'knows', 'status.readiness'),
      tried: bool(readiness, 'tried', 'status.readiness'),
      liveSince: nullableString(readiness, 'liveSince', 'status.readiness'),
    },
    testers: stringArray(status, 'testers', 'status'),
    needsYou: listEnvelope(status, 'needsYou')
      .map((raw, index) => readNeedsYou(raw, `status.needsYou[${index}]`))
      .filter(Boolean),
  };
};

/** `202 { requested: true, alreadyTold }` — `routes/api/credits.ts` (P-5.3). */
const readTopUpRequested = payload => {
  const body = record(payload, 'the response');
  if (bool(body, 'requested', 'the response') !== true) {
    throw malformed('the response.requested');
  }
  return {
    requested: true,
    alreadyTold: optionalBool(body, 'alreadyTold', 'the response'),
  };
};

/** `{ purchase: { available, testMode, packs } }` from `routes/api/credits.ts` (Buy credits). */
const readCreditPurchase = payload => {
  const purchase = envelope(payload, 'purchase');
  if (!Array.isArray(purchase.packs)) throw malformed('purchase.packs');
  return {
    available: bool(purchase, 'available', 'purchase'),
    testMode: bool(purchase, 'testMode', 'purchase'),
    packs: purchase.packs.map((raw, index) => {
      const where = `purchase.packs[${index}]`;
      const pack = record(raw, where);
      return {
        id: string(pack, 'id', where),
        credits: number(pack, 'credits', where),
        priceFils: number(pack, 'priceFils', where),
      };
    }),
  };
};

/** `201 { checkout: { orderId, url } }`. */
const readCreditCheckout = payload => {
  const checkout = envelope(payload, 'checkout');
  return {
    orderId: number(checkout, 'orderId', 'checkout'),
    url: string(checkout, 'url', 'checkout'),
  };
};

/** Every state a Buy credits order can be in (the sidecar's `credits/order-status.ts`). */
export const CREDIT_ORDER_STATUSES = [
  'creating',
  'pending',
  'paid',
  'failed',
  'cancelled',
  'expired',
  'start_failed',
  'mismatch',
];

/** `{ order }`. */
const readCreditOrder = payload => {
  const order = envelope(payload, 'order');
  return {
    id: number(order, 'id', 'order'),
    status: oneOf(order, 'status', CREDIT_ORDER_STATUSES, 'order'),
    credits: number(order, 'credits', 'order'),
    amountFils: number(order, 'amountFils', 'order'),
  };
};

/** `{ week: WeekDTO }` — `routes/api/week.ts` (P-5.4). No conversations is a null share. */
const readWeek = payload => {
  const week = envelope(payload, 'week');
  return {
    from: string(week, 'from', 'week'),
    to: string(week, 'to', 'week'),
    conversations: number(week, 'conversations', 'week'),
    handovers: number(week, 'handovers', 'week'),
    handledWithoutTeamPct: nullableNumber(
      week,
      'handledWithoutTeamPct',
      'week'
    ),
    tasksFinished: number(week, 'tasksFinished', 'week'),
    creditsUsed: number(week, 'creditsUsed', 'week'),
  };
};

/** `{ email: WeekEmailDTO }` — `routes/api/week.ts`: her own setting and where it goes. */
const readWeekEmail = payload => {
  const email = envelope(payload, 'email');
  const weekday = number(email, 'weekday', 'email');
  if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
    throw malformed('email.weekday');
  }
  return {
    enabled: bool(email, 'enabled', 'email'),
    weekday,
    address: string(email, 'address', 'email'),
  };
};

/**
 * `{ requested: true, alreadyRequestedToday }` — the answer to "please put an assistant on
 * this number", which an operator then does with one click.
 *
 * `requested` is asserted to be TRUE rather than merely read: this route has no outcome in
 * which the ask was declined, so a `false` arriving here is a server saying something this
 * client does not understand, and rendering "we have your request" over it would leave an
 * owner waiting for a call that was never queued.
 *
 * `alreadyRequestedToday` is read with the optional reader for one reason: the SCREEN SAYS
 * THE SAME SENTENCE EITHER WAY (`AttentionRow.vue` — the owner pressed a button, and our
 * bookkeeping about how many times she has pressed it is not an answer to her). A server
 * that does not send the flag therefore renders identically, which is exactly the case
 * `optionalBool` exists for.
 */
const readConnectRequested = payload => {
  const body = record(payload, 'the response');
  if (bool(body, 'requested', 'the response') !== true) {
    throw malformed('the response.requested');
  }
  return {
    requested: true,
    alreadyRequestedToday: optionalBool(
      body,
      'alreadyRequestedToday',
      'the response'
    ),
  };
};

/** `{ suggestion: { kind, from, to, text }, charged }`: one translation (P-7.1). */
const readTranslation = payload => {
  const suggestion = envelope(payload, 'suggestion');
  return {
    kind: string(suggestion, 'kind', 'suggestion'),
    from: string(suggestion, 'from', 'suggestion'),
    to: string(suggestion, 'to', 'suggestion'),
    text: string(suggestion, 'text', 'suggestion'),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

const TRANSLATE_OUTCOMES = ['ok', 'too_long', 'truncated'];

/**
 * `{ from, to, items: [{ kind, outcome, text? | length, limit }], charged }`: a batch (P-7.1).
 * Every item is read strictly; an `ok` without its words is a broken answer, not a blank one.
 */
const readTranslations = payload => {
  const body = record(payload, 'the response');
  return {
    items: listEnvelope(body, 'items').map((raw, index) => {
      const where = `items[${index}]`;
      const item = record(raw, where);
      const outcome = oneOf(item, 'outcome', TRANSLATE_OUTCOMES, where);
      return {
        kind: string(item, 'kind', where),
        outcome,
        text: outcome === 'ok' ? string(item, 'text', where) : '',
        length: outcome === 'too_long' ? number(item, 'length', where) : null,
        limit: outcome === 'too_long' ? number(item, 'limit', where) : null,
      };
    }),
    charged: optionalNullableNumber(body, 'charged', 'the response'),
  };
};

/** `{ suggestion: { kind, text } }` — `routes/api/optimize.ts`. */
const readSuggestion = payload => {
  const suggestion = envelope(payload, 'suggestion');
  return {
    kind: string(suggestion, 'kind', 'suggestion'),
    text: string(suggestion, 'text', 'suggestion'),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

/** The two categories a draft from a goal can suggest (P-4.5). */
const TEMPLATE_DRAFT_CATEGORIES = ['UTILITY', 'MARKETING'];

const readTemplateDraftHalf = (draft, key) => {
  const where = `draft.${key}`;
  const half = nested(draft, key, 'draft');
  return {
    body: string(half, 'body', where),
    footer: string(half, 'footer', where),
    quickReplies: stringArray(half, 'quickReplies', where),
  };
};

/**
 * `{ draft, charged }` — `routes/api/optimize.ts` `apiDraftTemplateRoutes`. Strict: a draft with
 * a language missing would fill half a form and look finished.
 */
const readTemplateDraft = payload => {
  const draft = envelope(payload, 'draft');
  const rawBlanks = draft.blanks;
  if (!Array.isArray(rawBlanks)) throw malformed('draft.blanks');
  return {
    draft: {
      category: oneOf(draft, 'category', TEMPLATE_DRAFT_CATEGORIES, 'draft'),
      categoryReason: string(draft, 'categoryReason', 'draft'),
      name: string(draft, 'name', 'draft'),
      blanks: rawBlanks.map((raw, index) => {
        const where = `draft.blanks[${index}]`;
        const blank = record(raw, where);
        return {
          name: string(blank, 'name', where),
          sampleEn: string(blank, 'sampleEn', where),
          sampleAr: string(blank, 'sampleAr', where),
        };
      }),
      en: readTemplateDraftHalf(draft, 'en'),
      ar: readTemplateDraftHalf(draft, 'ar'),
    },
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

/** The three verdicts the Overview has a row for (P-4.6); the BFF drops every other one. */
const TEMPLATE_EVENT_KINDS = ['approved', 'refused', 'paused'];

/** `{ events: [...] }` — `routes/api/templates.ts`. */
const readTemplateEvents = payload =>
  listEnvelope(payload, 'events').map((raw, index) => {
    const where = `events[${index}]`;
    const event = record(raw, where);
    return {
      id: number(event, 'id', where),
      kind: oneOf(event, 'kind', TEMPLATE_EVENT_KINDS, where),
      inboxId: nullableNumber(event, 'inboxId', where),
      templateId: string(event, 'templateId', where),
      name: string(event, 'name', where),
      language: string(event, 'language', where),
      reason: nullableString(event, 'reason', where),
      occurredAt: string(event, 'occurredAt', where),
    };
  });

/**
 * `reply.menu` — ABSENT reads as `null`. `optionalBool`'s rule, and it is safe for the same
 * reason: a BFF older than menus never attached one, so "it did not send the field" and "no
 * menu" are the same claim. Present, it is read as strictly as everything else — `choiceId`
 * is sent back verbatim when a chip is tapped, so a missing one is a bug, not a default.
 */
const readTestMenu = reply => {
  if (reply.menu === undefined || reply.menu === null) return null;
  const menu = record(reply.menu, 'reply.menu');
  return {
    title: string(menu, 'title', 'reply.menu'),
    options: listEnvelope(menu, 'options').map((raw, index) => {
      const where = `reply.menu.options[${index}]`;
      const option = record(raw, where);
      return {
        choiceId: string(option, 'choiceId', where),
        title: string(option, 'title', where),
      };
    }),
  };
};

/**
 * P-2.10: `reply.choices` — the options under a reply as the chosen "Show it as" channel shows
 * them. ABSENT (a BFF older than B4) and `null` (no options) both read as `null`. Present, it
 * is read strictly: a `choiceId` that is not a string would send a tap nobody can resolve.
 */
const readTestChoices = reply => {
  if (reply.choices === undefined || reply.choices === null) return null;
  const where = 'reply.choices';
  const choices = record(reply.choices, where);
  return {
    style: string(choices, 'style', where),
    items: listEnvelope(choices, 'items').map((raw, index) => {
      const at = `${where}.items[${index}]`;
      const item = record(raw, at);
      return {
        choiceId: string(item, 'choiceId', at),
        title: string(item, 'title', at),
        fallback: optionalBool(item, 'fallback', at),
      };
    }),
    listButton: optionalNullableString(choices, 'listButton', where),
  };
};

/**
 * P-2.12: `reply.trace.customer` — the pretend customer after this turn, and what its saves
 * changed. ABSENT and `null` read as `null`; present, it is read strictly.
 */
const readTestCustomer = trace => {
  if (trace.customer === undefined || trace.customer === null) return null;
  const where = 'reply.trace.customer';
  const card = record(trace.customer, where);
  const attributes = record(card.attributes, `${where}.attributes`);
  Object.entries(attributes).forEach(([key, value]) => {
    if (typeof value !== 'string')
      throw malformed(`${where}.attributes.${key}`);
  });
  return {
    name: nullableString(card, 'name', where),
    email: nullableString(card, 'email', where),
    phone: nullableString(card, 'phone', where),
    attributes: { ...attributes },
    labels: readStringList(card, 'labels', where),
    changes: listEnvelope(card, 'changes').map((raw, index) => {
      const at = `${where}.changes[${index}]`;
      const change = record(raw, at);
      return {
        field: string(change, 'field', at),
        before: nullableString(change, 'before', at),
        after: nullableString(change, 'after', at),
        saved: bool(change, 'saved', at),
        reason: nullableString(change, 'reason', at),
        by: string(change, 'by', at),
      };
    }),
  };
};

/**
 * P-2.11: a step connection's `source`. Absent (a BFF older than B4) follows `rehearsed`, the
 * reading that BFF made: `live` when it really ran, `simulated` otherwise.
 */
const connectionSourceOf = (connection, at) => {
  if (connection.source !== undefined) return string(connection, 'source', at);
  return connection.rehearsed === false ? 'live' : 'simulated';
};

/** `[{stepId, stepLabel}]` under `key`, or `[]` when the key is absent (an older BFF). */
const namedSteps = (run, key, where) =>
  run[key] === undefined
    ? []
    : listEnvelope(run, key).map((raw, index) => {
        const at = `${where}.${key}[${index}]`;
        const row = record(raw, at);
        return {
          stepId: string(row, 'stepId', at),
          stepLabel: optionalNullableString(row, 'stepLabel', at),
        };
      });

/**
 * `reply.trace.stepTask` — the step-driven engine's run this turn, or `null`.
 *
 * THE KEY NAME IS THE AGREEMENT, and it is written here as a literal because it is the one
 * thing the two repos can get wrong in complete silence. The sidecar calls this
 * `trace.task` (`ai-sidecar/src/http/playground.ts`), which on THIS wire is already spent
 * on the free-text playbook pointer read below, so the BFF renames it on the way through.
 * `specs/bff-contract.spec.js` pins `stepTask` as a literal against the BFF's own test
 * body, so a rename on that side is one red test and three known edits — here, in
 * `helpers/test-trace.js`, and in that spec — rather than a screen that quietly shows a
 * task-driven turn as if no task had run.
 *
 * ABSENT reads as `null`, under `optionalBool`'s rule and for its reason: a BFF from before
 * the engine never ran one, so "it did not send the field" and "no task ran this turn" are
 * the same claim, and both render as the section simply not being there. `null` is the
 * ordinary answer on the live wire — the engine is switched off for every account today.
 *
 * PRESENT is read strictly, because from here on absence is not an older server, it is a
 * drift between two repos: a `worked` that arrived as a string would otherwise draw
 * "Answered" over a connection that failed, next to a price the owner is about to trust.
 *
 * `next` is the one field NOT constrained to a known set. It is a plain string on the wire
 * on purpose — a new outcome added in the sidecar has to reach the screen rather than be
 * refused by a client that shipped first — and `helpers/test-trace.js` draws no line for a
 * value it has no sentence for.
 *
 * ADDING A FIELD TO THIS TRACE? The rule is not in this file, because half of it is not
 * enforceable from this repo — `../waslo-services/docs/step-driven-tasks.md` §4.4 ("The Test
 * it trace's wire — what the fork depends on"), the rule that begins "Every key
 * `testReplyDTO` publishes is PRESENT". It says this reader's
 * strictness is correct AND that the strictness makes every new field the emitter's problem:
 * `JSON.stringify` drops `undefined`, `authoring-web` and `ai-sidecar` are separate
 * containers in one stack, so a field defaulted with `?.` rather than `?? <value>` takes
 * this whole screen down for the length of a rolling deploy. A field added here later
 * inherits both halves of that, and only one of them can be tested from this repo.
 */
const readStepTask = trace => {
  if (trace.stepTask === undefined || trace.stepTask === null) return null;
  const where = 'reply.trace.stepTask';
  const run = record(trace.stepTask, where);
  return {
    taskName: nullableString(run, 'taskName', where),
    // P-5.7: the title she gave the task. Tolerant about absence, like `stepLabel`.
    taskTitle: optionalNullableString(run, 'taskTitle', where),
    stepId: nullableString(run, 'stepId', where),
    ruleFailed: nullableString(run, 'ruleFailed', where),
    /**
     * The owner's OWN words for that step, joined from the task's definition — "Which day"
     * beside the id `day`. Both are kept: the label is what the sentence says, the id is
     * the only string that finds the step again in the builder.
     *
     * Read with the TOLERANT reader, the same exception `handoffMessageAr` gets and for the
     * same reason: the label is genuinely `null` for a step whose label was left blank and
     * for a definition edited under a run in flight, the screen falls back to the id in
     * every one of those cases, and so an absent key and a `null` are the same claim to
     * every reader downstream. Holding it strict would instead pin a fork rebuild — which
     * serves dev AND prod off one branch — to the sidecar that joins it being live on both.
     * Tolerant about ABSENCE, not about type: a number here still throws.
     */
    stepLabel: optionalNullableString(run, 'stepLabel', where),
    ruleFailedLabel: optionalNullableString(run, 'ruleFailedLabel', where),
    // P-10.21: why the engine passed the chat on. Tolerant about absence, like `stepLabel`.
    handoverCause: optionalNullableString(run, 'handoverCause', where),
    // P-10.2: no AI wrote this turn's message. An exception marker, so absent is `false`.
    sentAsWritten: optionalBool(run, 'sentAsWritten', where),
    // P-10.9: how the task this turn started was started. Tolerant about absence, like `stepLabel`.
    startedBy: optionalNullableString(run, 'startedBy', where),
    next: string(run, 'next', where),
    answers: listEnvelope(run, 'answers').map((raw, index) => {
      const at = `${where}.answers[${index}]`;
      const answer = record(raw, at);
      return {
        key: string(answer, 'key', at),
        // Two different facts, both needed: `label` is what the customer saw and tapped,
        // `value` is what a connected system is handed for it. A tap on "Tomorrow, 4pm"
        // travels on as something like `2026-09-26T16:00`, and a mapping that has gone
        // wrong is invisible if only one of the two is kept.
        value: string(answer, 'value', at),
        label: string(answer, 'label', at),
        // P-2.12: the owner's name for the step that asked it. Tolerant about absence.
        question: optionalNullableString(answer, 'question', at),
      };
    }),
    /**
     * P-10.3 and P-10.22: the steps whose answer was not saved (the customer did not say it) and
     * the Calls not sent again. Read TOLERANTLY about absence, like `stepLabel`: a BFF older
     * than these publishes neither, and that is "none". Present, each row is read strictly.
     */
    dropped: namedSteps(run, 'dropped', where),
    alreadyDone: namedSteps(run, 'alreadyDone', where),
    connections: listEnvelope(run, 'connections').map((raw, index) => {
      const at = `${where}.connections[${index}]`;
      const connection = record(raw, at);
      return {
        name: string(connection, 'name', at),
        worked: bool(connection, 'worked', at),
        // Read as strictly as `actions[].rehearsed` and for the identical reason: with the
        // live switch off the connection was SIMULATED, and "Answered" printed over a
        // system nobody called is the one lie this screen must not tell.
        //
        // KEEP THIS STRICT — the fragility it looks like was fixed on the OTHER side.
        // This is the one field in the run that did not predate the engine (it derives
        // from the sidecar's `connections[].dryRun`), so during a rolling deploy where
        // `authoring-web` is new and the `ai-sidecar` beside it is one version behind, the
        // flag really can be missing: `undefined` is dropped by `JSON.stringify`, the key
        // vanishes, and a strict read here would refuse the whole Test it reply over a
        // badge. Rather than soften it, the BFF now emits `dryRun ?? true`, so the key is
        // always a boolean — and it resolves an unknown to REHEARSED, because the switch
        // it reflects is off by default and "nothing ran" is the safe way to be wrong
        // about a booking. Softening this reader would give that guarantee away.
        rehearsed: bool(connection, 'rehearsed', at),
        // P-2.11: where the answer came from, when the sample was taken, and the connection's id
        // for "Fetch a sample now". Tolerant about absence (a BFF older than B4): `source`
        // then follows `rehearsed`, the reading that BFF made.
        source: connectionSourceOf(connection, at),
        sampleAt: optionalNullableString(connection, 'sampleAt', at),
        connectionId: optionalNullableNumber(connection, 'connectionId', at),
      };
    }),
  };
};

/**
 * `reply.trace.gate` — the reply gate's verdict, or `null` when it had nothing to say.
 *
 * WHAT THIS FIELD IS FOR. A draft the gate withholds reaches the Test it screen as a bare
 * handover line, byte-identical to the assistant deciding by itself that a person was
 * needed. Those are opposite problems: one is the assistant working as designed, the other
 * is the assistant having written a price or a link that nothing it was given backs up. An
 * owner who cannot tell them apart rewrites her wording to fix a grounding failure, or
 * adds knowledge to fix a handover, and neither changes anything.
 *
 * ABSENT is `null`, on the same rule as `readStepTask`. `kind` is deliberately not held to
 * a known set here for the same reason `next` is not — `helpers/test-trace.js` maps an
 * unrecognised kind onto a generic sentence, which is a better answer than a 500-flavoured
 * error on a screen whose whole job is explaining a failure.
 */
const readGateVerdict = trace => {
  if (trace.gate === undefined || trace.gate === null) return null;
  const where = 'reply.trace.gate';
  const gate = record(trace.gate, where);
  return {
    /** The draft was never sent. The tester saw the handover line instead. */
    withheld: bool(gate, 'withheld', where),
    /** It was sent back once and rewritten, then it went out. */
    retried: bool(gate, 'retried', where),
    reasons: listEnvelope(gate, 'reasons').map((raw, index) => {
      const at = `${where}.reasons[${index}]`;
      const reason = record(raw, at);
      return {
        kind: string(reason, 'kind', at),
        value: string(reason, 'value', at),
      };
    }),
  };
};

/** `[{ label, value }]`, two strings each (P-5.7). Absent is none. */
const readTraceLines = (object, key, where) =>
  object[key] === undefined
    ? []
    : listEnvelope(object, key).map((raw, index) => {
        const at = `${where}.${key}[${index}]`;
        const row = record(raw, at);
        return {
          label: string(row, 'label', at),
          value: string(row, 'value', at),
        };
      });

/** P-4.1 / P-4.2: the two things a reply can send with its words. */
const ATTACHMENT_KINDS = ['link', 'location'];

/**
 * `reply.attachment`. ABSENT (a BFF older than D1) and `null` both read as `null`; present, it is
 * read strictly, like `reply.choices`.
 */
const readTestAttachment = reply => {
  if (reply.attachment === undefined || reply.attachment === null) return null;
  const where = 'reply.attachment';
  const attachment = record(reply.attachment, where);
  const kind = oneOf(attachment, 'kind', ATTACHMENT_KINDS, where);
  if (kind === 'link') {
    return {
      kind,
      label: string(attachment, 'label', where),
      url: string(attachment, 'url', where),
    };
  }
  return {
    kind,
    name: string(attachment, 'name', where),
    address: string(attachment, 'address', where),
    latitude: number(attachment, 'latitude', where),
    longitude: number(attachment, 'longitude', where),
    mapsUrl: string(attachment, 'mapsUrl', where),
  };
};

/**
 * `{ reply: TestReplyDTO }` — `routes/api/playground.ts`, the Test it screen.
 *
 * `input` and `output` on an action are deliberately read as `unknown` and not narrowed:
 * they are whatever the OWNER's own connected system chose to send and receive, so there
 * is no shape to assert. Everything the screen branches on — whether it was rehearsed,
 * whether it handed over, how close a saved answer was — is checked exactly like every
 * other field here, because a missing `rehearsed` rendering as "rehearsed" would tell an
 * owner nothing real happened while her live system was being called.
 */
const readTestReply = payload => {
  const reply = envelope(payload, 'reply');
  const trace = nested(reply, 'trace', 'reply');
  return {
    text: string(reply, 'text', 'reply'),
    handoff: bool(reply, 'handoff', 'reply'),
    menu: readTestMenu(reply),
    choices: readTestChoices(reply),
    attachment: readTestAttachment(reply),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
    trace: {
      actions: listEnvelope(trace, 'actions').map((raw, index) => {
        const where = `reply.trace.actions[${index}]`;
        const action = record(raw, where);
        // P-5.7: the JSON moved under `technical`; a BFF one version behind still sends it
        // at the top. Carried as `unknown` either way, for the reason the header gives.
        const technical =
          action.technical === undefined
            ? { input: action.input, output: action.output }
            : record(action.technical, `${where}.technical`);
        return {
          name: string(action, 'name', where),
          // P-5.7: what it was, whether it left the chat, and her words for it. Absent from
          // an older BFF and read as `null`; `helpers/test-trace.js` works the kind out from
          // the name then. Present, each is read as strictly as everything else.
          kind: optionalNullableString(action, 'kind', where),
          effect: optionalNullableString(action, 'effect', where),
          label: optionalNullableString(action, 'label', where),
          action: optionalNullableString(action, 'action', where),
          fields: readTraceLines(action, 'fields', where),
          received: readTraceLines(action, 'received', where),
          rehearsed: bool(action, 'rehearsed', where),
          input: technical.input,
          output: technical.output,
        };
      }),
      lookups: listEnvelope(trace, 'lookups').map((raw, index) => {
        const where = `reply.trace.lookups[${index}]`;
        const lookup = record(raw, where);
        return {
          query: string(lookup, 'query', where),
          matches: listEnvelope(lookup, 'matches').map((rawMatch, at) => {
            const matchWhere = `${where}.matches[${at}]`;
            const match = record(rawMatch, matchWhere);
            return {
              question: string(match, 'question', matchWhere),
              score: number(match, 'score', matchWhere),
            };
          }),
        };
      }),
      /**
       * ── `reply.trace.task` IS NOT READ HERE ANY MORE ────────────────────────────────
       *
       * It was the FREE-TEXT playbook pointer — "it started this playbook, it finished
       * one, it is on that one" — and it predates the step engine. It was read strictly:
       * an absent `reply.trace.task` threw `malformed` and cost the tester the whole
       * reply, for three fields nothing on this screen depends on. That is the wrong trade
       * for a pointer at a thing an owner can no longer author: `TaskComposePage` writes a
       * step graph from its first save, and the BFF refuses a task created without one.
       *
       * THE SCREEN LOSES NOTHING IT CAN STILL BE TOLD. `helpers/test-trace.js` reads
       * `trace.task` defensively (`scenarioOf`), so an absent key collapses to "no
       * playbook" and the panel is simply not drawn — which is the same thing it does for
       * every turn that was not on a playbook.
       *
       * WHAT IS STILL TRUE UPSTREAM, and why this is a removal rather than a claim that
       * the field is dead: the sidecar continues to fill `trace.scenario` for a task that
       * genuinely has no graph (`scenarios/provider.ts`), and the BFF still publishes it
       * here. A legacy definition-less row would therefore still report a playbook that
       * this screen now stops showing. That is the one thing this change gives up, and it
       * is given up knowingly: the field is unauthorable and the strict read was live on
       * every single reply.
       *
       * `stepTask` below is the step engine's trace and a different thing entirely — the
       * collision is why it is not called `task`, and why both names are pinned as
       * literals in `specs/bff-contract.spec.js`.
       */
      stepTask: readStepTask(trace),
      gate: readGateVerdict(trace),
      customer: readTestCustomer(trace),
    },
  };
};

/**
 * Where an opening message on the Test it screen came from: a question this account's
 * customers asked and nobody answered, or one it has already written an answer for.
 * Anything else is read as `null` — see `readTestStarters`.
 */
const STARTER_SOURCES = ['unanswered', 'saved_answer'];

/**
 * `{ starters: [{ text, source }] }` — the Test it screen's opening messages, drawn from
 * the ACCOUNT'S OWN content. At most three; an empty list is a real answer.
 *
 * THE ONE FIELD THIS FILE NARROWS INSTEAD OF REJECTING, and it is confined to `source`.
 * A provenance this client has no name for becomes `null` and the LINE IS KEPT, because
 * `source` decides nothing an owner reads — it records where the line came from, and the
 * line is a usable opening message without it. Neither of the stricter readings survives
 * the question "what does a BFF that renamed these two values do to this screen?":
 * throwing empties the opening state for every account, and dropping the row does the
 * same thing one row at a time. Both would spend a real starter policing a field nothing
 * renders.
 *
 * `text` is read as strictly as everything else here — a starter that is not a string is
 * a bug in one of two repos — except that a BLANK one is dropped rather than returned,
 * since an empty chip is not a message anyone can click.
 */
const readTestStarters = payload =>
  listEnvelope(payload, 'starters')
    .map((raw, index) => {
      const where = `starters[${index}]`;
      const starter = record(raw, where);
      return {
        text: string(starter, 'text', where),
        source: STARTER_SOURCES.includes(starter.source)
          ? starter.source
          : null,
      };
    })
    .filter(starter => starter.text.trim() !== '');

/**
 * The windows the Activity screen offers, in the order it offers them. Exactly the set
 * the BFF validates (`routes/api/reports.ts` `ACTIVITY_RANGES`), which is itself exactly
 * the set `metrics_daily` is rolled up for — a fourth value here would not be a wider
 * report, it would be an empty one. `specs/activity.spec.js` pins the two lists together.
 */
export const WASLO_ACTIVITY_RANGES = [7, 30, 90];

/** `{ report: ActivityReportDTO }` — `routes/api/reports.ts`, the Activity screen. */
const readReport = payload => {
  const report = envelope(payload, 'report');
  const totals = nested(report, 'totals', 'report');
  const previous = nested(report, 'previous', 'report');
  return {
    range: number(report, 'range', 'report'),
    totals: {
      conversations: number(totals, 'conversations', 'report.totals'),
      handoffConversations: number(
        totals,
        'handoffConversations',
        'report.totals'
      ),
      // 0–1. The percentage is formatted at the render site, in the account's locale.
      handoffRate: number(totals, 'handoffRate', 'report.totals'),
      unansweredCount: number(totals, 'unansweredCount', 'report.totals'),
    },
    previous: {
      conversations: number(previous, 'conversations', 'report.previous'),
      handoffRate: number(previous, 'handoffRate', 'report.previous'),
    },
    // The series the chart draws. The BFF sums the totals above from THIS list, so a
    // header number and the bars under it cannot disagree.
    trend: listEnvelope(report, 'trend').map((raw, index) => {
      const where = `report.trend[${index}]`;
      const point = record(raw, where);
      return {
        day: string(point, 'day', where),
        conversations: number(point, 'conversations', where),
        handoffConversations: number(point, 'handoffConversations', where),
      };
    }),
    unanswered: listEnvelope(report, 'unanswered').map((raw, index) =>
      readUnanswered(raw, `report.unanswered[${index}]`)
    ),
    conversations: listEnvelope(report, 'conversations').map((raw, index) => {
      const where = `report.conversations[${index}]`;
      const item = record(raw, where);
      return {
        id: number(item, 'id', where),
        snippet: string(item, 'snippet', where),
        lastAt: string(item, 'lastAt', where),
        turns: number(item, 'turns', where),
        handoff: bool(item, 'handoff', where),
      };
    }),
  };
};

/** `{ transcript: ActivityTranscriptDTO }` — one conversation, read on demand. */
const readTranscript = payload => {
  const transcript = envelope(payload, 'transcript');
  return {
    conversationId: number(transcript, 'conversationId', 'transcript'),
    turns: listEnvelope(transcript, 'turns').map((raw, index) => {
      const where = `transcript.turns[${index}]`;
      const turn = record(raw, where);
      return {
        at: string(turn, 'at', where),
        customer: string(turn, 'customer', where),
        // '' is a real answer: it handed over without saying anything.
        assistant: string(turn, 'assistant', where),
        handoff: bool(turn, 'handoff', where),
        usedSavedAnswers: bool(turn, 'usedSavedAnswers', where),
        // The reply gate held the draft back and asked a team member instead — the
        // customer never saw `assistant`. Defaulted because a BFF from before the gate
        // simply never withheld anything.
        withheld: optionalBool(turn, 'withheld', where),
        // The draft was rewritten once before it went out.
        gateRetried: optionalBool(turn, 'gateRetried', where),
        // P-1.8. Absent from an older BFF, which reads as "no sentence".
        why: readWhy(turn.why, `${where}.why`),
        // P-6.4: what "Wrong? Fix it" fills in. Absent from an older BFF: the customer's words.
        suggestedQuestion:
          turn.suggestedQuestion === undefined
            ? string(turn, 'customer', where)
            : string(turn, 'suggestedQuestion', where),
        handedTo: readHandedTo(turn, where),
      };
    }),
    truncated: bool(transcript, 'truncated', 'transcript'),
  };
};

/** `{ documents: DocumentSummaryDTO[] }` — `routes/api/documents.ts`. */
const readDocuments = payload =>
  listEnvelope(payload, 'documents').map((raw, index) => {
    const where = `documents[${index}]`;
    const doc = record(raw, where);
    return {
      id: number(doc, 'id', where),
      title: string(doc, 'title', where),
      // The BFF renames the store's `faqCount` here, once, so no screen has to.
      answerCount: number(doc, 'answerCount', where),
      updatedAt: string(doc, 'updatedAt', where),
    };
  });

/** `{ document: DocumentDetailDTO }` — the source text, exactly as it was read. */
const readDocument = payload => {
  const doc = envelope(payload, 'document');
  return {
    id: number(doc, 'id', 'document'),
    title: string(doc, 'title', 'document'),
    content: string(doc, 'content', 'document'),
    // P-6.2: the answers saved one by one, which are never removed all together. A BFF older
    // than the flag never marked one, and "not reserved" is what it meant.
    reserved: optionalBool(doc, 'reserved', 'document'),
  };
};

/**
 * `{ answers: DocumentAnswersDTO }` — one page of what a source taught.
 *
 * `total` is read strictly rather than defaulted to `items.length`: the difference
 * between them is the whole point of the page ("showing the first 50 of 214"), and a
 * client that quietly substituted one for the other would tell an owner she is looking
 * at everything her assistant learned when she is looking at a quarter of it.
 */
const readDocumentAnswers = payload => {
  const answers = envelope(payload, 'answers');
  return {
    documentId: number(answers, 'documentId', 'answers'),
    title: string(answers, 'title', 'answers'),
    total: number(answers, 'total', 'answers'),
    limit: number(answers, 'limit', 'answers'),
    offset: number(answers, 'offset', 'answers'),
    items: listEnvelope(answers, 'items').map((raw, index) => {
      const where = `answers.items[${index}]`;
      const item = record(raw, where);
      return {
        id: number(item, 'id', where),
        question: string(item, 'question', where),
        answer: string(item, 'answer', where),
      };
    }),
  };
};

/**
 * `{ job: JobRefDTO }` — all a write returns. Nothing has been created yet, so there is
 * nothing else honest to hand back; the screen watches `/jobs` from here.
 */
const readJobRef = payload => {
  const job = envelope(payload, 'job');
  return {
    id: number(job, 'id', 'job'),
    // True when an identical source was already being read. The screen says so rather
    // than pretending it queued something new.
    deduped: bool(job, 'deduped', 'job'),
  };
};

/** `{ deleted: { id } }` — the id the BFF actually removed, not the one we asked about. */
const readDeleted = payload => {
  const deleted = envelope(payload, 'deleted');
  return { id: number(deleted, 'id', 'deleted') };
};

/** `{ dismissed: { id } }` — `routes/api/jobs.ts`. */
const readDismissed = payload => {
  const dismissed = envelope(payload, 'dismissed');
  return { id: number(dismissed, 'id', 'dismissed') };
};

// ── undo (P-6.11) ───────────────────────────────────────────────────────────────────
// Who made a change is one of four words the BFF picks (`routes/api/revisions.ts`), never an id.
const UNDO_WHO = ['you', 'teammate', 'claude', 'waslo'];
const REMOVED_KINDS = [
  'source',
  'answer',
  'task',
  'menu',
  'connection',
  'notice',
];
const RESTORED_KINDS = ['personality', ...REMOVED_KINDS];

/** `{ revisions: RevisionDTO[] }` — one thing's saves, newest first. */
const readRevisions = payload =>
  listEnvelope(payload, 'revisions').map((raw, index) => {
    const where = `revisions[${index}]`;
    const row = record(raw, where);
    return {
      id: number(row, 'id', where),
      at: string(row, 'at', where),
      by: oneOf(row, 'by', UNDO_WHO, where),
    };
  });

/** `{ removed: RemovedDTO[] }` — Recently removed, 30 days. */
const readRemoved = payload =>
  listEnvelope(payload, 'removed').map((raw, index) => {
    const where = `removed[${index}]`;
    const row = record(raw, where);
    return {
      kind: oneOf(row, 'kind', REMOVED_KINDS, where),
      id: number(row, 'id', where),
      label: string(row, 'label', where),
      removedAt: string(row, 'removedAt', where),
      by: oneOf(row, 'by', UNDO_WHO, where),
    };
  });

/** `{ restored: RestoredDTO }` — what came back, renamed or switched off. */
const readRestored = payload => {
  const restored = envelope(payload, 'restored');
  return {
    kind: oneOf(restored, 'kind', RESTORED_KINDS, 'restored'),
    id: nullableNumber(restored, 'id', 'restored'),
    renamed: bool(restored, 'renamed', 'restored'),
    switchedOff: bool(restored, 'switchedOff', 'restored'),
  };
};

/**
 * P-5.9: chats that reached a task (and finished it), or were shown a menu, in the last 7
 * days. `null` is "we could not count", which the card then leaves out; never a zero.
 */
const readUsage = (row, where, withFinished) => {
  if (row.usage7d === null) return null;
  const usage = nested(row, 'usage7d', where);
  return {
    conversations: number(usage, 'conversations', `${where}.usage7d`),
    ...(withFinished
      ? { finished: number(usage, 'finished', `${where}.usage7d`) }
      : {}),
  };
};

/**
 * `{ tasks: TaskSummaryDTO[] }` — `routes/api/scenarios.ts`.
 *
 * NOTE WHAT IS NOT HERE: the machine name, and any step count. The BFF derives the first
 * and never ships it, and the sidecar's list projection genuinely does not carry the
 * second — so the list screen shows what a summary can know and nothing more, rather than
 * rendering "0 steps" for a task that has four.
 */
const readTasks = payload =>
  listEnvelope(payload, 'tasks').map((raw, index) => {
    const where = `tasks[${index}]`;
    const task = record(raw, where);
    return {
      id: number(task, 'id', where),
      title: string(task, 'title', where),
      trigger: string(task, 'trigger', where),
      enabled: bool(task, 'enabled', where),
      connectionCount: number(task, 'connectionCount', where),
      // Whether this task is written as steps. The list carries the FACT and never the
      // definition — a workspace with thirty tasks would otherwise ship thirty graphs to
      // draw a chip (`GRAPH-API-CONTRACT.md` §1: "summary() gains hasGraph only").
      hasGraph: bool(task, 'hasGraph', where),
      updatedAt: string(task, 'updatedAt', where),
      // P-10.26: the channels it may start on, or `null` for every channel. ABSENT (a BFF
      // older than B4) is every channel, which is what that BFF's sidecar did.
      channels:
        task.channels === undefined || task.channels === null
          ? null
          : readStringList(task, 'channels', where),
      // P-5.9: ABSENT means "not counted" (no key at all), `null` means "not known".
      ...(task.usage7d === undefined
        ? {}
        : { usage7d: readUsage(task, where, true) }),
    };
  });

/** `{ task: TaskDetailDTO }` — the editor's read, and every write's answer. */
/**
 * The step graph, exactly as the server sent it, or `null`.
 *
 * THE ONE FIELD IN THIS FILE THAT IS NOT READ APART, and that is the contract rather than
 * laziness. `GRAPH-API-CONTRACT.md` §4: a definition written by a NEWER sidecar must survive
 * an older builder untouched rather than be quietly narrowed to the fields this build
 * happens to know about — and reading it key by key is precisely how that narrowing happens.
 * What the map does with an unfamiliar step type is the map's business (it draws it and says
 * it cannot read it); what this reader must not do is delete it on the way in.
 *
 * ABSENT IS `null`, not a throw, unlike everything else here: a BFF older than this reader
 * does not send the field at all, and a task written before the step graph existed genuinely
 * has no graph. Both mean "there is nothing to draw", which is a state the map renders. A
 * definition that is present but is NOT an object is a contract breach and still throws.
 */
/**
 * THE STEP GRAPH — the one field here that is deliberately NOT narrowed.
 *
 * Every other reader in this file narrows, because a missing field must not become a
 * confident zero. This one is carried VERBATIM: the sidecar owns the shape
 * (`src/tasks/types.ts`), a newer sidecar will add fields to it, and a builder that read it
 * into named keys would hand those fields back STRIPPED on the next save — silent data loss
 * on somebody's task. Not narrowing costs nothing, because the builder never dereferences a
 * field it does not recognise (`helpers/task-flow-draft.js`).
 *
 * `null` is a REAL answer — "an old-style task, written as free text" — and the editor keeps
 * the old form for it.
 *
 * ABSENT THROWS, like every other field the contract promises. Two agents built this file in
 * parallel and disagreed here — one tolerated absent as `null`, one threw — so the choice is
 * recorded rather than left to whoever edits next. It throws because `hasGraph` sits three
 * lines below and is read strictly, and two adjacent contract fields with opposite absence
 * policies is a worse bug than either policy: the next reader cannot tell which is the rule.
 * Services also deploy BEFORE the fork (`docs/step-driven-tasks.md` §7), so absent is a
 * rollback-only state — exactly where a named, loud error beats a screen that quietly shows
 * the free-text editor for a task that has a graph.
 */
const taskDefinition = task => {
  if (!('definition' in task)) throw malformed('task.definition');
  const value = task.definition;
  if (value === null) return null;
  return record(value, 'task.definition');
};

const readTask = payload => {
  const task = envelope(payload, 'task');
  return {
    definition: taskDefinition(task),
    id: number(task, 'id', 'task'),
    title: string(task, 'title', 'task'),
    trigger: string(task, 'trigger', 'task'),
    enabled: bool(task, 'enabled', 'task'),
    connectionCount: number(task, 'connectionCount', 'task'),
    // Redundant beside `definition` and read anyway: the BFF's `detail()` is
    // `{ ...summary(r), … }` (`GRAPH-API-CONTRACT.md` §1), so it is on the wire, and a reader
    // that quietly dropped a field the contract puts there is how the two repos start
    // disagreeing about what the contract says.
    hasGraph: bool(task, 'hasGraph', 'task'),
    updatedAt: string(task, 'updatedAt', 'task'),
    // P-10.26, read as the list reads it.
    channels:
      task.channels === undefined || task.channels === null
        ? null
        : readStringList(task, 'channels', 'task'),
    /**
     * Bumped by the SERVER, and only when the definition really changed (the contract, §1).
     * The builder never computes it and never sends it; it is here because the engine reads
     * it off a running chat's state to notice a graph that moved under it.
     */
    version: number(task, 'version', 'task'),
    /**
     * ── `steps` AND `collect` ARE NOT READ HERE, AND THAT IS THE POINT OF THIS COMMENT ──
     *
     * They were the FREE-TEXT task: a list of written instructions and a list of details to
     * ask for. Both were read strictly, both threw `malformed` when absent, and the only
     * thing in this fork that ever dereferenced either was `TaskEditorPage` and the draft
     * helper under it — deleted with this change, because a task is a step graph now and
     * `TaskComposePage` has held both task routes since it replaced that page.
     *
     * A STRICT READ OF A FIELD NOBODY RENDERS IS NOT CAUTION, IT IS A TRIPWIRE. The BFF
     * still puts both on the wire (`taskDetailDTO`), so nothing breaks today; the moment it
     * stops, a reader that throws on an absent key takes every task screen down for a field
     * no screen was drawing. Which is also why THIS half ships first: a screen that has
     * stopped reading a key is fine against a server that still sends it, and never the
     * reverse.
     *
     * `connections` below IS still read, and is deliberately not in this paragraph: it is a
     * real value — the names of the systems this task may use (`row.toolNames`), the same
     * list the BFF counts into `connectionCount` — and not a leftover of the old editor.
     */
    connections: listEnvelope(task, 'connections').map((name, index) => {
      if (typeof name !== 'string')
        throw malformed(`task.connections[${index}]`);
      return name;
    }),
  };
};

/**
 * P-4.10: the Arrange page's preview, as the BFF's `formPreviewDTO` projects it. STRICT: a
 * missing list is a broken contract, never an empty form. `seed` is the arranged form the page
 * starts from, carried as the engine's own shape; `detail` is the one optional key, read as
 * `null` when absent.
 */
const optionalDetail = item =>
  typeof item.detail === 'string' ? item.detail : null;

const readFormPreview = payload => {
  const body = record(payload, 'form-preview');
  return {
    pages: listEnvelope(body, 'pages').map((raw, index) => {
      const at = `pages[${index}]`;
      const page = record(raw, at);
      return {
        id: string(page, 'id', at),
        title: string(page, 'title', at),
        intro: nullableString(page, 'intro', at),
        fieldNames: readStringList(page, 'fieldNames', at),
        button: string(page, 'button', at),
      };
    }),
    fields: listEnvelope(body, 'fields').map((raw, index) => {
      const at = `fields[${index}]`;
      const field = record(raw, at);
      return {
        name: string(field, 'name', at),
        stepId: string(field, 'stepId', at),
        shape: string(field, 'shape', at),
        label: string(field, 'label', at),
        labelTruncated: bool(field, 'labelTruncated', at),
        required: bool(field, 'required', at),
        options: readStringList(field, 'options', at),
      };
    }),
    stayInChat: listEnvelope(body, 'stayInChat').map((raw, index) => {
      const at = `stayInChat[${index}]`;
      const stay = record(raw, at);
      return {
        stepId: string(stay, 'stepId', at),
        name: string(stay, 'name', at),
        reason: string(stay, 'reason', at),
      };
    }),
    layoutIssues: listEnvelope(body, 'layoutIssues').map((raw, index) => {
      const at = `layoutIssues[${index}]`;
      const issue = record(raw, at);
      return {
        stepId: string(issue, 'stepId', at),
        warning: string(issue, 'warning', at),
        detail: optionalDetail(issue),
      };
    }),
    errors: listEnvelope(body, 'errors').map((raw, index) => {
      const at = `errors[${index}]`;
      const error = record(raw, at);
      return {
        stepId: string(error, 'stepId', at),
        problem: string(error, 'problem', at),
        detail: optionalDetail(error),
      };
    }),
    seed: body.seed === null ? null : record(body.seed, 'form-preview.seed'),
  };
};

/**
 * P-10.21: which ON tasks use a switched-off connection, and how many ON tasks use each one.
 * `inUse` keys are connection names; a name no ON task uses is absent, never 0.
 */
const readConnectionUse = payload => {
  const body = record(payload, 'connection-use');
  if (!Array.isArray(body.off)) {
    throw malformed('expected `connection-use.off` to be a list');
  }
  const off = body.off.map((raw, index) => {
    const where = `off[${index}]`;
    const item = record(raw, where);
    return {
      taskId: number(item, 'taskId', where),
      title: string(item, 'title', where),
      connection: string(item, 'connection', where),
    };
  });
  const counts = record(body.inUse, 'connection-use.inUse');
  const inUse = {};
  Object.keys(counts).forEach(name => {
    inUse[name] = number(counts, name, 'connection-use.inUse');
  });
  return { off, inUse };
};

/**
 * `{ connections: ConnectionChoiceDTO[] }` — what a task may be given to use.
 *
 * `id` IS THE ONE OPTIONAL FIELD HERE, and it is optional for a deploy-order reason rather
 * than a modelling one. The step editor's "Try it" calls `/connections/:id/try`, so it needs
 * a numeric id that this projection did not originally carry; the BFF is adding one. The fork
 * image and the app tier deploy independently, so this reader has to work against a BFF on
 * either side of that change — present means the editor needs no second read, absent means it
 * falls back to `/connections` for the ids alone. Narrowing it to `0` would be worse than
 * absent: `0` is a plausible id and would send "Try it" at the wrong row.
 */
const readTaskConnections = payload =>
  listEnvelope(payload, 'connections').map((raw, index) => {
    const where = `connections[${index}]`;
    const item = record(raw, where);
    return {
      name: string(item, 'name', where),
      description: string(item, 'description', where),
      enabled: bool(item, 'enabled', where),
      ...(item.id === undefined ? {} : { id: number(item, 'id', where) }),
      ...(item.purpose === undefined
        ? {}
        : { purpose: oneOf(item, 'purpose', CONNECTION_PURPOSES, where) }),
      // P-2.11: the field names of its last good test's answer. ABSENT is `[]`: no picker.
      samplePaths:
        item.samplePaths === undefined
          ? []
          : readStringList(item, 'samplePaths', where),
    };
  });

// ── menus — `routes/api/menus.ts` ─────────────────────────────────────────────────────
const readMenuSummary = (menu, where) => ({
  id: number(menu, 'id', where),
  title: string(menu, 'title', where),
  whenToShow: string(menu, 'whenToShow', where),
  enabled: bool(menu, 'enabled', where),
  optionCount: number(menu, 'optionCount', where),
  updatedAt: string(menu, 'updatedAt', where),
});

/** `{ menus: MenuSummaryDTO[] }`. The detail read does not carry `usage7d`, so only the list does. */
const readMenus = payload =>
  listEnvelope(payload, 'menus').map((raw, index) => {
    const where = `menus[${index}]`;
    const menu = record(raw, where);
    return {
      ...readMenuSummary(menu, where),
      ...(menu.usage7d === undefined
        ? {}
        : { usage7d: readUsage(menu, where, false) }),
    };
  });

/**
 * `{ menu: MenuDetailDTO }` — the editor's read and every write's answer. `taskId` is read
 * with `nullableNumber`: null is a real answer ("starts nothing"), a MISSING field is a bug.
 */
const readMenu = payload => {
  const menu = envelope(payload, 'menu');
  return {
    ...readMenuSummary(menu, 'menu'),
    bodyEn: string(menu, 'bodyEn', 'menu'),
    bodyAr: string(menu, 'bodyAr', 'menu'),
    buttonEn: string(menu, 'buttonEn', 'menu'),
    buttonAr: string(menu, 'buttonAr', 'menu'),
    options: listEnvelope(menu, 'options').map((raw, index) => {
      const where = `menu.options[${index}]`;
      const option = record(raw, where);
      return {
        id: string(option, 'id', where),
        titleEn: string(option, 'titleEn', where),
        titleAr: string(option, 'titleAr', where),
        taskId: nullableNumber(option, 'taskId', where),
      };
    }),
  };
};

/** `{ tasks: MenuTaskChoiceDTO[] }` — what an option may start. */
const readMenuTasks = payload =>
  listEnvelope(payload, 'tasks').map((raw, index) => {
    const where = `tasks[${index}]`;
    const task = record(raw, where);
    return {
      id: number(task, 'id', where),
      title: string(task, 'title', where),
      enabled: bool(task, 'enabled', where),
    };
  });

/**
 * P-5.1: the state of the channel this chat arrived on, or `null`. Absent (an older BFF),
 * `null` (the BFF could not read it) and a state this build has no words for all read as
 * `null`, and the panel then says on and off exactly as it did before: the panel is the one
 * place a Waslo error must never cost the agent the pane.
 */
const readPanelStatus = assistant => {
  const raw = assistant.status;
  if (raw === undefined || raw === null) return null;
  const status = record(raw, 'assistant.status');
  if (!STATUS_STATES.includes(status.state)) return null;
  return {
    state: status.state,
    fix: STATUS_FIXES.includes(status.fix) ? status.fix : 'none',
    resumesAt: nullableString(status, 'resumesAt', 'assistant.status'),
    inboxId: nullableNumber(status, 'inboxId', 'assistant.status'),
  };
};

/**
 * `{ assistant: ConversationAssistantDTO }` — `routes/api/knowledge.ts`, the conversation
 * pane (THE PLAN, §4 P11).
 *
 * `lastTurn: null` is a REAL answer — the assistant has not replied in this conversation —
 * and it is the only nullable object here, which is the point: `bestScore: null` beside
 * `matches: 0` is likewise a stated absence ("it searched and found nothing"), never a
 * field that failed to arrive. An agent reading this pane is deciding whether to correct
 * the assistant, and "it found nothing" and "we could not tell" are different decisions.
 */
const readConversationAssistant = payload => {
  const assistant = envelope(payload, 'assistant');
  const lastTurn = assistant.lastTurn;
  return {
    conversationId: number(assistant, 'conversationId', 'assistant'),
    aiEnabled: bool(assistant, 'aiEnabled', 'assistant'),
    turns: number(assistant, 'turns', 'assistant'),
    suggestedQuestion: string(assistant, 'suggestedQuestion', 'assistant'),
    status: readPanelStatus(assistant),
    lastTurn:
      lastTurn === null
        ? null
        : (() => {
            const turn = record(lastTurn, 'assistant.lastTurn');
            return {
              at: string(turn, 'at', 'assistant.lastTurn'),
              customer: string(turn, 'customer', 'assistant.lastTurn'),
              // '' is real: it handed over without saying anything.
              assistant: string(turn, 'assistant', 'assistant.lastTurn'),
              handoff: bool(turn, 'handoff', 'assistant.lastTurn'),
              lookups: listEnvelope(turn, 'lookups').map((raw, index) => {
                const where = `assistant.lastTurn.lookups[${index}]`;
                const lookup = record(raw, where);
                return {
                  query: string(lookup, 'query', where),
                  matches: number(lookup, 'matches', where),
                  bestScore:
                    lookup.bestScore === null
                      ? null
                      : number(lookup, 'bestScore', where),
                };
              }),
              actions: listEnvelope(turn, 'actions').map((name, index) => {
                if (typeof name !== 'string') {
                  throw malformed(`assistant.lastTurn.actions[${index}]`);
                }
                return name;
              }),
            };
          })(),
  };
};

/** An answer a save replaced, or one that says a different number (P-6.3). */
const readAnswerRef = (raw, where) => {
  const ref = record(raw, where);
  return {
    id: number(ref, 'id', where),
    question: string(ref, 'question', where),
    answer: string(ref, 'answer', where),
    sourceTitle: string(ref, 'sourceTitle', where),
  };
};

/** `replaced` and `conflicts` beside a save (P-6.3). Absent from an older BFF: none. */
const readSaveCheck = payload => ({
  replaced:
    payload?.replaced === undefined
      ? []
      : listEnvelope(payload, 'replaced').map((raw, i) =>
          readAnswerRef(raw, `replaced[${i}]`)
        ),
  conflicts:
    payload?.conflicts === undefined
      ? []
      : listEnvelope(payload, 'conflicts').map((raw, i) =>
          readAnswerRef(raw, `conflicts[${i}]`)
        ),
});

/**
 * `{ answer: TaughtAnswerDTO }` — what was SAVED, echoed back.
 *
 * `created: false` means an answer already taught for this question was replaced, and the
 * pane says so out loud: an agent who thinks she added a second answer when she overwrote
 * the first has been told the opposite of what happened.
 */
const readTaughtAnswer = payload => {
  const answer = envelope(payload, 'answer');
  return {
    // P-6.4: the saved answer's id, for "Check it now". Absent from an older BFF.
    id: optionalNullableNumber(answer, 'id', 'answer'),
    question: string(answer, 'question', 'answer'),
    answer: string(answer, 'answer', 'answer'),
    created: bool(answer, 'created', 'answer'),
    total: number(answer, 'total', 'answer'),
    ...readSaveCheck(payload),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

// ── knowledge the owner can fix (ease C3, spec §6) ──────────────────────────────────────

/** One row of "All answers" (P-6.1). `mine`: the words are hers. */
const readAnswer = (raw, where) => {
  const row = record(raw, where);
  return {
    id: number(row, 'id', where),
    question: string(row, 'question', where),
    answer: string(row, 'answer', where),
    mine: bool(row, 'mine', where),
    enabled: bool(row, 'enabled', where),
    documentId: number(row, 'documentId', where),
    sourceTitle: string(row, 'sourceTitle', where),
    updatedAt: string(row, 'updatedAt', where),
    replacedById: nullableNumber(row, 'replacedById', where),
  };
};

/** `{ answers: { total, page, pageSize, items }, charged }`. */
const readAnswerPage = payload => {
  const page = envelope(payload, 'answers');
  return {
    total: number(page, 'total', 'answers'),
    page: number(page, 'page', 'answers'),
    pageSize: number(page, 'pageSize', 'answers'),
    items: listEnvelope(page, 'items').map((raw, i) =>
      readAnswer(raw, `answers.items[${i}]`)
    ),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

/** `{ answer, replaced, conflicts, charged }`, a saved edit or switch. */
const readAnswerSave = payload => ({
  answer: readAnswer(envelope(payload, 'answer'), 'answer'),
  ...readSaveCheck(payload),
  charged: optionalNullableNumber(payload, 'charged', 'the response'),
});

/** `{ check: { now, handoff }, charged }`, "Check it now" (P-6.4). */
const readAnswerCheck = payload => {
  const check = envelope(payload, 'check');
  return {
    now: string(check, 'now', 'check'),
    handoff: bool(check, 'handoff', 'check'),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

const SCAN_STATES = ['running', 'done', 'failed'];

/** `{ scan }`, "Look through my team's replies" (P-6.5). */
const readScanState = payload => {
  const scan = envelope(payload, 'scan');
  const last = scan.last;
  return {
    today: string(scan, 'today', 'scan'),
    canScan: bool(scan, 'canScan', 'scan'),
    last:
      last === null
        ? null
        : (() => {
            const row = record(last, 'scan.last');
            return {
              state: oneOf(row, 'state', SCAN_STATES, 'scan.last'),
              day: string(row, 'day', 'scan.last'),
              suggested: number(row, 'suggested', 'scan.last'),
              charged: nullableNumber(row, 'charged', 'scan.last'),
            };
          })(),
  };
};

/** One "For now" notice (P-6.9). */
const readNotice = (raw, where) => {
  const notice = record(raw, where);
  return {
    id: number(notice, 'id', where),
    text: string(notice, 'text', where),
    endsOn: string(notice, 'endsOn', where),
    endsTomorrow: bool(notice, 'endsTomorrow', where),
  };
};

const readNotices = payload =>
  listEnvelope(payload, 'notices').map((raw, i) =>
    readNotice(raw, `notices[${i}]`)
  );

/**
 * `{ panel: ChatPanelDTO }`, the MEMBER route `routes/api/member.ts` (P-1.6 to P-1.8). One read
 * for administrators and staff alike; `isAdmin` decides what the pane offers. `lookups` carry a
 * closeness WORD, never a score.
 */
const readChatPanel = payload => {
  const panel = envelope(payload, 'panel');
  const lastTurn = panel.lastTurn;
  return {
    conversationId: number(panel, 'conversationId', 'panel'),
    isAdmin: bool(panel, 'isAdmin', 'panel'),
    accountOn:
      panel.accountOn === null ? null : bool(panel, 'accountOn', 'panel'),
    turns: number(panel, 'turns', 'panel'),
    status: readPanelStatus(panel),
    suggestedQuestion: string(panel, 'suggestedQuestion', 'panel'),
    lastTurn:
      lastTurn === null
        ? null
        : (() => {
            const where = 'panel.lastTurn';
            const turn = record(lastTurn, where);
            return {
              at: string(turn, 'at', where),
              customer: string(turn, 'customer', where),
              assistant: string(turn, 'assistant', where),
              handoff: bool(turn, 'handoff', where),
              lookups: listEnvelope(turn, 'lookups').map((raw, index) => {
                const at = `${where}.lookups[${index}]`;
                const lookup = record(raw, at);
                return {
                  query: string(lookup, 'query', at),
                  matches: number(lookup, 'matches', at),
                  closeness: oneOf(lookup, 'closeness', CLOSENESS, at),
                };
              }),
              actions: listEnvelope(turn, 'actions').map((name, index) => {
                if (typeof name !== 'string') {
                  throw malformed(`${where}.actions[${index}]`);
                }
                return name;
              }),
              why: readWhy(turn.why, `${where}.why`),
            };
          })(),
  };
};

/** `{ suggestion: { id, question, answer, createdAt } }`, what a staff member sent. */
const readStaffSuggestion = payload => {
  const suggestion = envelope(payload, 'suggestion');
  return {
    id: number(suggestion, 'id', 'suggestion'),
    question: string(suggestion, 'question', 'suggestion'),
    answer: string(suggestion, 'answer', 'suggestion'),
    createdAt: string(suggestion, 'createdAt', 'suggestion'),
  };
};

const SUGGESTION_ORIGINS = ['staff_tap', 'staff_reply'];
const SUGGESTION_STATUSES = ['pending', 'accepted', 'dismissed'];

const readTeamSuggestionRow = (raw, where) => {
  const row = record(raw, where);
  const by = row.suggestedBy;
  return {
    id: number(row, 'id', where),
    origin: oneOf(row, 'origin', SUGGESTION_ORIGINS, where),
    question: string(row, 'question', where),
    answer: string(row, 'answer', where),
    conversationId: nullableNumber(row, 'conversationId', where),
    suggestedBy:
      by === null
        ? null
        : (() => {
            const person = record(by, `${where}.suggestedBy`);
            return {
              id: number(person, 'id', `${where}.suggestedBy`),
              name: string(person, 'name', `${where}.suggestedBy`),
            };
          })(),
    chatCount: number(row, 'chatCount', where),
    status: oneOf(row, 'status', SUGGESTION_STATUSES, where),
    createdAt: string(row, 'createdAt', where),
  };
};

/** `{ suggestions: SuggestionDTO[], pending }`, "Suggested by your team" (P-1.7). */
const readTeamSuggestions = payload => ({
  items: listEnvelope(payload, 'suggestions').map((raw, index) =>
    readTeamSuggestionRow(raw, `suggestions[${index}]`)
  ),
  pending: number(payload, 'pending', 'the response'),
});

/** Accept: the suggestion, the answer it became, and what it cost (P-5.12). */
const readAcceptedSuggestion = payload => {
  const answer = envelope(payload, 'answer');
  return {
    suggestion: readTeamSuggestionRow(
      envelope(payload, 'suggestion'),
      'suggestion'
    ),
    answer: {
      question: string(answer, 'question', 'answer'),
      answer: string(answer, 'answer', 'answer'),
      created: bool(answer, 'created', 'answer'),
      total: number(answer, 'total', 'answer'),
    },
    ...readSaveCheck(payload),
    charged: optionalNullableNumber(payload, 'charged', 'the response'),
  };
};

const PEOPLE_ROLES = ['administrator', 'agent'];

/** `{ people }`, who "Hand chats to" can name (P-1.3). */
const readHandoverPeople = payload => {
  const people = envelope(payload, 'people');
  const named = (raw, where) => {
    const row = record(raw, where);
    return { id: number(row, 'id', where), name: string(row, 'name', where) };
  };
  return {
    agents: listEnvelope(people, 'agents').map((raw, index) => {
      const where = `people.agents[${index}]`;
      const agent = record(raw, where);
      return {
        id: number(agent, 'id', where),
        name: string(agent, 'name', where),
        role: oneOf(agent, 'role', PEOPLE_ROLES, where),
        missingInboxes: listEnvelope(agent, 'missingInboxes').map((row, at) =>
          named(row, `${where}.missingInboxes[${at}]`)
        ),
      };
    }),
    teams: listEnvelope(people, 'teams').map((row, index) =>
      named(row, `people.teams[${index}]`)
    ),
    membershipKnown: bool(people, 'membershipKnown', 'people'),
  };
};

// ── WhatsApp broadcasts ─────────────────────────────────────────────────────────────
// The only reads and writes in this section that cost the owner money per message. Every
// shape below is pinned in `services/authoring-web/test/routes-api-broadcasts.test.ts`
// and in `specs/broadcasts.spec.js`, on both sides of the same literal.

/**
 * The seven statuses the BFF is allowed to answer with.
 *
 * `unknown` is the SEVENTH and it is this service's invention: `sidecar.ts`'s status union
 * is hand-written, so a status added upstream reaches the browser unvalidated. Refusing it
 * here would take a whole list down over one row, so the BFF maps anything it does not
 * recognise onto `unknown` and the row says so instead of guessing.
 */
const BROADCAST_STATUSES = [
  'draft',
  'running',
  'paused',
  'completed',
  'cancelled',
  'failed',
  'unknown',
];

const readBroadcastRow = (raw, where) => {
  const row = record(raw, where);
  return {
    id: number(row, 'id', where),
    title: string(row, 'title', where),
    status: oneOf(row, 'status', BROADCAST_STATUSES, where),
    channelId: number(row, 'channelId', where),
    recipientCount: number(row, 'recipientCount', where),
    excludedCount: number(row, 'excludedCount', where),
    sentCount: number(row, 'sentCount', where),
    deliveredCount: number(row, 'deliveredCount', where),
    failedCount: number(row, 'failedCount', where),
    /**
     * DISPATCHED, AND NO PROVIDER HAS SPOKEN ABOUT IT YET — the bucket that used to be
     * counted as sent. A run finalises as soon as nothing is left queued or sending,
     * which is normally BEFORE the first delivery callback lands, so on a healthy
     * hundred-recipient send this is the overwhelming majority of it.
     *
     * Optional for one release only, and safe for exactly the reason `optionalNumber`
     * requires: a BFF from before this bucket existed folded these rows into
     * `sentCount`, and what it sends is still internally consistent — it simply has
     * nothing separate to report, which a `0` renders as (no pending fragment, no
     * pending tile) rather than as a wrong claim. Once every environment is past
     * services #104 this can go back to `number`.
     */
    pendingCount: optionalNumber(row, 'pendingCount', where),
    launchedAt: nullableString(row, 'launchedAt', where),
    finishedAt: nullableString(row, 'finishedAt', where),
    createdAt: string(row, 'createdAt', where),
  };
};

/** `{ broadcasts: BroadcastRowDTO[] }` — `routes/api/broadcasts.ts`. */
const readBroadcasts = payload =>
  listEnvelope(payload, 'broadcasts').map((raw, index) =>
    readBroadcastRow(raw, `broadcasts[${index}]`)
  );

/** The nine recipient states, all present, all numbers. */
const PROGRESS_KEYS = [
  'queued',
  'sending',
  'accepted',
  'sent',
  'delivered',
  'read',
  'failed',
  'skipped',
  'unconfirmed',
];

/**
 * `{ broadcast: BroadcastDTO, progress: BroadcastProgressDTO|null }`.
 *
 * `progress: null` IS A REAL ANSWER — "the run loaded, its breakdown did not" — and it is
 * read as one rather than defaulted to zeros. A zero-filled breakdown is indistinguishable
 * on screen from a send where nothing has happened yet, which on a live broadcast is the
 * one thing it must not be mistaken for.
 */
const readBroadcast = payload => {
  const broadcast = envelope(payload, 'broadcast');
  const rawProgress =
    payload && typeof payload === 'object' ? payload.progress : undefined;
  let progress = null;
  if (rawProgress !== null && rawProgress !== undefined) {
    const bag = record(rawProgress, 'progress');
    progress = {};
    PROGRESS_KEYS.forEach(key => {
      progress[key] = number(bag, key, 'progress');
    });
  }
  return {
    ...readBroadcastRow(broadcast, 'broadcast'),
    bodyPreview: nullableString(broadcast, 'bodyPreview', 'broadcast'),
    error: nullableString(broadcast, 'error', 'broadcast'),
    progress,
  };
};

/** `{ channels: BroadcastChannelDTO[] }` — the WhatsApp numbers a send can go out from. */
const readBroadcastChannels = payload =>
  listEnvelope(payload, 'channels').map((raw, index) => {
    const where = `channels[${index}]`;
    const channel = record(raw, where);
    return {
      id: number(channel, 'id', where),
      name: string(channel, 'name', where),
      phoneNumber: nullableString(channel, 'phoneNumber', where),
      // The BFF's boolean, not a `!== null` this client re-derives from an id it should
      // never have been shown.
      usable: bool(channel, 'usable', where),
    };
  });

const VALUE_KINDS = ['text', 'number', 'date', 'days', 'boolean', 'none'];

/**
 * `{ fields: AudienceFieldDTO[] }` — what an audience may be built from.
 *
 * The operator list is PER FIELD and is read strictly, because it is not decoration: the
 * BFF derives it from the tables that are the only thing standing between an owner and a
 * query Postgres cannot run. A client that fell back to "all operators" would offer
 * conditions that 422 at preview time, after a minute of waiting.
 */
const readAudienceFields = payload =>
  listEnvelope(payload, 'fields').map((raw, index) => {
    const where = `fields[${index}]`;
    const field = record(raw, where);
    return {
      key: string(field, 'key', where),
      label: string(field, 'label', where),
      custom: bool(field, 'custom', where),
      operators: listEnvelope(field, 'operators').map((rawOp, at) => {
        const opWhere = `${where}.operators[${at}]`;
        const operator = record(rawOp, opWhere);
        return {
          id: string(operator, 'id', opWhere),
          label: string(operator, 'label', opWhere),
          valueKind: oneOf(operator, 'valueKind', VALUE_KINDS, opWhere),
        };
      }),
    };
  });

/** `{ templates: MessageTemplateDTO[] }` — `routes/api/templates.ts`. */
const readMessageTemplates = payload =>
  listEnvelope(payload, 'templates').map((raw, index) => {
    const where = `templates[${index}]`;
    const template = record(raw, where);
    return {
      name: string(template, 'name', where),
      language: string(template, 'language', where),
      /**
       * EVERY blank the compose screen asks for, in order (P-4.3): the body's, then the title's
       * (`header:<name>`), then each button that takes a value (`button:<index>`). The compose page
       * iterates this list, so the new rows exist without touching it. A BFF older than D1 sends no
       * `bindingKeys`, and then the body's blanks are the whole list, as before.
       */
      variables: (template.bindingKeys === undefined
        ? listEnvelope(template, 'variables')
        : listEnvelope(template, 'bindingKeys')
      ).map((value, at) => {
        if (typeof value !== 'string') {
          throw malformed(`${where}.variables[${at}]`);
        }
        return value;
      }),
      body: string(template, 'body', where),
      category: string(template, 'category', where),
      sendable: bool(template, 'sendable', where),
      // Present exactly when `sendable` is false. Read as nullable rather than defaulted,
      // so a greyed-out choice can never sit there with no reason beside it.
      unsendableReason: nullableString(template, 'unsendableReason', where),
    };
  });

/** The skip codes this screen translates (P-3.7). One it does not know is left out. */
const SKIP_CODES = [
  'no_phone',
  'unusable_phone',
  'suppressed',
  'render_failed',
];

/**
 * `{ preview: SendPreviewDTO, fingerprint: string }`.
 *
 * The fingerprint is the whole reason the confirm step can be trusted, so it is read as a
 * required string: a preview with nothing to send back is a preview the send would refuse,
 * and finding that out here is better than finding it out after the owner presses Send.
 */
const readSendPreview = payload => {
  const preview = envelope(payload, 'preview');
  // The fingerprint is a SIBLING of `preview`, not a member of it: it describes the state
  // the preview was computed from, not the preview.
  const body = record(payload, 'the response');
  return {
    willReceive: number(preview, 'willReceive', 'preview'),
    willBeSkipped: number(preview, 'willBeSkipped', 'preview'),
    skipReasons: listEnvelope(preview, 'skipReasons').map((raw, index) => {
      const where = `preview.skipReasons[${index}]`;
      const item = record(raw, where);
      return {
        reason: string(item, 'reason', where),
        count: number(item, 'count', where),
        // P-3.7: the sentence is then the owner's, from `helpers/skip-reasons.js`. A code this
        // screen does not know is dropped and the sidecar's own sentence is shown.
        ...(SKIP_CODES.includes(item.code) ? { code: item.code } : {}),
      };
    }),
    // True when the reasons were grouped from a bounded sample while the total above is
    // exact. Two different kinds of number; the screen is told which it is holding.
    reasonsFromSample: bool(preview, 'reasonsFromSample', 'preview'),
    sampleSize: number(preview, 'sampleSize', 'preview'),
    fingerprint: string(body, 'fingerprint', 'the response'),
  };
};

/** `{ launched: { id, recipientCount } }` — the id to go and watch, and how many it went to. */
const readLaunched = payload => {
  const launched = envelope(payload, 'launched');
  return {
    id: number(launched, 'id', 'launched'),
    recipientCount: number(launched, 'recipientCount', 'launched'),
  };
};

// ── settings: permissions and connections (P8) ─────────────────────────────────────

const PERMISSION_KEYS = ['close', 'assign', 'labels', 'note', 'contact'];

// Lead capture (`contact`). The same closed sets the BFF's `routes/api/actions.ts` defines.
const CONTACT_FIELD_TYPES = [
  'standard',
  'text',
  'number',
  'currency',
  'percent',
  'link',
  'date',
  'list',
  'checkbox',
];
const INERT_REASONS = ['nothing_chosen', 'service_account_missing'];

/** One detail lead capture may save: `{ key, label, type, values?, description? }`. */
const readContactField = (raw, where) => {
  const field = record(raw, where);
  return {
    key: string(field, 'key', where),
    label: string(field, 'label', where),
    type: oneOf(field, 'type', CONTACT_FIELD_TYPES, where),
    ...(field.values === undefined
      ? {}
      : { values: stringArray(field, 'values', where) }),
    ...(field.description === undefined
      ? {}
      : { description: string(field, 'description', where) }),
  };
};

const readTitle = (value, where) => {
  if (typeof value !== 'string') throw malformed(where);
  return value;
};

/**
 * A list a BFF older than lead capture does not send. `optionalBool`'s rule, for a list:
 * ABSENT is `[]` — that server had no such list, so "nothing chosen" is literally what it
 * meant — and PRESENT is read as strictly as everything else here.
 */
const optionalList = (object, key, where, readItem) =>
  object[key] === undefined
    ? []
    : listEnvelope(object, key).map((item, index) =>
        readItem(item, `${where}.${key}[${index}]`)
      );

const readTarget = (raw, where) => {
  const target = record(raw, where);
  return {
    id: number(target, 'id', where),
    name: string(target, 'name', where),
  };
};

const readChosen = (raw, where) => {
  const chosen = record(raw, where);
  return {
    teams: listEnvelope(chosen, 'teams').map((row, index) =>
      readTarget(row, `${where}.teams[${index}]`)
    ),
    agents: listEnvelope(chosen, 'agents').map((row, index) =>
      readTarget(row, `${where}.agents[${index}]`)
    ),
    labels: listEnvelope(chosen, 'labels').map((label, index) => {
      if (typeof label !== 'string')
        throw malformed(`${where}.labels[${index}]`);
      return label;
    }),
    contactFields: optionalList(
      chosen,
      'contactFields',
      where,
      readContactField
    ),
    contactLabels: optionalList(chosen, 'contactLabels', where, readTitle),
  };
};

/**
 * Why an on permission cannot fire — READ, like `inert`. A BFF older than lead capture
 * sends no reason, and the only reason it ever had was "nothing chosen", so absence maps
 * to that when `inert` is set. Present, it is read strictly.
 */
const readInertReason = (row, inert, where) => {
  if (row.inertReason === undefined) return inert ? 'nothing_chosen' : null;
  if (row.inertReason === null) return null;
  return oneOf(row, 'inertReason', INERT_REASONS, where);
};

/**
 * One permission — `routes/api/actions.ts`.
 *
 * `inert` is READ, never re-derived. The BFF computes it from the same rule the sidecar's
 * `buildNativeActionTools` applies when it decides whether to register a tool at all, so
 * re-deriving it here as `enabled && needsChoices && nothing chosen` would be a second copy
 * of a rule that lives upstream — and the first time either moved, the card would say the
 * assistant can do something it cannot.
 */
const readPermissionRow = (raw, where) => {
  const row = record(raw, where);
  const inert = bool(row, 'inert', where);
  return {
    key: oneOf(row, 'key', PERMISSION_KEYS, where),
    needsChoices: bool(row, 'needsChoices', where),
    enabled: bool(row, 'enabled', where),
    guidance: string(row, 'guidance', where),
    chosen: readChosen(row.chosen, `${where}.chosen`),
    inert,
    inertReason: readInertReason(row, inert, where),
  };
};

/** `{ actions: PermissionDTO[] }` — all four, in the order the screen shows them. */
const readPermissions = payload =>
  listEnvelope(payload, 'actions').map((raw, index) =>
    readPermissionRow(raw, `actions[${index}]`)
  );

/** `{ action: PermissionDTO }` — what one write actually saved. */
const readPermission = payload =>
  readPermissionRow(envelope(payload, 'action'), 'action');

/** `{ targets: … }` — everything in this workspace it could be allowed to pick. */
const readTargets = payload => {
  const targets = envelope(payload, 'targets');
  return {
    teams: listEnvelope(targets, 'teams').map((row, index) =>
      readTarget(row, `targets.teams[${index}]`)
    ),
    agents: listEnvelope(targets, 'agents').map((row, index) =>
      readTarget(row, `targets.agents[${index}]`)
    ),
    labels: listEnvelope(targets, 'labels').map((label, index) => {
      if (typeof label !== 'string')
        throw malformed(`targets.labels[${index}]`);
      return label;
    }),
    contactFields: optionalList(
      targets,
      'contactFields',
      'targets',
      readContactField
    ),
  };
};

const CONNECTION_PARAM_TYPES = ['string', 'number', 'integer', 'boolean'];
const CONNECTION_AUTH_TYPES = ['none', 'bearer', 'header', 'basic'];

const CONNECTION_HEALTH_STATES = ['idle', 'failing', 'ok'];

/**
 * A connection's week of live use (P-3.5). `state` is the BFF's verdict and is never re-derived
 * here; an unknown one throws, because a row coloured by a guess is worse than a row that
 * failed to load.
 */
const readConnectionHealth = (raw, where) => {
  const health = record(raw, where);
  if (health.lastOk !== null && typeof health.lastOk !== 'boolean') {
    throw malformed(`${where}.lastOk`);
  }
  return {
    state: oneOf(health, 'state', CONNECTION_HEALTH_STATES, where),
    lastAt: nullableString(health, 'lastAt', where),
    lastOk: health.lastOk,
    worked: number(health, 'worked', where),
    failed: number(health, 'failed', where),
    lastStatus: nullableNumber(health, 'lastStatus', where),
    lastErrorKind: nullableString(health, 'lastErrorKind', where),
  };
};

const readConnectionSummary = (raw, where) => {
  const row = record(raw, where);
  return {
    id: number(row, 'id', where),
    name: string(row, 'name', where),
    description: string(row, 'description', where),
    method: string(row, 'method', where),
    enabled: bool(row, 'enabled', where),
    // NOT the secret, and there is no shape of this API that carries one: the sidecar
    // never returns it either. This boolean is the whole of what the browser learns.
    signInSaved: bool(row, 'signInSaved', where),
    updatedAt: string(row, 'updatedAt', where),
    // P-3.4 and P-3.1. ALL OPTIONAL, for a BFF older than this reader. An absent test stamp
    // is left OUT rather than read as null: the screens gate the switch only on a stamp
    // that is present and null, so an older BFF never locks a working connection off.
    ...(row.purpose === undefined
      ? {}
      : { purpose: oneOf(row, 'purpose', CONNECTION_PURPOSES, where) }),
    ...(row.lastTestedAt === undefined
      ? {}
      : { lastTestedAt: nullableString(row, 'lastTestedAt', where) }),
    ...(row.lastTestOkAt === undefined
      ? {}
      : { lastTestOkAt: nullableString(row, 'lastTestOkAt', where) }),
    legacyChatUse: optionalBool(row, 'legacyChatUse', where),
    // Field report F1.6. OPTIONAL for an older BFF: absent is `false`, as before.
    taskOnly: optionalBool(row, 'taskOnly', where),
    contactDetails:
      row.contactDetails === undefined
        ? []
        : readStringList(row, 'contactDetails', where),
    // P-3.8 and P-3.5, both OPTIONAL for an older BFF. Absent `signInNeeded` is `false`, so the
    // row behaves exactly as it did; absent or null `health` is `null`, which draws NO line at
    // all, never a made-up "not used".
    signInNeeded: optionalBool(row, 'signInNeeded', where),
    health:
      row.health === undefined || row.health === null
        ? null
        : readConnectionHealth(row.health, `${where}.health`),
    // P-2.11: when the sample Test it rehearses with was taken. ABSENT and `null`: none.
    sampleCapturedAt: optionalNullableString(row, 'sampleCapturedAt', where),
  };
};

/** `{ connections: ConnectionSummaryDTO[] }` — `routes/api/tools.ts`. */
const readConnections = payload =>
  listEnvelope(payload, 'connections').map((raw, index) =>
    readConnectionSummary(raw, `connections[${index}]`)
  );

/** `{ connection: ConnectionDetailDTO }` — the editor's read, and every write's answer. */
const readConnection = payload => {
  const row = envelope(payload, 'connection');
  const auth = nested(row, 'auth', 'connection');
  const authType = oneOf(
    auth,
    'type',
    CONNECTION_AUTH_TYPES,
    'connection.auth'
  );
  return {
    ...readConnectionSummary(row, 'connection'),
    url: string(row, 'url', 'connection'),
    params: listEnvelope(row, 'params').map((raw, index) => {
      const where = `connection.params[${index}]`;
      const param = record(raw, where);
      return {
        name: string(param, 'name', where),
        type: oneOf(param, 'type', CONNECTION_PARAM_TYPES, where),
        description: string(param, 'description', where),
        required: bool(param, 'required', where),
        // Absent is a real answer ("any value"), so it is the one optional field here.
        ...(param.choices === undefined
          ? {}
          : {
              choices: listEnvelope(param, 'choices').map((choice, at) => {
                if (typeof choice !== 'string')
                  throw malformed(`${where}.choices[${at}]`);
                return choice;
              }),
            }),
      };
    }),
    // Whatever JSON the owner's own system expects — there is no shape to assert, and
    // `null` means "sends nothing", which is different from "we could not read it".
    body: row.body === undefined ? null : row.body,
    auth:
      authType === 'header'
        ? {
            type: 'header',
            headerName: string(auth, 'headerName', 'connection.auth'),
          }
        : { type: authType },
    keepFields:
      row.keepFields === null
        ? null
        : listEnvelope(row, 'keepFields').map((field, index) => {
            if (typeof field !== 'string') {
              throw malformed(`connection.keepFields[${index}]`);
            }
            return field;
          }),
    extraHeaderCount: number(row, 'extraHeaderCount', 'connection'),
  };
};

/**
 * `{ result: … }` — one dry run.
 *
 * `ok: false` HERE IS A SUCCESSFUL REQUEST. The call was made and the owner's own system
 * refused, timed out or answered with an error; that is the answer the panel exists to
 * show. Only a thrown `WasloApiError` means the test did not happen.
 */
const readTryResult = payload => {
  // P-3.4: a change the owner has not told to send answers with WHAT it would send.
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    payload.preview !== undefined
  ) {
    const preview = nested(payload, 'preview', 'payload');
    return {
      preview: {
        method: string(preview, 'method', 'preview'),
        url: string(preview, 'url', 'preview'),
        headerNames: readStringList(preview, 'headerNames', 'preview'),
        body: preview.body === undefined ? null : preview.body,
      },
    };
  }
  const result = envelope(payload, 'result');
  const ok = bool(result, 'ok', 'result');
  if (ok) {
    return {
      ok: true,
      status: number(result, 'status', 'result'),
      data: result.data,
    };
  }
  return {
    ok: false,
    // Null is a real answer: nothing came back at all, which reads differently from "your
    // system said 500".
    status: result.status === null ? null : number(result, 'status', 'result'),
    error: string(result, 'error', 'result'),
    // A bounded excerpt of what the other system's error body actually said, when it
    // said anything. Absent is a real answer (nothing came back, or nothing readable), so
    // it is left out rather than narrowed to ''.
    ...(result.detail === undefined
      ? {}
      : { detail: string(result, 'detail', 'result') }),
  };
};

// ── the outbound API: the owner's OWN systems sending WhatsApp ──────────────────────
// Three screens (Setup, Message log, Do not contact) over one BFF prefix,
// `/accounts/:accountId/outbound/**` (`services/authoring-web/src/routes/api/outbound.ts`).
// The browser side is camelCase and the sidecar side is snake_case; the BFF is the only
// place that mapping exists, so every name below is a BFF name and never a wire one.

/**
 * The nine statuses a message can be in, in the order the ladder runs.
 *
 * Frozen and exported because the Log's filter, its chips and its detail page all have to
 * agree about the set — three copies of a nine-item list is three chances to be missing
 * the one that matters. Identical to the sidecar's `OUTBOUND_STATUSES`
 * (`src/outbound/types.ts`), which is what the store's CHECK constraint is built from, so
 * a tenth value cannot appear without a migration on the other side.
 *
 * `unconfirmed` is NOT a failure and `suppressed` is not one either: the first means we
 * asked and never heard back, the second means we deliberately did not send. The labels
 * for all nine are in `WASLO.OUTBOUND.STATUS.*` and say so.
 */
export const WASLO_OUTBOUND_STATUSES = Object.freeze([
  'queued',
  'sending',
  'accepted',
  'sent',
  'delivered',
  'read',
  'failed',
  'unconfirmed',
  'suppressed',
]);

/**
 * Why a number is on the Do not contact list.
 *
 * FOUR, NOT THREE. `api` is the one an implementation plan is most likely to forget and
 * the one a real workspace hits first: it is written by the PUBLIC outbound API when the
 * owner's own site posts a number to `/v1/outbound/suppressions`
 * (`src/http/outbound.ts:1083`, which passes the literal `'api'`). A reader that admitted
 * only the other three would throw `malformed` on the commonest row this feature creates
 * and take the whole page down with it.
 */
export const WASLO_SUPPRESSION_REASONS = Object.freeze([
  'manual',
  'keyword',
  'provider',
  'api',
]);

/**
 * The two reasons a workspace may lift on its own.
 *
 * The other two are the customer's own decision — `keyword` is the customer typing STOP,
 * `provider` is WhatsApp's own opt-out — and `DELETE /v1/outbound/suppressions/:phone`
 * answers 403 `opt_out_not_removable` for both (`src/http/outbound-internal.ts:703-710`).
 * The rule lives here so the row can decline to offer a button that would 403, rather
 * than finding out after the press. THE PLAN's "Do not contact" section.
 */
export const WASLO_SUPPRESSION_REMOVABLE_REASONS = Object.freeze([
  'manual',
  'api',
]);

/**
 * Why a message was NOT sent — a different vocabulary from the one above, and conflating
 * the two is the mistake this comment exists to stop.
 *
 * A suppression ROW says who put the number on the list (`manual`/`keyword`/…). A
 * MESSAGE's `suppressedReason` says which rule stopped this one send
 * (`src/outbound/types.ts:124`): the recipient was on the list, test mode was on and they
 * were not one of the test numbers, or the day's per-recipient cap was already spent.
 * They share neither values nor meaning.
 */
export const WASLO_OUTBOUND_SUPPRESSED_REASONS = Object.freeze([
  'opt_out',
  'test_mode',
  'recipient_cap',
]);

/**
 * `?a=1&b=2` from the parts that were actually given, or '' when none were.
 *
 * Undefined, null and '' are DROPPED rather than sent empty: `?status=` is a filter for
 * the empty status on a surface whose filter is "all", and the BFF would be right to
 * reject it. `false` is kept — it is a real answer for `includeRemoved`.
 */
const queryString = params => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

/**
 * `{ settings: OutboundSettingsDTO }` — `routes/api/outbound.ts`, GET and PATCH alike.
 *
 * THREE THINGS THAT MUST NOT BE CONFLATED, and the DTO keeps them apart: what the owner
 * switched (`enabled`, `paused`, `testMode`, `testNumbers`), what is actually in force
 * (`limits`), and which of those limits an operator pinned (`operatorSet`). A limit an
 * operator owns is rendered read-only with "Set by Waslo" beside it — a control that
 * writes nowhere is worse than a label.
 *
 * `operatorSet` carries SEVEN booleans where `limits` carries nine fields, and that is
 * not an omission: `resumeKeywords` and `retentionDays` have no per-workspace override
 * column at all (`settingsDto` in `src/http/outbound-internal.ts`), so there is nothing
 * for an operator to have set.
 */
const readOutboundSettings = payload => {
  const settings = envelope(payload, 'settings');
  const limits = nested(settings, 'limits', 'settings');
  const operatorSet = nested(settings, 'operatorSet', 'settings');
  return {
    enabled: bool(settings, 'enabled', 'settings'),
    paused: bool(settings, 'paused', 'settings'),
    // Both null until someone pauses. Read as nullable rather than defaulted to '': a
    // paused workspace whose banner cannot name who stopped it is the one case this pair
    // exists for.
    pausedAt: nullableString(settings, 'pausedAt', 'settings'),
    pausedBy: nullableString(settings, 'pausedBy', 'settings'),
    testMode: bool(settings, 'testMode', 'settings'),
    testNumbers: stringArray(settings, 'testNumbers', 'settings'),
    limits: {
      ratePerMin: number(limits, 'ratePerMin', 'settings.limits'),
      ratePerDay: number(limits, 'ratePerDay', 'settings.limits'),
      readRatePerDay: number(limits, 'readRatePerDay', 'settings.limits'),
      recipientCapPerDay: number(
        limits,
        'recipientCapPerDay',
        'settings.limits'
      ),
      // Null is "no monthly override", and 0 is an operator deliberately stopping this
      // workspace. `nullableNumber` keeps them apart; a `||` anywhere here would report
      // the platform default while the counters applied the zero.
      monthlyMessagesOverride: nullableNumber(
        limits,
        'monthlyMessagesOverride',
        'settings.limits'
      ),
      allowedCountryPrefixes: nullableStringArray(
        limits,
        'allowedCountryPrefixes',
        'settings.limits'
      ),
      optOutKeywords: stringArray(limits, 'optOutKeywords', 'settings.limits'),
      resumeKeywords: stringArray(limits, 'resumeKeywords', 'settings.limits'),
      // How long the Log keeps a row. The empty Log says this out loud rather than
      // letting an owner conclude her history vanished.
      retentionDays: number(limits, 'retentionDays', 'settings.limits'),
      /**
       * How many test numbers may be saved — the sidecar's own bound, projected through
       * the BFF. Read strictly, and NOT defaulted to a number typed in the editor: a
       * browser that disabled Add at 50 while the server refused past 25 would produce a
       * 400 with nothing on screen to explain it, and a default is indistinguishable from
       * a real answer at exactly the moment it is wrong.
       */
      maxTestNumbers: number(limits, 'maxTestNumbers', 'settings.limits'),
    },
    operatorSet: {
      ratePerMin: bool(operatorSet, 'ratePerMin', 'settings.operatorSet'),
      ratePerDay: bool(operatorSet, 'ratePerDay', 'settings.operatorSet'),
      readRatePerDay: bool(
        operatorSet,
        'readRatePerDay',
        'settings.operatorSet'
      ),
      recipientCapPerDay: bool(
        operatorSet,
        'recipientCapPerDay',
        'settings.operatorSet'
      ),
      monthlyMessagesOverride: bool(
        operatorSet,
        'monthlyMessagesOverride',
        'settings.operatorSet'
      ),
      allowedCountryPrefixes: bool(
        operatorSet,
        'allowedCountryPrefixes',
        'settings.operatorSet'
      ),
      optOutKeywords: bool(
        operatorSet,
        'optOutKeywords',
        'settings.operatorSet'
      ),
    },
    updatedAt: nullableString(settings, 'updatedAt', 'settings'),
  };
};

/**
 * One row of the Log, and one whole detail page — the same shape, because the sidecar
 * serves the same projection to both and inventing a narrower list row here would mean
 * the detail page could show a field the list had already thrown away.
 *
 * `metadata` is deliberately read as `unknown` and not narrowed, exactly like a
 * connection's dry-run body: it is whatever the OWNER's own system attached to the send,
 * so there is no shape to assert. Everything the screens branch on — the status, whether
 * it is finished, why it was not sent — is checked like every other field here.
 */
const readOutboundMessageRow = (raw, where) => {
  const row = record(raw, where);
  return {
    id: string(row, 'id', where),
    status: oneOf(row, 'status', WASLO_OUTBOUND_STATUSES, where),
    // The sidecar's own verdict on whether this can still change, never a set of statuses
    // this client re-derives. A second copy of that rule is how a page ends up polling a
    // finished message forever.
    terminal: bool(row, 'terminal', where),
    providerStatus: nullableString(row, 'providerStatus', where),
    errorCode: nullableString(row, 'errorCode', where),
    error: nullableString(row, 'error', where),
    to: string(row, 'to', where),
    template: string(row, 'template', where),
    language: string(row, 'language', where),
    metadata: row.metadata,
    idempotencyKey: string(row, 'idempotencyKey', where),
    suppressedReason:
      row.suppressedReason === null
        ? null
        : oneOf(
            row,
            'suppressedReason',
            WASLO_OUTBOUND_SUPPRESSED_REASONS,
            where
          ),
    inboxId: number(row, 'inboxId', where),
    // Null until the send actually created (or reused) a conversation. On a `suppressed`
    // or `failed` row it stays null forever, which is a real answer.
    conversationId: nullableNumber(row, 'conversationId', where),
    messageId: nullableNumber(row, 'messageId', where),
    conversationReused: bool(row, 'conversationReused', where),
    attempts: number(row, 'attempts', where),
    createdAt: string(row, 'createdAt', where),
    updatedAt: string(row, 'updatedAt', where),
    enqueuedAt: nullableString(row, 'enqueuedAt', where),
    sentAt: nullableString(row, 'sentAt', where),
    terminalAt: nullableString(row, 'terminalAt', where),
  };
};

/**
 * `{ messages, total, limit, offset, serverTime }` — one page of the Log, newest first.
 *
 * `total` is read strictly rather than defaulted to `messages.length`, for the same
 * reason the Answers pager does: the difference between them is the whole point of the
 * pager, and substituting one for the other tells an owner she is looking at everything
 * she has sent when she is looking at the first fifty.
 *
 * `serverTime` is the clock the relative timestamps are measured against. It comes from
 * the server on purpose — a browser whose clock is a day out would otherwise render every
 * row as "yesterday" and nothing would say why.
 */
const readOutboundMessages = payload => {
  const body = record(payload, 'the response');
  return {
    messages: listEnvelope(body, 'messages').map((raw, index) =>
      readOutboundMessageRow(raw, `messages[${index}]`)
    ),
    total: number(body, 'total', 'the response'),
    limit: number(body, 'limit', 'the response'),
    offset: number(body, 'offset', 'the response'),
    serverTime: string(body, 'serverTime', 'the response'),
  };
};

/** `{ message: OutboundMessageDTO }` — one send, in full. */
const readOutboundMessage = payload =>
  readOutboundMessageRow(envelope(payload, 'message'), 'message');

/** One number on the Do not contact list. */
const readSuppression = (raw, where) => {
  const row = record(raw, where);
  return {
    id: number(row, 'id', where),
    phone: string(row, 'phone', where),
    // NOT re-derived into "removable" here — see `WASLO_SUPPRESSION_REMOVABLE_REASONS`,
    // which is the one place that rule is written down on this side.
    reason: oneOf(row, 'reason', WASLO_SUPPRESSION_REASONS, where),
    createdAt: string(row, 'createdAt', where),
    // Non-null means it WAS on the list and was taken off. The list only carries these
    // when the screen asked for them (`includeRemoved`).
    removedAt: nullableString(row, 'removedAt', where),
    removedBy: nullableString(row, 'removedBy', where),
  };
};

/** `{ numbers, total, limit, offset }` — one page of the Do not contact list. */
const readOutboundNumbers = payload => {
  const body = record(payload, 'the response');
  return {
    numbers: listEnvelope(body, 'numbers').map((raw, index) =>
      readSuppression(raw, `numbers[${index}]`)
    ),
    total: number(body, 'total', 'the response'),
    limit: number(body, 'limit', 'the response'),
    offset: number(body, 'offset', 'the response'),
  };
};

/** `{ number: SuppressionDTO }` — what was actually added, in the form it was stored. */
const readOutboundNumber = payload =>
  readSuppression(envelope(payload, 'number'), 'number');

/**
 * `{ deleted: true }` — the whole of what a removal answers.
 *
 * `true` is asserted rather than merely read: `{ deleted: false }` is not a shape the BFF
 * produces, so seeing one means something upstream changed and the screen must not report
 * a removal that did not happen.
 */
const readOutboundDeleted = payload => {
  const body = record(payload, 'the response');
  if (bool(body, 'deleted', 'the response') !== true) {
    throw malformed('the response.deleted');
  }
  return { deleted: true };
};

/**
 * `{ senders: SenderDTO[] }` — the WhatsApp numbers this workspace can send from.
 *
 * `botRef` is not a field of this type and never will be: it is the path segment of the
 * per-workspace callback URL the platform signs, and this DTO is rendered into a browser.
 */
const readOutboundSenders = payload =>
  listEnvelope(payload, 'senders').map((raw, index) => {
    const where = `senders[${index}]`;
    const sender = record(raw, where);
    return {
      inboxId: number(sender, 'inboxId', where),
      botId: number(sender, 'botId', where),
      aiEnabled: bool(sender, 'aiEnabled', where),
    };
  });

/**
 * One key, as it can be READ BACK — which is to say, without its secret.
 *
 * THE PLAINTEXT IS NOT A FIELD OF THIS TYPE. The store holds a one-way sha256, so no read
 * path can produce it again; it exists only in the mint's answer, for the length of one
 * response (`readOutboundKeySecret`).
 */
const readOutboundKeyRow = (raw, where) => {
  const row = record(raw, where);
  return {
    id: number(row, 'id', where),
    keyPrefix: string(row, 'keyPrefix', where),
    label: nullableString(row, 'label', where),
    scope: string(row, 'scope', where),
    // Null means "any of this workspace's numbers" — a real answer, and the only one a
    // single-number workspace ever produces.
    inboxId: nullableNumber(row, 'inboxId', where),
    createdAt: string(row, 'createdAt', where),
    lastUsedAt: nullableString(row, 'lastUsedAt', where),
    revokedAt: nullableString(row, 'revokedAt', where),
    // WHY THE ROW CARRIES ITS REFUSALS. Last-used is stamped BEFORE the send is judged, so
    // a key whose every call is rejected renders "Last used just now" — a broken
    // integration that looks like a healthy one, which is the one thing this list exists
    // to answer. The count, when, and the server's CODE (never its sentence: the UI writes
    // the words, in the owner's language).
    //
    // All three are read optionally, and only because ABSENCE AND ZERO ARE THE SAME CLAIM
    // here: this image ships independently of the BFF, and a server that does not count
    // refusals yet is a server on which, as far as this page can know, none happened. A
    // wrong TYPE still throws — that is drift between the two repos, not an old build.
    refusalCount: optionalNumber(row, 'refusalCount', where),
    lastRefusedAt: optionalNullableString(row, 'lastRefusedAt', where),
    lastRefusalCode: optionalNullableString(row, 'lastRefusalCode', where),
  };
};

/** `{ keys: OutboundKeyDTO[] }`. */
const readOutboundKeys = payload =>
  listEnvelope(payload, 'keys').map((raw, index) =>
    readOutboundKeyRow(raw, `keys[${index}]`)
  );

/**
 * `{ key: OutboundKeyDTO & { secret } }` — the mint, and the ONE response in this whole
 * client that carries a credential.
 *
 * `secret` is read as a required string precisely because it cannot be fetched again: a
 * mint whose secret failed to arrive has created a key the owner can never use, and
 * saying so here is the only chance to say it. It goes to the reveal panel and nowhere
 * else — never a list, never a toast, never a log line.
 */
const readOutboundKeySecret = payload => {
  const key = envelope(payload, 'key');
  return {
    ...readOutboundKeyRow(key, 'key'),
    secret: string(key, 'secret', 'key'),
  };
};

/** `{ templates: OutboundTemplateDTO[], inboxId }` — what one sender may send. */
const readOutboundTemplates = payload => {
  const body = record(payload, 'the response');
  return {
    // Echoed by the BFF so a list can be routed back to the sender that asked for it:
    // templates are approved per WhatsApp number, so a list shown under the wrong one is
    // not a smaller answer, it is a wrong one.
    inboxId: number(body, 'inboxId', 'the response'),
    templates: listEnvelope(body, 'templates').map((raw, index) => {
      const where = `templates[${index}]`;
      const template = record(raw, where);
      return {
        name: string(template, 'name', where),
        // The EXACT stored casing. Chatwoot matches the language exactly, so an owner who
        // copies a lowercased "ar" out of this page builds a call that dies at send.
        language: string(template, 'language', where),
        status: string(template, 'status', where),
        category: string(template, 'category', where),
        body: string(template, 'body', where),
        variables: stringArray(template, 'variables', where),
        /** P-4.3: absent from a BFF older than D1, which is no buttons. */
        buttons:
          template.buttons === undefined
            ? []
            : listEnvelope(template, 'buttons').map((rawButton, at) => {
                const button = record(rawButton, `${where}.buttons[${at}]`);
                return {
                  index: number(button, 'index', `${where}.buttons[${at}]`),
                  text: string(button, 'text', `${where}.buttons[${at}]`),
                  needsValue: bool(
                    button,
                    'needsValue',
                    `${where}.buttons[${at}]`
                  ),
                };
              }),
      };
    }),
  };
};

/**
 * `{ ok, inboxId, syncRequested, note }` — the answer to "refresh from WhatsApp".
 *
 * `syncRequested` and `note` are the honest part and are read strictly. The refresh is
 * BEST-EFFORT background work upstream (`InboxPolicy#sync_templates?` can refuse the
 * broadcast token outright), so the screen renders what the server said about it rather
 * than implying the next read is current.
 */
const readOutboundTemplateSync = payload => {
  const body = record(payload, 'the response');
  return {
    ok: bool(body, 'ok', 'the response'),
    inboxId: number(body, 'inboxId', 'the response'),
    syncRequested: bool(body, 'syncRequested', 'the response'),
    note: string(body, 'note', 'the response'),
  };
};

/** P-4.2: one place, as `routes/api/places.ts` sends it. */
const readPlace = (raw, where) => {
  const place = record(raw, where);
  return {
    id: number(place, 'id', where),
    nameEn: string(place, 'nameEn', where),
    nameAr: string(place, 'nameAr', where),
    addressEn: string(place, 'addressEn', where),
    addressAr: string(place, 'addressAr', where),
    latitude: number(place, 'latitude', where),
    longitude: number(place, 'longitude', where),
    mapsUrl: string(place, 'mapsUrl', where),
  };
};

const readPlaces = payload =>
  listEnvelope(payload, 'places').map((raw, index) =>
    readPlace(raw, `places[${index}]`)
  );

const readOnePlace = payload => readPlace(envelope(payload, 'place'), 'place');

// ── set up with AI (spec §8 P-8.1 to P-8.3) ─────────────────────────────────────────
// `routes/api/assist-setup.ts`. Read strictly, like every reader here: a draft the screen cannot
// read is a bug in one of two repos and must not render as an empty draft.

const SETUP_SOURCE_KINDS = ['profile', 'website', 'account_name', 'waslo'];
const SETUP_SECTIONS = ['identity', 'business', 'always', 'never'];
const SETUP_TARGETS = ['business', 'answers', 'hours'];
const SETUP_STATUSES = ['open', 'applied', 'dismissed'];
/** E2 adds Claude's four kinds that are not change sets (services `routes/api/assist-setup.ts`). */
const SETUP_KINDS = [
  'personality',
  'change_set',
  'prompt',
  'answers',
  'approved_message',
  'outbound_settings',
];
const CLAUDE_KINDS = [
  'prompt',
  'answers',
  'approved_message',
  'outbound_settings',
];
/** Who proposed it. Absent from a BFF older than E2, which only ever sent Waslo's own. */
const SETUP_SOURCES = [
  'first_draft',
  'describe_task',
  'what_changed',
  'claude',
];
const SETUP_ACTIONS = [
  'answer.add',
  'answer.edit',
  'answer.switch_off',
  'personality.edit_line',
  'task.option_label',
  'task.option_remove',
  'task.switch_off',
  'menu.row_label',
  'menu.row_remove',
  'hours.set',
  'notice.add',
];
const SETUP_PAGES = [
  'answers',
  'personality',
  'tasks',
  'menus',
  'hours',
  'notices',
];

const readSetupSource = (raw, where) => {
  const source = record(raw, where);
  const kind = oneOf(source, 'kind', SETUP_SOURCE_KINDS, where);
  if (kind === 'profile') {
    return {
      kind,
      inboxId: number(source, 'inboxId', where),
      field: string(source, 'field', where),
    };
  }
  if (kind === 'website') {
    return { kind, documentId: number(source, 'documentId', where) };
  }
  return { kind };
};

const readFirstDraft = (raw, where) => {
  const draft = record(raw, where);
  return {
    lang: oneOf(draft, 'lang', ['en', 'ar'], where),
    lines: listEnvelope(draft, 'lines').map((item, i) => {
      const at = `${where}.lines[${i}]`;
      const line = record(item, at);
      return {
        id: string(line, 'id', at),
        section: oneOf(line, 'section', SETUP_SECTIONS, at),
        text: string(line, 'text', at),
        source: readSetupSource(line.source, `${at}.source`),
      };
    }),
    answers: listEnvelope(draft, 'answers').map((item, i) => {
      const at = `${where}.answers[${i}]`;
      const answer = record(item, at);
      return {
        id: string(answer, 'id', at),
        question: string(answer, 'question', at),
        answer: string(answer, 'answer', at),
        source: readSetupSource(answer.source, `${at}.source`),
      };
    }),
    questions: listEnvelope(draft, 'questions').map((item, i) => {
      const at = `${where}.questions[${i}]`;
      const question = record(item, at);
      return {
        id: string(question, 'id', at),
        ask: string(question, 'ask', at),
        target: oneOf(question, 'target', SETUP_TARGETS, at),
      };
    }),
  };
};

const readChangeSet = (raw, where) => {
  const set = record(raw, where);
  return {
    text: string(set, 'text', where),
    items: listEnvelope(set, 'items').map((item, i) => {
      const at = `${where}.items[${i}]`;
      const change = record(item, at);
      return {
        index: number(change, 'index', at),
        action: oneOf(change, 'action', SETUP_ACTIONS, at),
        page: oneOf(change, 'page', SETUP_PAGES, at),
        targetId: string(change, 'targetId', at),
        before: string(change, 'before', at),
        after: string(change, 'after', at),
        question: string(change, 'question', at),
        endsOn: string(change, 'endsOn', at),
        why: string(change, 'why', at),
      };
    }),
  };
};

const readMessageHalf = (raw, where) => {
  const half = record(raw, where);
  return {
    body: string(half, 'body', where),
    footer: string(half, 'footer', where),
    quickReplies: readStringList(half, 'quickReplies', where),
  };
};

/** E2: what Claude proposed in a kind that is not a change set. */
const readClaudeProposal = (raw, kind, where) => {
  const p = record(raw, where);
  if (p.kind !== kind) throw malformed(`${where}.kind`);
  if (kind === 'prompt') {
    return {
      kind,
      text: string(p, 'text', where),
      why: string(p, 'why', where),
    };
  }
  if (kind === 'answers') {
    return {
      kind,
      items: listEnvelope(p, 'items').map((item, i) => {
        const at = `${where}.items[${i}]`;
        const a = record(item, at);
        return {
          index: number(a, 'index', at),
          question: string(a, 'question', at),
          answer: string(a, 'answer', at),
        };
      }),
    };
  }
  if (kind === 'approved_message') {
    const at = `${where}.message`;
    const m = nested(p, 'message', where);
    return {
      kind,
      message: {
        category: oneOf(m, 'category', ['UTILITY', 'MARKETING'], at),
        categoryReason: string(m, 'categoryReason', at),
        name: string(m, 'name', at),
        blanks: listEnvelope(m, 'blanks').map((item, i) => {
          const b = record(item, `${at}.blanks[${i}]`);
          return {
            name: string(b, 'name', at),
            sampleEn: string(b, 'sampleEn', at),
            sampleAr: string(b, 'sampleAr', at),
          };
        }),
        en: readMessageHalf(m.en, `${at}.en`),
        ar: readMessageHalf(m.ar, `${at}.ar`),
      },
    };
  }
  return {
    kind,
    testNumbers: readStringList(p, 'testNumbers', where),
    why: string(p, 'why', where),
  };
};

const readSetupSuggestion = (raw, where) => {
  const s = record(raw, where);
  const kind = oneOf(s, 'kind', SETUP_KINDS, where);
  return {
    id: number(s, 'id', where),
    kind,
    source:
      s.source === undefined ? null : oneOf(s, 'source', SETUP_SOURCES, where),
    batchId: string(s, 'batchId', where),
    status: oneOf(s, 'status', SETUP_STATUSES, where),
    createdAt: string(s, 'createdAt', where),
    draft:
      kind === 'personality' ? readFirstDraft(s.draft, `${where}.draft`) : null,
    changes:
      kind === 'change_set'
        ? readChangeSet(s.changes, `${where}.changes`)
        : null,
    claude: CLAUDE_KINDS.includes(kind)
      ? readClaudeProposal(s.claude, kind, `${where}.claude`)
      : null,
  };
};

/** `{ suggestion, charged }` (first draft) and `{ suggestion }` (read, dismiss). */
const readSetupSuggestionEnvelope = payload => ({
  suggestion: readSetupSuggestion(
    envelope(payload, 'suggestion'),
    'suggestion'
  ),
  charged: optionalNullableNumber(
    record(payload, 'payload'),
    'charged',
    'payload'
  ),
});

/** `{ suggestion | null, dropped, charged }` (what changed). */
const readWhatChanged = payload => {
  const body = record(payload, 'payload');
  return {
    suggestion:
      body.suggestion === null
        ? null
        : readSetupSuggestion(body.suggestion, 'suggestion'),
    dropped: number(body, 'dropped', 'payload'),
    charged: optionalNullableNumber(body, 'charged', 'payload'),
  };
};

const NOTE_KINDS = ['needs_system', 'left_out'];

/** `{ title, trigger, definition, notes, problems, menuOption, approvedMessage, charged }` (describe a task). */
const readDescribedTask = payload => {
  const body = record(payload, 'payload');
  const menu =
    body.menuOption === null ? null : record(body.menuOption, 'menuOption');
  const goal =
    body.approvedMessage === null
      ? null
      : record(body.approvedMessage, 'approvedMessage');
  return {
    title: string(body, 'title', 'payload'),
    trigger: string(body, 'trigger', 'payload'),
    definition: record(body.definition, 'definition'),
    notes: listEnvelope(body, 'notes').map((item, i) => {
      const at = `notes[${i}]`;
      const note = record(item, at);
      return {
        stepId: string(note, 'stepId', at),
        kind: oneOf(note, 'kind', NOTE_KINDS, at),
        detail: string(note, 'detail', at),
      };
    }),
    problems: listEnvelope(body, 'problems').map((item, i) => {
      const at = `problems[${i}]`;
      const problem = record(item, at);
      return {
        stepId: string(problem, 'stepId', at),
        problem: string(problem, 'problem', at),
        ...(problem.detail === undefined
          ? {}
          : { detail: string(problem, 'detail', at) }),
      };
    }),
    menuOption:
      menu === null
        ? null
        : {
            titleEn: string(menu, 'titleEn', 'menuOption'),
            titleAr: string(menu, 'titleAr', 'menuOption'),
          },
    approvedMessage:
      goal === null ? null : { goal: string(goal, 'goal', 'approvedMessage') },
    charged: optionalNullableNumber(body, 'charged', 'payload'),
  };
};

/** `{ applied, batchId, touched, charged }` (Save these, Apply). */
const readApplied = payload => {
  const body = record(payload, 'payload');
  const touched = nested(body, 'touched', 'payload');
  return {
    applied: bool(body, 'applied', 'payload'),
    batchId: string(body, 'batchId', 'payload'),
    touched: {
      personality: bool(touched, 'personality', 'touched'),
      tasks: bool(touched, 'tasks', 'touched'),
      menus: bool(touched, 'menus', 'touched'),
      answers: number(touched, 'answers', 'touched'),
    },
    charged: optionalNullableNumber(body, 'charged', 'payload'),
  };
};

// ── the Claude connector (services spec §8 P-8.4, `routes/api/assistant.ts`) ─────────

const readConnectRequest = payload => {
  const r = envelope(payload, 'request');
  return {
    clientName: string(r, 'clientName', 'request'),
    redirectHost: string(r, 'redirectHost', 'request'),
  };
};

/** Where to send the owner back to Claude. Only ever an http(s) address the BFF built. */
const readRedirect = payload => {
  const url = string(record(payload, 'payload'), 'redirect', 'payload');
  if (!/^https?:\/\//.test(url)) throw malformed('payload.redirect');
  return url;
};

const readGrant = (raw, where) => {
  const g = record(raw, where);
  return {
    id: number(g, 'id', where),
    clientName: string(g, 'clientName', where),
    connectedBy: string(g, 'connectedBy', where),
    connectedAt: string(g, 'connectedAt', where),
    lastUsedAt: nullableString(g, 'lastUsedAt', where),
  };
};

const readAssistantSummary = payload => {
  const body = record(payload, 'payload');
  const drafted = nested(body, 'drafted', 'payload');
  return {
    batches: listEnvelope(body, 'batches').map((raw, i) => {
      const at = `batches[${i}]`;
      const b = record(raw, at);
      return {
        batchId: string(b, 'batchId', at),
        suggestions: number(b, 'suggestions', at),
        drafted: number(b, 'drafted', at),
        lastAt: string(b, 'lastAt', at),
      };
    }),
    drafted: {
      tasks: numberArray(drafted, 'tasks', 'drafted'),
      menus: numberArray(drafted, 'menus', 'drafted'),
      connections: numberArray(drafted, 'connections', 'drafted'),
    },
  };
};

const readUndoAll = payload => {
  const b = record(payload, 'payload');
  return {
    undone: number(b, 'undone', 'payload'),
    skipped: number(b, 'skipped', 'payload'),
    dismissed: number(b, 'dismissed', 'payload'),
  };
};

const readKept = payload => {
  const b = record(payload, 'payload');
  return {
    kept: number(b, 'kept', 'payload'),
    remaining: number(b, 'remaining', 'payload'),
    batchId: string(b, 'batchId', 'payload'),
    charged: optionalNullableNumber(b, 'charged', 'payload'),
    replaced: number(b, 'replaced', 'payload'),
    conflicts: number(b, 'conflicts', 'payload'),
  };
};

const readDraftsRemoved = payload => {
  const b = record(payload, 'payload');
  return {
    removed: number(b, 'removed', 'payload'),
    remaining: number(b, 'remaining', 'payload'),
  };
};

/** `{ undone: { count, skipped } }` (`routes/api/revisions.ts`). */
const readBatchUndone = payload => {
  const undone = envelope(payload, 'undone');
  return {
    count: number(undone, 'count', 'undone'),
    skipped: number(undone, 'skipped', 'undone'),
  };
};

export const wasloApi = {
  getConfig: async (accountId, signal) =>
    readConfig(await wasloRequest(accountId, '/config', { signal })),

  /**
   * A genuine partial update: send only what changed, render the RESPONSE. The saved
   * state is echoed in full, so a value the sidecar normalised never diverges on screen.
   *
   * @param {{ systemPrompt?: string, temperature?: number,
   *           handoffMessage?: string|null, handoffMessageAr?: string|null,
   *           aiEnabled?: boolean, customerLanguages?: 'ar'|'en'|'both',
   *           arabicFormal?: boolean, speaksAs?: 'she'|'he'|null }} patch
   */
  updateConfig: async (accountId, patch, signal) =>
    readConfig(
      await wasloRequest(accountId, '/config', {
        method: 'PATCH',
        body: patch,
        signal,
      })
    ),

  getCredits: async (accountId, signal) =>
    readCredits(await wasloRequest(accountId, '/credits', { signal })),

  /**
   * How much of each spending limit is gone, and which one — if any — has stopped live
   * replies. Percentages only; no amount of money crosses this boundary.
   *
   * Every caller treats a failure here as "say nothing", never as "nothing is wrong": a
   * usage line the page cannot read is a line it does not draw.
   */
  getLimits: async (accountId, signal) =>
    readLimits(await wasloRequest(accountId, '/limits', { signal })),

  /**
   * Today, the last seven days, when the assistant last said anything, and what it got
   * stuck on. Takes no range on purpose — an Overview you configure is a report.
   */
  getActivity: async (accountId, signal) =>
    readActivity(await wasloRequest(accountId, '/activity', { signal })),

  /** Every knowledge source still being built, and every one that failed. */
  getJobs: async (accountId, signal) =>
    readJobs(await wasloRequest(accountId, '/jobs', { signal })),

  /**
   * Dismiss a source that could not be read, so it leaves "Could not be read" and the
   * Overview queue. Only a failed job can be dismissed — the BFF answers `conflict` for
   * any other — and one that is already gone answers as dismissed.
   */
  dismissJob: async (accountId, jobId, signal) =>
    readDismissed(
      await wasloRequest(
        accountId,
        `/jobs/${encodeURIComponent(String(jobId))}`,
        { method: 'DELETE', signal }
      )
    ),

  /** The WhatsApp channels on this workspace, and whether a bot is attached to each. */
  getInboxes: async (accountId, signal) =>
    readInboxes(await wasloRequest(accountId, '/inboxes', { signal })),

  /**
   * Ask US to put an assistant on one of this workspace's numbers.
   *
   * THE OWNER CANNOT DO THIS HERSELF and the screen must stop pretending she can: the
   * queue's row linked to a settings page that redirects straight back to the Overview,
   * so the button did nothing at all. Attaching a bot to a number is one click on the
   * operator side, so the row sends the ask there instead.
   *
   * NO BODY. Who is asking is the session's answer (the BFF reads it off the verified
   * dashboard cookie); a requester named by the browser would be a field worth forging.
   */
  requestInboxConnect: async (accountId, inboxId, signal) =>
    readConnectRequested(
      await wasloRequest(
        accountId,
        `/inboxes/${encodeURIComponent(String(inboxId))}/connect-request`,
        { method: 'POST', signal }
      )
    ),

  /**
   * ONE TRUTHFUL STATUS (P-5.1, P-5.2, P-5.9): the account's state and its one fix, every
   * channel's, the practice readiness, the test numbers and the Needs-you rows. Every screen
   * that talks about the assistant reads this and nothing else.
   */
  getStatus: async (accountId, signal) =>
    readStatus(await wasloRequest(accountId, '/status', { signal })),

  /** P-5.2: the numbers a practising assistant answers. The whole list, every time. */
  updateTesters: async (accountId, phones, signal) =>
    readStatus(
      await wasloRequest(accountId, '/status/testers', {
        method: 'PUT',
        body: { phones },
        signal,
      })
    ),

  /**
   * P-5.2: "Start answering customers". NO BODY: who went live is the verified dashboard
   * user, read by the BFF off the session. The Claude connector has no route to this.
   */
  goLive: async (accountId, signal) =>
    readStatus(
      await wasloRequest(accountId, '/status/go-live', {
        method: 'POST',
        signal,
      })
    ),

  /** P-5.2: back to answering only the test numbers. */
  backToPractice: async (accountId, signal) =>
    readStatus(
      await wasloRequest(accountId, '/status/practice', {
        method: 'POST',
        signal,
      })
    ),

  /**
   * "Ask us to finish setup" (P-5.5) and the status fixes that ask us (P-5.1). `what` is
   * `assistant`, `broadcasts` or `limit_raise`; the answer is read exactly like
   * `requestInboxConnect`'s, because to her it is the same thing: we have it.
   */
  requestSetup: async (accountId, what, signal) =>
    readConnectRequested(
      await wasloRequest(accountId, '/setup-request', {
        method: 'POST',
        body: { what },
        signal,
      })
    ),

  /**
   * P-5.3: "Request top-up". NO BODY: who asked is the verified dashboard user, read by the BFF
   * off the session. The answer says whether we were already told (an alert or an earlier press
   * since the last top-up), which the button then says instead of sending again.
   */
  requestTopUp: async (accountId, signal) =>
    readTopUpRequested(
      await wasloRequest(accountId, '/credits/top-up-request', {
        method: 'POST',
        signal,
      })
    ),

  getCreditPurchase: async (accountId, signal) =>
    readCreditPurchase(
      await wasloRequest(accountId, '/credits/packs', { signal })
    ),
  startCreditCheckout: async (accountId, packId, method, signal) =>
    readCreditCheckout(
      await wasloRequest(accountId, '/credits/checkout', {
        method: 'POST',
        body: { packId, method },
        signal,
      })
    ),
  getCreditOrder: async (accountId, orderId, signal) =>
    readCreditOrder(
      await wasloRequest(
        accountId,
        `/credits/orders/${encodeURIComponent(orderId)}`,
        { signal }
      )
    ),

  /** P-5.4: the last 7 whole days, counted in the account's own timezone, dates included. */
  getWeek: async (accountId, signal) =>
    readWeek(await wasloRequest(accountId, '/week', { signal })),

  /** P-5.4: the signed-in admin's own weekly-email setting, and the address it goes to. */
  getWeekEmail: async (accountId, signal) =>
    readWeekEmail(await wasloRequest(accountId, '/week/email', { signal })),

  /**
   * Hers only; the address is the session's, never sent from here. `locale` is the language
   * this screen is in, which the email is written in.
   *
   * @param {{ enabled: boolean, weekday: number, locale: 'en'|'ar' }} choice
   */
  updateWeekEmail: async (accountId, { enabled, weekday, locale }, signal) =>
    readWeekEmail(
      await wasloRequest(accountId, '/week/email', {
        method: 'PUT',
        body: { enabled, weekday, locale },
        signal,
      })
    ),

  /**
   * Preview-then-accept: suggests, saves nothing. `kind` is echoed by the BFF so two
   * in-flight suggestions can be routed back to the field that asked.
   *
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   *
   * @param {{ kind: 'persona'|'scenario_trigger', text: string }} input
   * @returns {Promise<{ kind: string, text: string, charged: number|null }>}
   */
  optimize: async (accountId, input, signal, confirm = {}) =>
    readSuggestion(
      await wasloRequest(accountId, '/assist/optimize', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /**
   * An approved message written from a goal (P-4.5): English and Arabic, named blanks, samples,
   * quick replies and a suggested category. It SUGGESTS: nothing is saved and nothing reaches
   * WhatsApp; the editor fills its own form and the owner sends it.
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   *
   * @param {{ goal: string, description: string }} input
   */
  draftTemplate: async (accountId, input, signal, confirm = {}) =>
    readTemplateDraft(
      await wasloRequest(accountId, '/assist/draft-template', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** WhatsApp's unseen verdicts on this account's approved messages (P-4.6). */
  getTemplateEvents: async (accountId, signal) =>
    readTemplateEvents(
      await wasloRequest(accountId, '/templates/events', { signal })
    ),

  /** Opening a verdict's row marks it seen, so the Overview stops showing it. */
  markTemplateEventSeen: async (accountId, id) => {
    await wasloRequest(
      accountId,
      `/templates/events/${encodeURIComponent(String(id))}/seen`,
      { method: 'POST' }
    );
  },

  /** P-4.2: the business's places, for a Tell that sends a pin. */
  listPlaces: async (accountId, signal) =>
    readPlaces(await wasloRequest(accountId, '/places', { signal })),

  /** @param {{ nameEn, nameAr, addressEn, addressAr, where }} place `where`: the pasted link or the typed point. */
  addPlace: async (accountId, place) =>
    readOnePlace(
      await wasloRequest(accountId, '/places', { method: 'POST', body: place })
    ),

  updatePlace: async (accountId, id, place) =>
    readOnePlace(
      await wasloRequest(
        accountId,
        `/places/${encodeURIComponent(String(id))}`,
        {
          method: 'PUT',
          body: place,
        }
      )
    ),

  /** Refused with `PLACE_IN_USE` (the tasks on `error.tasks`) while a task sends the place. */
  removePlace: async (accountId, id) => {
    await wasloRequest(accountId, `/places/${encodeURIComponent(String(id))}`, {
      method: 'DELETE',
    });
  },

  /**
   * Propose one field's words in the other language (P-7.1). Suggests, saves nothing. `from`
   * and `to` are the field the words are stored in and the one that is empty, never a guess.
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   *
   * @param {{ kind: string, from: 'en'|'ar', to: 'en'|'ar', text: string }} input
   * @returns {Promise<{ kind: string, from: string, to: string, text: string,
   *                     charged: number|null }>}
   */
  translate: async (accountId, input, signal, confirm = {}) =>
    readTranslation(
      await wasloRequest(accountId, '/assist/translate', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /**
   * Up to 20 fields in one call, one charge (P-7.1 "Translate them for me"). Each item comes
   * back with its own outcome; only `ok` carries words.
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   *
   * @param {{ from: 'en'|'ar', to: 'en'|'ar',
   *           items: Array<{ kind: string, text: string }> }} input
   */
  translateBatch: async (accountId, input, signal, confirm = {}) =>
    readTranslations(
      await wasloRequest(accountId, '/assist/translate', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /**
   * One rehearsal message on the Test it screen.
   *
   * `sessionId` is generated by the screen and is the only thing tying the messages into
   * a conversation — the thread itself lives in the sidecar's memory, not here and not in
   * this service, so a reload is a clean chat by construction rather than by a delete
   * call somebody has to remember to make.
   *
   * `liveActions` is sent EXPLICITLY on every message, never omitted-to-mean-false. The
   * BFF defaults it to false and would be right to, but the value is the difference
   * between pretending to place an order and placing one, and a field that is sometimes
   * absent is a field that eventually gets read from the wrong variable.
   *
   * @param {{ sessionId: string, message: string, liveActions: boolean,
   *           contact?: { name?: string, email?: string, phone?: string,
   *                       attributes?: Record<string, string> },
   *           instructionsOverride?: { instructions?: string, style?: number } }} input
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   */
  sendTestMessage: async (accountId, input, signal, confirm = {}) =>
    readTestReply(
      await wasloRequest(accountId, '/test/messages', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /**
   * Up to three opening messages for the Test it screen, taken from THIS account's own
   * content rather than from a worked example: what its customers asked and nobody
   * answered, and what it has already written an answer for.
   *
   * An empty list is the ordinary answer for a new account, and a failure here is not
   * news — the screen tops the list up with its own neutral lines either way, so it never
   * opens on nothing.
   */
  getTestStarters: async (accountId, signal) =>
    readTestStarters(
      await wasloRequest(accountId, '/test/starters', { signal })
    ),

  /**
   * The Activity screen, in one read: the numbers, the day-by-day series they are summed
   * from, the questions with no answer, and the conversations behind them.
   *
   * `range` is one of `WASLO_ACTIVITY_RANGES`. It is NOT defaulted here — the screen owns
   * that choice (it is in the URL), and a client-side default would quietly disagree with
   * the BFF's the first time one of the two moved.
   *
   * @param {number|string} accountId
   * @param {7|30|90} range
   */
  getActivityReport: async (accountId, range, signal) =>
    readReport(
      await wasloRequest(
        accountId,
        `/report?range=${encodeURIComponent(String(range))}`,
        { signal }
      )
    ),

  /**
   * One conversation, as the customer saw it. Fetched only when a row is opened, so the
   * list costs one request however long it is.
   */
  getConversationTranscript: async (accountId, conversationId, signal) =>
    readTranscript(
      await wasloRequest(
        accountId,
        `/report/conversations/${encodeURIComponent(String(conversationId))}`,
        { signal }
      )
    ),

  // ── the knowledge library ─────────────────────────────────────────────────────────
  /** Everything the assistant has been given to read, newest first. */
  getDocuments: async (accountId, signal) =>
    readDocuments(await wasloRequest(accountId, '/documents', { signal })),

  /** One source: its name and the text exactly as it was read. */
  getDocument: async (accountId, documentId, signal) =>
    readDocument(
      await wasloRequest(
        accountId,
        `/documents/${encodeURIComponent(String(documentId))}`,
        { signal }
      )
    ),

  /**
   * What the assistant actually learned from one source. The retriever searches these
   * pairs and not the original text, so this is the only place a wrong answer can be
   * diagnosed.
   */
  getDocumentAnswers: async (accountId, documentId, signal) =>
    readDocumentAnswers(
      await wasloRequest(
        accountId,
        `/documents/${encodeURIComponent(String(documentId))}/answers`,
        { signal }
      )
    ),

  /**
   * Queue a source the owner typed or an address for us to read. Answers with the job to
   * watch — NOT with a source, because none exists yet.
   *
   * @param {{ type: 'text', title: string, content: string }
   *        |{ type: 'url', title: string, url: string }} input
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   */
  addDocument: async (accountId, input, signal, confirm = {}) =>
    readJobRef(
      await wasloRequest(accountId, '/documents', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /**
   * The same thing for a file. `FormData` rather than JSON — see `wasloRequest`, which
   * leaves the `Content-Type` to the browser so the multipart boundary is real.
   *
   * @param {{ title: string, file: File }} input
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   */
  uploadDocumentPdf: async (accountId, input, signal, confirm = {}) => {
    const form = new FormData();
    form.append('title', input.title);
    form.append('file', input.file);
    return readJobRef(
      await wasloRequest(accountId, '/documents/pdf', {
        method: 'POST',
        body: form,
        signal,
        ...confirm,
      })
    );
  },

  /** Remove a source. It waits in Recently removed for 30 days (P-6.11). */
  deleteDocument: async (accountId, documentId, signal) =>
    readDeleted(
      await wasloRequest(
        accountId,
        `/documents/${encodeURIComponent(String(documentId))}`,
        { method: 'DELETE', signal }
      )
    ),

  // ── undo: earlier versions, and what was removed (P-6.11) ─────────────────────────
  /**
   * One thing's saves, newest first. `kind` is `personality` (no id), `task`, `menu`,
   * `connection` or `answer`.
   */
  getRevisions: async (accountId, kind, itemId, signal) => {
    const params = new URLSearchParams({ kind });
    if (itemId !== null && itemId !== undefined) {
      params.set('id', String(itemId));
    }
    return readRevisions(
      await wasloRequest(accountId, `/revisions?${params.toString()}`, {
        signal,
      })
    );
  },

  /** Bring back how it was just before that save. The restore is itself a save. */
  restoreRevision: async (accountId, revisionId, signal) =>
    readRestored(
      await wasloRequest(
        accountId,
        `/revisions/${encodeURIComponent(String(revisionId))}/restore`,
        { method: 'POST', signal }
      )
    ),

  /** What was removed in the last 30 days, newest first. */
  getRemoved: async (accountId, signal) =>
    // `with=notice`: this build knows "For now" notices (P-6.9); an older BFF ignores it.
    readRemoved(
      await wasloRequest(accountId, '/removed?with=notice', { signal })
    ),

  /** Put one back. No AI: what it learned comes back with it. */
  putBack: async (accountId, kind, itemId, signal) =>
    readRestored(
      await wasloRequest(
        accountId,
        `/removed/${encodeURIComponent(kind)}/${encodeURIComponent(String(itemId))}/restore`,
        { method: 'POST', signal }
      )
    ),

  // ── tasks: the things the assistant can do, step by step ──────────────────────────
  /** Every task on this workspace, on or off. */
  getTasks: async (accountId, signal) =>
    readTasks(await wasloRequest(accountId, '/tasks', { signal })),

  /** One task: its steps, what it writes down, and the connections attached to it. */
  getTask: async (accountId, taskId, signal) =>
    readTask(
      await wasloRequest(
        accountId,
        `/tasks/${encodeURIComponent(String(taskId))}`,
        { signal }
      )
    ),

  /** P-10.21: the Overview's "switched off" row and the Connections switch-off confirm. */
  getConnectionUse: async (accountId, signal) =>
    readConnectionUse(
      await wasloRequest(accountId, '/tasks/connection-use', { signal })
    ),

  /**
   * The connections a task may be given. Served under `/tasks/` deliberately — this is the
   * choice set for a task, and an editor that cannot open because a sibling screen's route
   * moved is a coupling with no upside (`routes/api/scenarios.ts` says the same thing from
   * the other side).
   */
  getTaskConnections: async (accountId, signal) =>
    readTaskConnections(
      await wasloRequest(accountId, '/tasks/connections', { signal })
    ),

  /**
   * Create. The BFF invents and owns the machine identifier the engine matches on, so
   * nothing here sends one and nothing here can read one back.
   *
   * @param {{ title: string, trigger: string, steps: string[],
   *           collect?: {label: string, required: boolean}[],
   *           connections?: string[], enabled?: boolean,
   *           definition?: object|null }} input
   */
  createTask: async (accountId, input, signal) =>
    readTask(
      await wasloRequest(accountId, '/tasks', {
        method: 'POST',
        body: input,
        signal,
      })
    ),

  /**
   * A genuine partial update: send only what changed, render the RESPONSE. That is what
   * lets the list screen's switch send `{ enabled: false }` on its own without carrying a
   * draft somebody else is editing along with it.
   *
   * `definition` obeys that rule and gains a third state from it. ABSENT means "leave the
   * stored graph alone" (`GRAPH-API-CONTRACT.md` §1), which is what makes the switch on the
   * list screen safe; `null` means "this task has no graph"; an object replaces it. So the
   * one thing this must never do is send `definition: null` because it had nothing to
   * say — that would wipe a graph from a screen that was only renaming a task.
   *
   * It can be refused with a 400 carrying `problems`, which is on the thrown
   * `WasloApiError` as `problems` and belongs on the step cards it names.
   */
  updateTask: async (accountId, taskId, patch, signal) =>
    readTask(
      await wasloRequest(
        accountId,
        `/tasks/${encodeURIComponent(String(taskId))}`,
        { method: 'PATCH', body: patch, signal }
      )
    ),

  /**
   * P-2.11: whether THIS version of a task (the draft as compiled, saved or not) has been tried
   * in Test it, and when. `{ rehearsedAt: string|null, turns: number }`. Never charged.
   */
  getRehearsalStatus: async (accountId, definition, signal) => {
    const body = record(
      await wasloRequest(accountId, '/tasks/rehearsal-status', {
        method: 'POST',
        body: { definition },
        signal,
      }),
      'rehearsal-status'
    );
    return {
      rehearsedAt: nullableString(body, 'rehearsedAt', 'rehearsal-status'),
      turns: number(body, 'turns', 'rehearsal-status'),
    };
  },

  /**
   * P-4.10: the Arrange page's preview of an unsaved draft, in one language. A pure compile on
   * the server: never an AI call, never charged, nothing saved.
   */
  formPreview: async (accountId, definition, lang, signal) =>
    readFormPreview(
      await wasloRequest(accountId, '/tasks/form-preview', {
        method: 'POST',
        body: { definition, lang },
        signal,
      })
    ),

  /** Remove a task. Answers 204 with no body — there is nothing left to read. */
  deleteTask: async (accountId, taskId, signal) => {
    await wasloRequest(
      accountId,
      `/tasks/${encodeURIComponent(String(taskId))}`,
      { method: 'DELETE', signal }
    );
  },

  // ── menus: tappable options under a reply ──────────────────────────────────────────
  /** Every menu on this workspace, on or off. */
  getMenus: async (accountId, signal) =>
    readMenus(await wasloRequest(accountId, '/menus', { signal })),

  /** One menu, with its options in order. */
  getMenu: async (accountId, menuId, signal) =>
    readMenu(
      await wasloRequest(
        accountId,
        `/menus/${encodeURIComponent(String(menuId))}`,
        { signal }
      )
    ),

  /** The tasks an option may start — served under `/menus/`, like a task's connections. */
  getMenuTasks: async (accountId, signal) =>
    readMenuTasks(await wasloRequest(accountId, '/menus/tasks', { signal })),

  /**
   * Create. The BFF invents the machine name and mints option ids; nothing here sends or
   * reads either.
   *
   * @param {{ title: string, whenToShow: string, enabled?: boolean,
   *           bodyEn?: string, bodyAr?: string, buttonEn?: string, buttonAr?: string,
   *           options: { titleEn: string, titleAr: string, taskId: number|null }[] }} input
   */
  createMenu: async (accountId, input, signal) =>
    readMenu(
      await wasloRequest(accountId, '/menus', {
        method: 'POST',
        body: input,
        signal,
      })
    ),

  /** A genuine partial update — the list's switch sends `{ enabled }` alone. */
  updateMenu: async (accountId, menuId, patch, signal) =>
    readMenu(
      await wasloRequest(
        accountId,
        `/menus/${encodeURIComponent(String(menuId))}`,
        { method: 'PATCH', body: patch, signal }
      )
    ),

  /** Remove a menu. Answers 204 with no body. */
  deleteMenu: async (accountId, menuId, signal) => {
    await wasloRequest(
      accountId,
      `/menus/${encodeURIComponent(String(menuId))}`,
      { method: 'DELETE', signal }
    );
  },

  // ── permissions: what it may do to a chat, beyond replying ────────────────────────
  /** All four, on or off, with what each one is allowed to pick from. */
  getPermissions: async (accountId, signal) =>
    readPermissions(await wasloRequest(accountId, '/actions', { signal })),

  /**
   * Everything in this workspace a permission could be allowed to pick — read straight
   * from Chatwoot with the caller's own session, which is why it is the one read on this
   * screen that can fail on its own while everything else is fine.
   */
  getPermissionTargets: async (accountId, signal) =>
    readTargets(await wasloRequest(accountId, '/actions/targets', { signal })),

  /**
   * A genuine partial update, and the partiality IS the safety: a patch with no `chosen`
   * cannot clear what was picked, so a save from a card whose chooser failed to load is a
   * no-op for the list rather than a silent wipe. Send `chosen` only when the chooser
   * really rendered.
   *
   * @param {{ enabled?: boolean, guidance?: string,
   *           chosen?: { teams?: {id: number, name: string}[],
   *                      agents?: {id: number, name: string}[],
   *                      labels?: string[],
   *                      contactFields?: {key: string, label: string, type: string,
   *                                       values?: string[], description?: string}[],
   *                      contactLabels?: string[] } }} patch
   */
  updatePermission: async (accountId, key, patch, signal) =>
    readPermission(
      await wasloRequest(
        accountId,
        `/actions/${encodeURIComponent(String(key))}`,
        { method: 'PATCH', body: patch, signal }
      )
    ),

  // ── connections: the systems it may reach into mid-chat ───────────────────────────
  /** Every connection on this workspace, on or off. */
  getConnections: async (accountId, signal) =>
    readConnections(await wasloRequest(accountId, '/connections', { signal })),

  /** One connection, in full — minus the secret, which nothing can read back. */
  getConnection: async (accountId, connectionId, signal) =>
    readConnection(
      await wasloRequest(
        accountId,
        `/connections/${encodeURIComponent(String(connectionId))}`,
        { signal }
      )
    ),

  /**
   * Create. `name` is accepted HERE and nowhere else: the BFF slugifies it, stores it, and
   * refuses to change it afterwards, because a task attaches to a connection BY name and a
   * rename would detach every one of them.
   */
  createConnection: async (accountId, input, signal) =>
    readConnection(
      await wasloRequest(accountId, '/connections', {
        method: 'POST',
        body: input,
        signal,
      })
    ),

  /** A genuine partial update. `name` is not a field a patch may carry. */
  updateConnection: async (accountId, connectionId, patch, signal) =>
    readConnection(
      await wasloRequest(
        accountId,
        `/connections/${encodeURIComponent(String(connectionId))}`,
        { method: 'PATCH', body: patch, signal }
      )
    ),

  /**
   * P-2.11: "Forget the sample". The answer its last good test got is dropped, so Test it says
   * there is none until the next test. Answers 204.
   */
  forgetConnectionSample: async (accountId, connectionId, signal) => {
    await wasloRequest(
      accountId,
      `/connections/${encodeURIComponent(String(connectionId))}/sample`,
      { method: 'DELETE', signal }
    );
  },

  /** Remove a connection. Answers 204 — there is nothing left to read. */
  deleteConnection: async (accountId, connectionId, signal) => {
    await wasloRequest(
      accountId,
      `/connections/${encodeURIComponent(String(connectionId))}`,
      { method: 'DELETE', signal }
    );
  },

  /**
   * One test against the owner's own system. `values` are raw strings — the sidecar owns
   * turning them into numbers and yes/no, and duplicating that here would only let the two
   * disagree about what "0" means. A connection that CHANGES something answers
   * `{ preview }` unless `options.send` is true (P-3.4).
   *
   * @param {Record<string, string>} values
   * @param {{ send?: boolean, sample?: Record<string, string> }} [options] `sample`: the
   *   "Sample customer" values for the details Waslo fills in (P-3.7), by reserved name.
   */
  tryConnection: async (
    accountId,
    connectionId,
    values,
    options = {},
    signal = undefined
  ) =>
    readTryResult(
      await wasloRequest(
        accountId,
        `/connections/${encodeURIComponent(String(connectionId))}/try`,
        {
          method: 'POST',
          body: {
            values,
            ...(options.send === true ? { send: true } : {}),
            // P-3.7: the "Sample customer" boxes. Left out when none was typed, so the body is
            // byte-identical to before for every connection that sends no customer detail.
            ...(options.sample && Object.keys(options.sample).length > 0
              ? { sample: options.sample }
              : {}),
          },
          signal,
        }
      )
    ),

  // ── the conversation pane ─────────────────────────────────────────────────────────
  /**
   * What the assistant did in ONE conversation: whether it is answering at all, how many
   * turns it handled here, and its most recent reply with what it searched for.
   *
   * One request for the whole pane. The pane opens on every conversation an agent clicks,
   * so two round-trips here is two round-trips per click, all day.
   */
  getConversationAssistant: async (accountId, conversationId, signal) =>
    readConversationAssistant(
      await wasloRequest(
        accountId,
        `/conversations/${encodeURIComponent(String(conversationId))}/assistant`,
        { signal }
      )
    ),

  /**
   * Teach one answer. It is live on the next customer message — nothing is queued and
   * nothing is generated from it.
   *
   * `conversationId` is sent for the record and is deliberately NOT what scopes the write:
   * the account comes from the session and the destination is the service's own reserved
   * source, so a caller cannot aim a taught answer at somebody else's upload.
   *
   * @param {{ question: string, answer: string, conversationId?: number }} input
   *
   * The fourth argument is `withConfirm(credits)` (P-5.12).
   */
  teachAnswer: async (accountId, input, signal, confirm = {}) =>
    readTaughtAnswer(
      await wasloRequest(accountId, '/knowledge/answers', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** P-1.6 to P-1.8: the chat panel, one read for administrators and staff (member route). */
  getChatPanel: async (accountId, conversationId, signal) =>
    readChatPanel(
      await wasloRequest(
        accountId,
        `/conversations/${encodeURIComponent(String(conversationId))}/panel`,
        { signal, scope: 'member' }
      )
    ),

  /** P-1.7: "Suggest this answer to the owner". Free: nothing is taught until she accepts. */
  suggestAnswer: async (accountId, conversationId, input, signal) =>
    readStaffSuggestion(
      await wasloRequest(
        accountId,
        `/conversations/${encodeURIComponent(String(conversationId))}/suggestions`,
        { method: 'POST', body: input, signal, scope: 'member' }
      )
    ),

  /** P-1.7: what the team suggested and is waiting on her. */
  getTeamSuggestions: async (accountId, signal) =>
    readTeamSuggestions(
      await wasloRequest(accountId, '/knowledge/suggestions?status=pending', {
        signal,
      })
    ),

  /**
   * Accept is a charged teach (P-5.12): the fifth argument is `withConfirm(credits)`. `edits` is
   * `{}` for "as written", or both `question` and `answer`.
   */
  acceptTeamSuggestion: async (accountId, id, edits, signal, confirm = {}) =>
    readAcceptedSuggestion(
      await wasloRequest(
        accountId,
        `/knowledge/suggestions/${encodeURIComponent(String(id))}/accept`,
        { method: 'POST', body: edits, signal, ...confirm }
      )
    ),

  dismissTeamSuggestion: async (accountId, id, signal) =>
    readTeamSuggestionRow(
      envelope(
        await wasloRequest(
          accountId,
          `/knowledge/suggestions/${encodeURIComponent(String(id))}/dismiss`,
          { method: 'POST', signal }
        ),
        'suggestion'
      ),
      'suggestion'
    ),

  // ── knowledge the owner can fix (ease C3, spec §6) ──────────────────────────────────
  /**
   * "All answers" (P-6.1): a page of 25. A words search is free; `by: 'meaning'` is priced,
   * so the fifth argument is `withConfirm(credits)` for it.
   *
   * @param {{ q?: string, by?: 'words'|'meaning', filter?: 'all'|'yours'|'sources'|'off',
   *           documentId?: number, page?: number }} query
   */
  getAnswers: async (accountId, query, signal, confirm = {}) => {
    const { q, by, filter, documentId, page } = query ?? {};
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (by) params.set('by', by);
    if (filter) params.set('filter', filter);
    if (documentId) params.set('documentId', String(documentId));
    if (page && page > 1) params.set('page', String(page));
    const search = params.toString();
    return readAnswerPage(
      await wasloRequest(accountId, `/answers${search ? `?${search}` : ''}`, {
        signal,
        ...confirm,
      })
    );
  },

  /** Edit, switch off or on (P-6.1). A new question is priced: `withConfirm(credits)`. */
  updateAnswer: async (accountId, id, patch, signal, confirm = {}) =>
    readAnswerSave(
      await wasloRequest(
        accountId,
        `/answers/${encodeURIComponent(String(id))}`,
        { method: 'PATCH', body: patch, signal, ...confirm }
      )
    ),

  /** Remove one answer. It waits in Recently removed for 30 days (P-6.11). */
  removeAnswer: async (accountId, id, signal) => {
    const removed = envelope(
      await wasloRequest(
        accountId,
        `/answers/${encodeURIComponent(String(id))}`,
        {
          method: 'DELETE',
          signal,
        }
      ),
      'removed'
    );
    return { id: number(removed, 'id', 'removed') };
  },

  /** "Check it now" (P-6.4): one Test it message, priced, on a tap only. */
  checkAnswer: async (accountId, question, signal, confirm = {}) =>
    readAnswerCheck(
      await wasloRequest(accountId, '/knowledge/answers/check', {
        method: 'POST',
        body: { question },
        signal,
        ...confirm,
      })
    ),

  /** "Look through my team's replies" (P-6.5): may she, and how did the last one go. */
  getScanState: async (accountId, signal) =>
    readScanState(
      await wasloRequest(accountId, '/knowledge/suggestions/scan', { signal })
    ),

  /** Start the day's scan. Priced: the third argument is `withConfirm(credits)`. */
  startScan: async (accountId, signal, confirm = {}) =>
    readJobRef(
      await wasloRequest(accountId, '/knowledge/suggestions/scan', {
        method: 'POST',
        signal,
        ...confirm,
      })
    ),

  /** P-6.7: up to ten photos as one source. The fourth argument is `withConfirm(credits)`. */
  uploadDocumentImages: async (accountId, input, signal, confirm = {}) => {
    const form = new FormData();
    form.append('title', input.title);
    input.files.forEach(file => form.append('files', file));
    return readJobRef(
      await wasloRequest(accountId, '/documents/images', {
        method: 'POST',
        body: form,
        signal,
        ...confirm,
      })
    );
  },

  /** "For now" (P-6.9): the notes in force, soonest to end first. */
  getNotices: async (accountId, signal) =>
    readNotices(await wasloRequest(accountId, '/notices', { signal })),

  addNotice: async (accountId, input, signal) =>
    readNotice(
      envelope(
        await wasloRequest(accountId, '/notices', {
          method: 'POST',
          body: input,
          signal,
        }),
        'notice'
      ),
      'notice'
    ),

  /** Extend is a new `endsOn`. */
  updateNotice: async (accountId, id, patch, signal) =>
    readNotice(
      envelope(
        await wasloRequest(
          accountId,
          `/notices/${encodeURIComponent(String(id))}`,
          { method: 'PATCH', body: patch, signal }
        ),
        'notice'
      ),
      'notice'
    ),

  removeNotice: async (accountId, id, signal) => {
    const removed = envelope(
      await wasloRequest(
        accountId,
        `/notices/${encodeURIComponent(String(id))}`,
        {
          method: 'DELETE',
          signal,
        }
      ),
      'removed'
    );
    return { id: number(removed, 'id', 'removed') };
  },

  /** P-1.3: the people and teams "Hand chats to" can name, and who misses a channel. */
  getHandoverPeople: async (accountId, signal) =>
    readHandoverPeople(
      await wasloRequest(accountId, '/handover/people', { signal })
    ),

  // ── WhatsApp broadcasts ───────────────────────────────────────────────────────────
  /** Every broadcast this workspace has sent or started, newest first. */
  getBroadcasts: async (accountId, signal) =>
    readBroadcasts(await wasloRequest(accountId, '/broadcasts', { signal })),

  /** One broadcast and how far it has got. */
  getBroadcast: async (accountId, broadcastId, signal) =>
    readBroadcast(
      await wasloRequest(
        accountId,
        `/broadcasts/${encodeURIComponent(String(broadcastId))}`,
        { signal }
      )
    ),

  /** The WhatsApp numbers a broadcast can go out from, and whether each one can. */
  getBroadcastChannels: async (accountId, signal) =>
    readBroadcastChannels(
      await wasloRequest(accountId, '/broadcasts/channels', { signal })
    ),

  /** What an audience may be built from, each field with only the conditions that work. */
  getAudienceFields: async (accountId, signal) =>
    readAudienceFields(
      await wasloRequest(accountId, '/broadcasts/audience-fields', { signal })
    ),

  /**
   * The approved messages one channel is allowed to send.
   *
   * `channelId` is required, never defaulted: templates are approved per WhatsApp sender,
   * so a list assembled without one is not a smaller answer, it is a wrong one.
   */
  getMessageTemplates: async (accountId, channelId, signal) =>
    readMessageTemplates(
      await wasloRequest(
        accountId,
        `/templates?channelId=${encodeURIComponent(String(channelId))}`,
        { signal }
      )
    ),

  /**
   * Resolve the whole audience and say what would be sent. Spends nothing, creates nothing.
   *
   * Fired ONLY from an explicit press: this walks every page of the audience upstream and
   * is separately rate-limited at 10/min, so a preview on change or on keystroke would
   * exhaust it before the owner finished typing a phone number.
   *
   * The `fingerprint` it answers with must be sent back untouched by `launchBroadcast` —
   * see there.
   *
   * @param {object} draft `helpers/broadcast-compose.js` `draftToWire` output.
   */
  previewBroadcast: async (accountId, draft, signal) =>
    readSendPreview(
      await wasloRequest(accountId, '/broadcasts/preview', {
        method: 'POST',
        body: draft,
        signal,
      })
    ),

  /**
   * Create it and send it. THE ONE CALL IN THIS SECTION THAT SPENDS MONEY.
   *
   * Three fields ride along from the preview and none of them is data — they are the two
   * spend gates the BFF checks (`routes/api/broadcasts.ts`):
   *   • `previewFingerprint` — the state the preview was built from. A draft edited after
   *     the preview is refused rather than sent, because an acknowledgement ticked for 37
   *     skipped contacts must not satisfy a re-resolved audience of 900.
   *   • `previewExcluded` — how many the preview said would be skipped. A lost or
   *     non-numeric echo reads as "unknown" and demands the tick.
   *   • `acknowledged` — the tick itself.
   *
   * Answers with the id to go and watch and nothing else. The caller navigates to the
   * detail route from here, which is what keeps a live Send button from ever sitting on
   * top of a broadcast that has already gone out.
   */
  launchBroadcast: async (accountId, body, signal) =>
    readLaunched(
      await wasloRequest(accountId, '/broadcasts', {
        method: 'POST',
        body,
        signal,
      })
    ),

  /** Stop a running or paused send. Everything still queued is skipped. */
  cancelBroadcast: async (accountId, broadcastId, signal) =>
    readBroadcast(
      await wasloRequest(
        accountId,
        `/broadcasts/${encodeURIComponent(String(broadcastId))}/cancel`,
        { method: 'POST', signal }
      )
    ),

  /** Start a paused send again. */
  resumeBroadcast: async (accountId, broadcastId, signal) =>
    readBroadcast(
      await wasloRequest(
        accountId,
        `/broadcasts/${encodeURIComponent(String(broadcastId))}/resume`,
        { method: 'POST', signal }
      )
    ),

  /**
   * Send again to the recipients this run failed on. Each one is a NEW paid message, which
   * is why the screen asks before it calls this.
   */
  retryBroadcast: async (accountId, broadcastId, signal) =>
    readBroadcast(
      await wasloRequest(
        accountId,
        `/broadcasts/${encodeURIComponent(String(broadcastId))}/retry`,
        { method: 'POST', signal }
      )
    ),

  // ── the outbound API ──────────────────────────────────────────────────────────────
  /**
   * The master switch, the pause, test mode and the limits — one read for the whole
   * Setup page.
   */
  getOutboundSettings: async (accountId, signal) =>
    readOutboundSettings(
      await wasloRequest(accountId, '/outbound/settings', { signal })
    ),

  /**
   * A genuine partial update: send only what changed, render the RESPONSE.
   *
   * FOUR FIELDS AND NOTHING ELSE. The limits are on the same DTO and are deliberately not
   * writable here — an operator owns them, and the BFF rebuilds its patch field by field
   * rather than spreading this body, so a key added to the schema later cannot travel
   * into a column it has no business in.
   *
   * @param {{ enabled?: boolean, paused?: boolean, testMode?: boolean,
   *           testNumbers?: string[] }} patch
   */
  updateOutboundSettings: async (accountId, patch, signal) =>
    readOutboundSettings(
      await wasloRequest(accountId, '/outbound/settings', {
        method: 'PATCH',
        body: patch,
        signal,
      })
    ),

  /**
   * One page of the Log, NEWEST FIRST, with the total the pager needs.
   *
   * `status` is one of `WASLO_OUTBOUND_STATUSES` or absent for "all" — absent, never `''`
   * or `'all'`: those are values, and a filter for the empty status is a filter the BFF
   * would be right to reject.
   *
   * Written with the query narrowed inside rather than defaulted in the signature:
   * `signal` is the last argument on every method in this object, and a default in the
   * middle would move it.
   *
   * @param {{ status?: string, limit?: number, offset?: number }} [query]
   */
  getOutboundMessages: async (accountId, query, signal) => {
    const { status, limit, offset } = query ?? {};
    return readOutboundMessages(
      await wasloRequest(
        accountId,
        `/outbound/messages${queryString({ status, limit, offset })}`,
        { signal }
      )
    );
  },

  /** One send, in full — every field the list row had, and the ones it had no room for. */
  getOutboundMessage: async (accountId, messageId, signal) =>
    readOutboundMessage(
      await wasloRequest(
        accountId,
        `/outbound/messages/${encodeURIComponent(String(messageId))}`,
        { signal }
      )
    ),

  /**
   * One page of the Do not contact list.
   *
   * `includeRemoved` is sent as a real `false` rather than omitted, because it is a
   * question the screen answers deliberately: numbers that were taken off the list are
   * history, and a page that showed them by accident would read as "still blocked".
   *
   * @param {{ includeRemoved?: boolean, limit?: number, offset?: number }} [query]
   */
  getOutboundNumbers: async (accountId, query, signal) => {
    const { includeRemoved, limit, offset } = query ?? {};
    return readOutboundNumbers(
      await wasloRequest(
        accountId,
        `/outbound/numbers${queryString({ includeRemoved, limit, offset })}`,
        { signal }
      )
    );
  },

  /**
   * Add a number by hand. The BFF normalises it to E.164, and the row it answers with is
   * the stored form — which is what the screen renders, so a number typed with spaces
   * never appears twice in two spellings.
   */
  addOutboundNumber: async (accountId, phone, signal) =>
    readOutboundNumber(
      await wasloRequest(accountId, '/outbound/numbers', {
        method: 'POST',
        body: { phone },
        signal,
      })
    ),

  /**
   * Take a number off the list. Offered ONLY for the reasons in
   * `WASLO_SUPPRESSION_REMOVABLE_REASONS` — the API answers 403 for the other two, and a
   * button that 403s is a worse answer than a sentence saying the customer has to send
   * START.
   */
  removeOutboundNumber: async (accountId, phone, signal) =>
    readOutboundDeleted(
      await wasloRequest(
        accountId,
        `/outbound/numbers/${encodeURIComponent(String(phone))}`,
        { method: 'DELETE', signal }
      )
    ),

  /** The WhatsApp numbers a key can be pinned to, and whether each also answers by itself. */
  getOutboundSenders: async (accountId, signal) =>
    readOutboundSenders(
      await wasloRequest(accountId, '/outbound/senders', { signal })
    ),

  /** Every key this workspace has, including the ones already turned off. */
  getOutboundKeys: async (accountId, signal) =>
    readOutboundKeys(
      await wasloRequest(accountId, '/outbound/keys', { signal })
    ),

  /**
   * Mint a key. THE ONE CALL IN THIS SECTION WHOSE ANSWER CANNOT BE FETCHED AGAIN.
   *
   * `key.secret` is the plaintext and exists nowhere else — not in the store, which holds
   * a one-way hash, and not in any later read. Whatever the caller does with it, it does
   * once: a reveal panel, a copy button, and no toast, no list and no `console.log`.
   *
   * `inboxId` is required whenever the workspace has more than one WhatsApp number; the
   * sidecar refuses the mint with `inbox_required` otherwise rather than picking one.
   *
   * @param {{ label?: string, inboxId?: number|null }} input
   */
  createOutboundKey: async (accountId, input, signal) =>
    readOutboundKeySecret(
      await wasloRequest(accountId, '/outbound/keys', {
        method: 'POST',
        body: input,
        signal,
      })
    ),

  /**
   * Turn a key off. Immediate on every replica — an outbound key's lookup is deliberately
   * not memoised on the sidecar — so the next call from the owner's system is refused.
   */
  revokeOutboundKey: async (accountId, keyId, signal) =>
    readOutboundDeleted(
      await wasloRequest(
        accountId,
        `/outbound/keys/${encodeURIComponent(String(keyId))}`,
        { method: 'DELETE', signal }
      )
    ),

  /**
   * The approved messages one sender may send, with the blanks each one expects.
   *
   * `inboxId` is required, never defaulted, for the same reason it is required on
   * broadcasts: templates are approved per WhatsApp sender.
   */
  getOutboundTemplates: async (accountId, inboxId, signal) =>
    readOutboundTemplates(
      await wasloRequest(
        accountId,
        `/outbound/templates${queryString({ inboxId })}`,
        { signal }
      )
    ),

  /**
   * Ask WhatsApp for a fresh list. BEST-EFFORT AND SAYS SO: the answer's `note` describes
   * what was actually requested, and the screen renders it rather than implying the next
   * read is already current.
   */
  syncOutboundTemplates: async (accountId, inboxId, signal) =>
    readOutboundTemplateSync(
      await wasloRequest(accountId, '/outbound/templates/sync', {
        method: 'POST',
        body: inboxId === undefined || inboxId === null ? {} : { inboxId },
        signal,
      })
    ),
  // ── set up with AI (spec §8 P-8.1 to P-8.3). The confirm argument is `withConfirm(credits)`. ──

  /** "Write it for me" (P-8.1): a stored first draft, read back by id. Proposes; writes nothing. */
  firstDraft: async (accountId, input, signal, confirm = {}) =>
    readSetupSuggestionEnvelope(
      await wasloRequest(accountId, '/assist/first-draft', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** "Start from a description" (P-8.2): a task draft for the composer. Nothing is written. */
  describeTask: async (accountId, input, signal, confirm = {}) =>
    readDescribedTask(
      await wasloRequest(accountId, '/assist/describe-task', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** "Tell Waslo what changed" (P-8.3): proposed changes, stored; `suggestion` is null when none. */
  whatChanged: async (accountId, input, signal, confirm = {}) =>
    readWhatChanged(
      await wasloRequest(accountId, '/assist/what-changed', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** An owner's voice note as words, for her to read and fix before anything is drafted. */
  transcribeVoiceNote: async (
    accountId,
    { blob, seconds },
    signal,
    confirm = {}
  ) => {
    const form = new FormData();
    form.append('audio', blob, 'note.webm');
    form.append('seconds', String(Math.round(seconds)));
    const body = record(
      await wasloRequest(accountId, '/assist/transcribe', {
        method: 'POST',
        body: form,
        signal,
        ...confirm,
      }),
      'payload'
    );
    return {
      text: string(body, 'text', 'payload'),
      charged: optionalNullableNumber(body, 'charged', 'payload'),
    };
  },

  /** Open proposals of one source (`first_draft` or `what_changed`), newest first. */
  getSetupSuggestions: async (accountId, source, signal) =>
    listEnvelope(
      await wasloRequest(
        accountId,
        `/assist/suggestions?source=${encodeURIComponent(source)}`,
        { signal }
      ),
      'items'
    ).map((item, i) => readSetupSuggestion(item, `items[${i}]`)),

  getSetupSuggestion: async (accountId, id, signal) =>
    readSetupSuggestionEnvelope(
      await wasloRequest(
        accountId,
        `/assist/suggestions/${encodeURIComponent(String(id))}`,
        { signal }
      )
    ).suggestion,

  /** `{ keep, edits }` for a first draft, `{ pick }` for a change set. Priced only when it adds answers. */
  applySetupSuggestion: async (accountId, id, input, signal, confirm = {}) =>
    readApplied(
      await wasloRequest(
        accountId,
        `/assist/suggestions/${encodeURIComponent(String(id))}/apply`,
        { method: 'POST', body: input, signal, ...confirm }
      )
    ),

  dismissSetupSuggestion: async (accountId, id, signal) =>
    readSetupSuggestionEnvelope(
      await wasloRequest(
        accountId,
        `/assist/suggestions/${encodeURIComponent(String(id))}/dismiss`,
        { method: 'POST', signal }
      )
    ).suggestion,

  /** One Undo for everything one apply did (P-6.11 batch undo). */
  undoBatch: async (accountId, batchId, signal) =>
    readBatchUndone(
      await wasloRequest(
        accountId,
        `/batches/${encodeURIComponent(batchId)}/undo`,
        { method: 'POST', signal }
      )
    ),

  // ── the Claude connector (services spec §8 P-8.4). Nothing here runs Waslo AI but the Keep. ──

  /** The consent page: who is asking to connect. */
  getConnectRequest: async (accountId, requestId, signal) =>
    readConnectRequest(
      await wasloRequest(
        accountId,
        `/assistant/requests/${encodeURIComponent(requestId)}`,
        { signal }
      )
    ),

  /** Allow or Cancel. Answers where to send the owner back to Claude. */
  decideConnectRequest: async (accountId, requestId, allow, signal) =>
    readRedirect(
      await wasloRequest(accountId, '/assistant/grants', {
        method: 'POST',
        body: { request: requestId, allow },
        signal,
      })
    ),

  /** Connected AI apps. */
  getAssistantGrants: async (accountId, signal) =>
    listEnvelope(
      await wasloRequest(accountId, '/assistant/grants', { signal }),
      'items'
    ).map((item, i) => readGrant(item, `items[${i}]`)),

  /** Disconnect: the next thing that app tries is refused. */
  revokeAssistantGrant: async (accountId, grantId, signal) =>
    bool(
      record(
        await wasloRequest(
          accountId,
          `/assistant/grants/${encodeURIComponent(String(grantId))}`,
          { method: 'DELETE', signal }
        ),
        'payload'
      ),
      'revoked',
      'payload'
    ),

  /** "Claude suggested N changes", and what still carries "Suggested by Claude". */
  getAssistantSummary: async (accountId, signal) =>
    readAssistantSummary(
      await wasloRequest(accountId, '/assistant/summary', { signal })
    ),

  /** Undo all of one Claude session, and close what still waits in it. */
  undoAssistantBatch: async (accountId, batchId, signal) =>
    readUndoAll(
      await wasloRequest(
        accountId,
        `/assistant/batches/${encodeURIComponent(batchId)}/undo`,
        { method: 'POST', signal }
      )
    ),

  /** Keep answers Claude drafted. Priced and confirmed: `confirm` is `withConfirm(credits)`. */
  keepAnswerDrafts: async (accountId, input, signal, confirm = {}) =>
    readKept(
      await wasloRequest(accountId, '/answers/keep', {
        method: 'POST',
        body: input,
        signal,
        ...confirm,
      })
    ),

  /** Remove answers Claude drafted. Free. */
  removeAnswerDrafts: async (accountId, suggestionId, pick, signal) =>
    readDraftsRemoved(
      await wasloRequest(
        accountId,
        `/answers/drafts/${encodeURIComponent(String(suggestionId))}/remove`,
        { method: 'POST', body: { pick }, signal }
      )
    ),
};
